import { listWorkers } from "../api/workers.js";
import { WorkersPage } from "./workers.jsx";
// Console with left sidebar: Overview / Keys / Usage / OAuth / Billing / Referral / Settings
import React, { useState as ucS, useMemo as ucM } from "react";
import { createPortal } from "react-dom";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Cell,
  AreaChart,
  Area,
} from "recharts";
import { XPrim, XIcons } from "../primitives.jsx";
import { XData, XEnv } from "../data.jsx";
import { XApi } from "../services.js";
import { topApisByCalls, usageStatus } from "./console-usage.js";
import { XShell } from "../shell.jsx";
import {
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
  Select,
  SelectOption,
} from "../ui/index.js";
import { useT } from "../i18n.jsx";
import { TaskResultView } from "./api-detail.jsx";
import { ApiAvatar } from "./market.jsx";
import { TimingWaterfall } from "../components/TimingWaterfall.jsx";
import { SANDBOX_BASE as SHARED_SANDBOX_BASE } from "../components/SandboxLogs.jsx";
import { AutoRechargeSettings } from "../components/AutoRechargeSettings.jsx";
import { TopUpPage } from "./other.jsx";
import { RechargeModeSwitch, useRechargeMode } from "../components/RechargeModeSwitch.jsx";
import { useStripePaymentTracking } from "../components/StripePaymentTracking.jsx";
import {
  enabledPermCount,
  isPermOn,
  toggleScope,
  visibleKeyPerms,
} from "../key-permissions.js";

// Abbreviates large integers for chart Y-axis ticks so 6+ digit values don't
// get clipped by the YAxis `width`. 1234 → "1.2K", 257016 → "257K", 3.4M → "3.4M".
// Keeps small numbers (< 1000) unchanged so low-traffic accounts still see
// concrete tick values instead of "0K".
const compactNumber = (n) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return "";
  const abs = Math.abs(v);
  if (abs >= 1_000_000)
    return (
      (v / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1).replace(/\.0$/, "") +
      "M"
    );
  if (abs >= 1_000)
    return (v / 1_000).toFixed(abs >= 10_000 ? 0 : 1).replace(/\.0$/, "") + "K";
  return String(v);
};

const ConsolePage = () => {
  const { route, navigate } = XShell.useRoute();
  const {
    user,
    authed,
    isApiKeyLogin,
    isTwitterLogin,
    canManageKeys,
    canProvideApis,
    isEntity,
    refreshMe,
  } = XShell.useAuth();
  const { t } = useT();
  const { toast: rootToast } = XPrim.useToast();
  // The active tab is derived directly from route.params.tab so that in-app navigations
  // (e.g. TopBar Invite → navigate('console', { tab: 'referral' }), or any deep link)
  // immediately switch tabs without relying on state-sync effects. setTab updates the URL
  // and route state via navigate, so sidebar clicks also reflect in the URL.
  const tab = route.params.tab || "overview";
  const setTab = (next) => {
    if (next === tab) return;
    const { tab: _drop, payment: _payment, autoRecharge: _autoRecharge, setupSession: _setupSession, stripe: _stripe, pendingStripeSession: _pendingStripeSession, ...rest } = route.params || {};
    navigate("console", { ...rest, tab: next });
  };
  const {
    IconGauge,
    IconKey,
    IconLayers,
    IconShield,
    IconWallet,
    IconUsers,
    IconSettings,
    IconBell,
    IconPlus,
    IconCopy,
    IconTrash,
    IconRefresh,
    IconCheck,
    IconX,
    IconArrowUp,
    IconArrowDown,
    IconZap,
    IconDollar,
  } = XIcons;

  // OAuth-bind callback toast. Bootstrap forwards `?linked=success|error` here from the
  // backend's /dashboard?linked=… redirect. We surface a toast and refresh /me so the new
  // accountType (ENTITY) propagates immediately. Then strip the param from the URL so a
  // refresh doesn't re-toast.
  React.useEffect(() => {
    const linked = route.params && route.params.linked;
    if (!linked) return;
    if (linked === "success") {
      rootToast(t("bind.successToast"));
      try {
        refreshMe();
      } catch {}
    } else if (linked === "merged") {
      // Virtual account was absorbed into an existing ENTITY account that already owned this X handle.
      // bootstrap.js already swapped tokens; refreshMe() will pull the ENTITY profile.
      rootToast(t("bind.mergedToast"));
      try {
        refreshMe();
      } catch {}
    } else if (linked === "error") {
      rootToast(
        t("bind.errorToast") +
          (route.params.message ? ": " + route.params.message : ""),
      );
    }
    // Strip linked= from the URL so the toast doesn't fire again on tab switch.
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete("linked");
      url.searchParams.delete("message");
      history.replaceState(null, "", url.pathname + url.search + url.hash);
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Live counts. /keys (list) and /api-services/mine are JWT-only — apikey users can read both;
  // write-side endpoints are gated separately via canManageKeys / canProvideApis at the card level.
  const [keysCount, setKeysCount] = ucS(null);
  const [myApisCount, setMyApisCount] = ucS(null);
  React.useEffect(() => {
    if (!authed) {
      setKeysCount(null);
      setMyApisCount(null);
      return;
    }
    let cancelled = false;
    Promise.allSettled([
      XApi.keyService.list(),
      // /api-services/mine requires @RequireEntity AND a Twitter session (apikey rejected).
      // Skip the call for apikey logins to avoid the noisy 403 every render.
      isEntity && !isApiKeyLogin
        ? XApi.apiSvc.listMine()
        : Promise.resolve(null),
    ]).then(([k, m]) => {
      if (cancelled) return;
      if (k.status === "fulfilled" && Array.isArray(k.value))
        setKeysCount(k.value.length);
      if (m.status === "fulfilled" && Array.isArray(m.value))
        setMyApisCount(m.value.length);
    });
    return () => {
      cancelled = true;
    };
  }, [authed, tab, isEntity, isApiKeyLogin]);

  // All read-side tabs are visible for any authed user — backend permits read for apikey logins.
  // Write-side gating happens inside the tab body (KeysTab buttons disabled with upgrade CTA).
  const navItems = [
    {
      k: "overview",
      l: t("console.nav.overview"),
      icon: <IconGauge size={14} />,
    },
    // Apikey login is a scoped session (one signed-in key) — hide entity-wide count to avoid
    // exposing aggregate info from the underlying ENTITY account.
    {
      k: "keys",
      l: t("console.nav.keys"),
      icon: <IconKey size={14} />,
      num: isApiKeyLogin ? null : keysCount,
    },
    {
      k: "env",
      l: t("console.nav.env"),
      icon: <XIcons.IconTerminal size={14} />,
    },
    { k: "usage", l: t("console.nav.usage"), icon: <IconLayers size={14} /> },
    {
      k: "aiLogs",
      l: t("console.nav.aiLogs"),
      icon: <XIcons.IconBolt size={14} />,
    },
    {
      k: "sandbox",
      l: t("console.nav.sandbox"),
      icon: <XIcons.IconCpu size={14} />,
    },
    {
      k: "workers",
      l: t("console.nav.workers"),
      icon: <XIcons.IconLayers size={14} />,
    },
    {
      k: "tasks",
      l: t("console.nav.tasks"),
      icon: <XIcons.IconZap size={14} />,
    },
    {
      k: "billing",
      l: t("console.nav.billing"),
      icon: <IconWallet size={14} />,
    },
    {
      k: "referral",
      l: t("console.nav.referral"),
      icon: <IconUsers size={14} />,
    },
    // Settings tab hidden for now — keep the SettingsTab render branch + the
    // i18n keys so it can be restored by re-adding this entry to the array.
    // { k: 'settings', l: t('console.nav.settings'), icon: <IconSettings size={14}/> },
  ];
  // Defensive: if route.params.tab is unknown, fall back to overview.
  // Special case: legacy ?tab=analytics deep links now live in the per-API
  // workspace (analytics moved out of console). Send them to /workspace so
  // they land on the API list and can drill into a specific API's analytics.
  React.useEffect(() => {
    if (tab === "analytics") {
      navigate("workspace");
      return;
    }
    if (!navItems.find((n) => n.k === tab)) setTab("overview");
  }, []);
  const [sidebarOpen, setSidebarOpen] = ucS(false);

  return (
    <div className="console-shell">
      {/* Mobile topbar */}
      <div className="console-mobile-bar">
        <button
          className="btn btn-sm btn-ghost btn-icon"
          onClick={() => setSidebarOpen(true)}
          aria-label="Open menu"
        >
          <XIcons.IconMenu size={14} />
        </button>
        <div className="mono" style={{ fontSize: 13 }}>
          {t("topbar.console")} · {navItems.find((n) => n.k === tab)?.l}
        </div>
        <div style={{ width: 28 }} />
      </div>

      {/* Sidebar */}
      {sidebarOpen && (
        <div
          className="console-backdrop"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      <aside className={`console-sidebar ${sidebarOpen ? "open" : ""}`}>
        <div
          className="row between"
          style={{ padding: "4px 10px", marginBottom: 4 }}
        >
          <div className="micro">{t("topbar.console")}</div>
          <button
            className="btn btn-sm btn-ghost btn-icon console-close"
            onClick={() => setSidebarOpen(false)}
            aria-label="Close menu"
          >
            <XIcons.IconX size={12} />
          </button>
        </div>
        <div className="col gap-1">
          {navItems.map((n) => (
            <button
              key={n.k}
              className={`nav-item ${tab === n.k ? "active" : ""}`}
              onClick={() => {
                setTab(n.k);
                setSidebarOpen(false);
              }}
            >
              {n.icon}
              <span>{n.l}</span>
              {n.num != null && <span className="num">{n.num}</span>}
            </button>
          ))}
        </div>
        {/* Provider section: visible to any ENTITY user. Whitelist-gated actions
            (admin / register approval) are still enforced by the backend; entries
            without whitelist will be navigated to but will surface the apply CTA
            or a 403 instead of a silent hide. */}
        {isEntity && (
          <div
            style={{
              borderTop: "1px solid var(--line)",
              marginTop: 16,
              paddingTop: 12,
            }}
          >
            <div
              className="micro"
              style={{ padding: "4px 10px", marginBottom: 4 }}
            >
              {t("console.nav.providerHeading")}
            </div>
            <div className="col gap-1">
              <button
                className="nav-item"
                onClick={() => navigate("workspace")}
              >
                <XIcons.IconCpu size={14} /> {t("console.nav.workspace")}
                {myApisCount != null && (
                  <span className="num">{myApisCount}</span>
                )}
              </button>
              <button
                className="nav-item"
                onClick={() => navigate("workspace-new")}
              >
                <XIcons.IconPlus size={14} /> {t("console.nav.register")}
              </button>
              <button className="nav-item" onClick={() => navigate("earnings")}>
                <XIcons.IconWallet size={14} /> {t("console.nav.earnings")}
              </button>
              {canProvideApis && (
                <button className="nav-item" onClick={() => navigate("admin")}>
                  <XIcons.IconShield size={14} /> {t("console.nav.admin")}
                </button>
              )}
            </div>
          </div>
        )}
        {isApiKeyLogin && !isTwitterLogin && (
          <div
            style={{
              marginTop: 16,
              padding: 12,
              borderRadius: 6,
              background: "rgba(245,165,36,0.08)",
              border: "1px solid rgba(245,165,36,0.3)",
            }}
          >
            <div
              className="micro"
              style={{ color: "#f5c87a", marginBottom: 6 }}
            >
              {t("console.keys.readOnlyLogin")}
            </div>
            <div
              style={{ fontSize: 12, color: "var(--fg-2)", lineHeight: 1.65 }}
            >
              {t("console.keys.readOnlyCapabilities")}
            </div>
            <button
              className="btn btn-sm"
              style={{ marginTop: 10, width: "100%", justifyContent: "center" }}
              onClick={() => navigate("bind")}
            >
              <XIcons.IconTwitter size={11} /> {t("console.keys.verify")}
            </button>
          </div>
        )}
        <div
          style={{
            marginTop: 24,
            padding: 12,
            border: "1px solid var(--line)",
            borderRadius: 6,
          }}
        >
          <div className="micro" style={{ marginBottom: 6 }}>
            {t("topbar.balance")}
          </div>
          <div className="mono" style={{ fontSize: 20 }}>
            ${(user.balance || 0).toFixed(2)}
          </div>
          <button
            className="btn btn-sm btn-primary"
            style={{ marginTop: 10, width: "100%", justifyContent: "center" }}
            onClick={() => navigate("console", { tab: "billing", payment: "manual" })}
          >
            <IconPlus size={12} /> {t("topbar.topUp")}
          </button>
        </div>
      </aside>

      {/* Content — key={tab} replays the quick fade on every tab switch
          instead of a hard cut (same .apx-fade vocabulary as api-detail). */}
      <main className="console-main">
        <div key={tab} className="apx-fade">
          {tab === "overview" && <OverviewTab />}
          {tab === "keys" && <KeysTab />}
          {tab === "env" && <EnvTab focus={route.params.focus} />}
          {tab === "usage" && <UsageTab />}
          {tab === "aiLogs" && <AiLogsTab />}
          {tab === "sandbox" && <SandboxTab />}
          {tab === "workers" && <WorkersPage embedded />}
          {tab === "tasks" && <TasksTab />}
          {tab === "billing" && <BillingTab />}
          {tab === "referral" && <ReferralTab />}
          {tab === "settings" && <SettingsTab />}
        </div>
      </main>
    </div>
  );
};

const KeyFlag = ({ active, label, desc, onToggle, radio }) => (
  <button
    onClick={onToggle}
    className="card card-h"
    style={{
      padding: 12,
      textAlign: "left",
      cursor: "pointer",
      color: "inherit",
      background: active ? "var(--bg-2)" : "var(--bg-0)",
      borderColor: active ? "var(--line-2)" : "var(--line)",
    }}
  >
    <div className="row between" style={{ marginBottom: 4 }}>
      <span
        style={{
          fontSize: 12.5,
          color: active ? "var(--fg-0)" : "var(--fg-2)",
          fontWeight: 500,
        }}
      >
        {label}
      </span>
      {radio ? (
        <span
          style={{
            width: 14,
            height: 14,
            borderRadius: "50%",
            border: "1px solid " + (active ? "var(--fg-1)" : "var(--line-2)"),
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {active && (
            <span
              style={{
                width: 7,
                height: 7,
                borderRadius: "50%",
                background: "var(--fg-1)",
              }}
            />
          )}
        </span>
      ) : (
        <span
          style={{
            width: 28,
            height: 16,
            borderRadius: 10,
            background: active ? "var(--fg-1)" : "var(--bg-3)",
            border: "1px solid " + (active ? "var(--fg-1)" : "var(--line-2)"),
            position: "relative",
            transition: "background .15s, border-color .15s",
          }}
        >
          <span
            style={{
              position: "absolute",
              top: 1,
              left: active ? 13 : 1,
              width: 12,
              height: 12,
              borderRadius: "50%",
              background: active ? "var(--bg-0)" : "var(--fg-3)",
              transition: "left .15s",
            }}
          />
        </span>
      )}
    </div>
    <div
      className="micro-mono"
      style={{
        textTransform: "none",
        letterSpacing: 0,
        color: "var(--fg-4)",
        fontSize: 10.5,
        lineHeight: 1.5,
      }}
    >
      {desc}
    </div>
  </button>
);

// 表格 / 列表里的「权限」入口：摘要 + 管理（点开进权限弹窗）。
const PermSummaryButton = ({ k, onClick }) => {
  const { t } = useT();
  const n = enabledPermCount(k);
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        whiteSpace: "nowrap",
        fontSize: 12,
        padding: "5px 12px",
        borderRadius: 8,
        cursor: "pointer",
        border: "1px solid var(--line)",
        background: "var(--bg-0)",
        color: "var(--fg-2)",
      }}
    >
      {n > 0 ? (
        <>
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              background: "#16a34a",
              display: "inline-block",
            }}
          />
          <span style={{ color: "var(--fg-1)", fontWeight: 600 }}>{t("console.keys.permissions.count", { n })}</span>
        </>
      ) : (
        <span style={{ color: "var(--fg-3)" }}>{t("console.keys.permissions.none")}</span>
      )}
      <span style={{ color: "var(--fg-3)" }}>· {t("console.keys.permissions.manage")}</span>
    </button>
  );
};

// 权限弹窗：分组的开关行（标题 + 说明 + 开关），专业清爽，取代一堆线框 chip。
const KeyPermissionsModal = ({
  k,
  onClose,
  onTogglePerm,
  onWorkerAccessChange,
  workerOptions,
  canUseXntCloud,
  pending,
}) => {
  const { t } = useT();
  if (!k) return null;
  const hasWorkerScope =
    isPermOn(k, 'workers:read') || isPermOn(k, 'workers:write');
  const accessMode = k.workerAccessMode || 'ALL';
  const selectedWorkers = new Set(k.workerAccessIds || []);
  const selectableWorkers = (workerOptions || []).filter(
    (worker) => worker.createdByApiKeyId !== k.id,
  );
  return (
    <Dialog open={!!k} onClose={onClose} maxWidth={560} responsive>
      <DialogHeader title={`${t("console.keys.permissions.title")} · ${k.name}`} onClose={onClose} />
      <DialogBody>
        <div
          style={{
            fontSize: 12,
            color: "var(--fg-3)",
            marginBottom: 16,
            lineHeight: 1.6,
          }}
        >
          {t("console.keys.permissions.intro")}
        </div>
        {visibleKeyPerms(canUseXntCloud).map((g) => (
          <div key={g.groupKey} style={{ marginBottom: 18 }}>
            <div
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: "var(--fg-2)",
                textTransform: "uppercase",
                letterSpacing: ".05em",
              }}
            >
              {t(g.groupKey)}
            </div>
            {g.noteKey && (
              <div style={{ fontSize: 11, color: "var(--fg-4)", marginTop: 3 }}>
                {t(g.noteKey)}
              </div>
            )}
            <div
              style={{
                marginTop: 8,
                border: "1px solid var(--line)",
                borderRadius: 10,
                overflow: "hidden",
              }}
            >
              {g.items.map((item, i) => {
                const on = isPermOn(k, item.id);
                return (
                  <button
                    type="button"
                    key={item.id}
                    onClick={() => onTogglePerm(item.id)}
                    role="switch"
                    aria-checked={on}
                    disabled={pending}
                    style={{
                      display: "flex",
                      width: "100%",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 14,
                      padding: "11px 14px",
                      cursor: pending ? "wait" : "pointer",
                      border: 0,
                      borderTop: i ? "1px solid var(--line)" : "none",
                      background: on ? "var(--bg-1)" : "transparent",
                      color: "inherit",
                      font: "inherit",
                      textAlign: "left",
                      opacity: pending ? 0.65 : 1,
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: 500,
                          color: "var(--fg-1)",
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                        }}
                      >
                        {t(item.labelKey)}
                        {item.danger && (
                          <span
                            style={{
                              fontSize: 9.5,
                              color: "#dc2626",
                              border: "1px solid rgba(220,38,38,0.4)",
                              borderRadius: 4,
                              padding: "0 4px",
                              fontWeight: 600,
                            }}
                          >
                            {t("console.keys.permissions.highRisk")}
                          </span>
                        )}
                      </div>
                      <div
                        style={{
                          fontSize: 11.5,
                          color: "var(--fg-3)",
                          marginTop: 2,
                        }}
                      >
                        {t(item.descKey)}
                      </div>
                    </div>
                    <MiniToggle active={on} />
                  </button>
                );
              })}
            </div>
          </div>
        ))}
        {hasWorkerScope && (
          <div style={{ marginBottom: 8 }}>
            <div
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: 'var(--fg-2)',
                textTransform: 'uppercase',
                letterSpacing: '.05em',
                marginBottom: 8,
              }}
            >
              {t("console.keys.workers.access")}
            </div>
            <select
              className="input"
              aria-label={t("console.keys.workers.access")}
              value={accessMode}
              disabled={pending}
              onChange={(event) =>
                onWorkerAccessChange(
                  event.target.value,
                  k.workerAccessIds || [],
                )
              }
              style={{ width: '100%' }}
            >
              <option value="ALL">{t("console.keys.workers.all")}</option>
              <option value="OWN">{t("console.keys.workers.own")}</option>
              <option value="OWN_AND_SELECTED">{t("console.keys.workers.selected")}</option>
            </select>
            <div
              style={{
                marginTop: 7,
                color: 'var(--fg-4)',
                fontSize: 11.5,
                lineHeight: 1.55,
              }}
            >
              {t("console.keys.workers.accessNote")}
            </div>
            {accessMode === 'OWN_AND_SELECTED' && (
              <div
                style={{
                  marginTop: 10,
                  border: '1px solid var(--line)',
                  borderRadius: 10,
                  maxHeight: 190,
                  overflow: 'auto',
                }}
              >
                {selectableWorkers.length === 0 ? (
                  <div
                    style={{ padding: 12, color: 'var(--fg-4)', fontSize: 12 }}
                  >
                    {t("console.keys.workers.empty")}
                  </div>
                ) : (
                  selectableWorkers.map((worker, index) => (
                    <label
                      key={worker.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 9,
                        padding: '9px 12px',
                        borderTop: index ? '1px solid var(--line)' : 'none',
                        cursor: pending ? 'wait' : 'pointer',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedWorkers.has(worker.id)}
                        disabled={pending}
                        onChange={() => {
                          const next = new Set(selectedWorkers);
                          if (next.has(worker.id)) next.delete(worker.id);
                          else next.add(worker.id);
                          onWorkerAccessChange(accessMode, [...next]);
                        }}
                      />
                      <span style={{ minWidth: 0 }}>
                        <span style={{ display: 'block', fontSize: 12.5 }}>
                          {worker.name}
                        </span>
                        <span
                          className="mono"
                          style={{ color: 'var(--fg-4)', fontSize: 10.5 }}
                        >
                          {worker.id}
                        </span>
                      </span>
                    </label>
                  ))
                )}
              </div>
            )}
          </div>
        )}
      </DialogBody>
      <DialogFooter>
        <button type="button" className="btn btn-sm" onClick={onClose}>
          {t("console.keys.done")}
        </button>
      </DialogFooter>
    </Dialog>
  );
};

const KeyIpAllowlistModal = ({ k, onClose, onSave, pending }) => {
  const { t } = useT();
  const [draft, setDraft] = ucS("");

  React.useEffect(() => {
    setDraft((k?.allowedCidrs || []).join("\n"));
  }, [k?.id, k?.allowedCidrs]);

  if (!k) return null;
  const entries = draft
    .split(/[\n,]+/)
    .map((value) => value.trim())
    .filter(Boolean);

  return (
    <Dialog open={!!k} onClose={pending ? undefined : onClose} maxWidth={500} responsive>
      <DialogHeader title={`${t("console.keys.ip.title")} · ${k.name}`} onClose={onClose} />
      <DialogBody>
        <p
          style={{
            margin: "0 0 12px",
            color: "var(--fg-3)",
            fontSize: 12.5,
            lineHeight: 1.65,
          }}
        >
          {t("console.keys.ip.desc")} {t("console.keys.ip.hint")}
        </p>
        <textarea
          className="input mono"
          rows={7}
          value={draft}
          disabled={pending}
          placeholder={t("console.keys.ip.placeholder")}
          onChange={(event) => setDraft(event.target.value)}
          style={{ width: "100%", resize: "vertical", lineHeight: 1.6 }}
        />
        <div style={{ marginTop: 8, color: "var(--fg-4)", fontSize: 11.5 }}>
          {entries.length
            ? t("console.keys.ip.entries", { n: entries.length })
            : t("console.keys.ip.unrestricted")}
        </div>
      </DialogBody>
      <DialogFooter>
        <button type="button" className="btn btn-sm" disabled={pending} onClick={onClose}>
          {t("common.cancel")}
        </button>
        <button
          type="button"
          className="btn btn-sm btn-primary"
          disabled={pending || entries.length > 50}
          onClick={() => onSave(entries)}
        >
          {pending ? t("console.keys.saving") : t("console.keys.ip.save")}
        </button>
      </DialogFooter>
    </Dialog>
  );
};

const KeySettingsList = ({ k, onOpenPerms, onOpenIpAllowlist, onSetLimit }) => {
  const { t } = useT();
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
        gap: 8,
      }}
    >
      <KeyLimitRow value={k.limit} onChange={onSetLimit} />
      <KeySettingsRow
        label={t("console.keys.permissions.title")}
        desc={t("console.keys.permissions.settingsDesc")}
        onClick={onOpenPerms}
        right={
          <span
            style={{
              fontSize: 12,
              color: "var(--fg-2)",
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            {t("console.keys.permissions.count", { n: enabledPermCount(k) })}{" "}
            <span style={{ color: "var(--fg-4)" }}>›</span>
          </span>
        }
      />
      <KeySettingsRow
        label={t("console.keys.ip.title")}
        desc={t("console.keys.ip.desc")}
        onClick={onOpenIpAllowlist}
        right={
          <span style={{ fontSize: 12, color: "var(--fg-2)" }}>
            {k.allowedCidrs.length
              ? t("console.keys.ip.entries", { n: k.allowedCidrs.length })
              : t("console.keys.ip.unrestricted")} {" "}
            <span style={{ color: "var(--fg-4)" }}>›</span>
          </span>
        }
      />
    </div>
  );
};

// One tile inside KeySettingsList grid. Single-line layout; the description
// lives on `title` (hover tooltip) so labels in any language stay one line and
// row height never balloons.
const KeySettingsRow = ({ label, desc, right, onClick }) => (
  <div
    onClick={onClick}
    title={desc || undefined}
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
      padding: "10px 14px",
      cursor: onClick ? "pointer" : "default",
      minHeight: 42,
      border: "1px solid var(--line)",
      borderRadius: 8,
      background: "var(--bg-0)",
    }}
  >
    <div
      style={{
        fontSize: 13,
        color: "var(--fg-1)",
        fontWeight: 500,
        flex: 1,
        minWidth: 0,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      {label}
    </div>
    <div style={{ flexShrink: 0 }}>{right}</div>
  </div>
);

const MiniToggle = ({ active }) => (
  <span
    aria-hidden
    style={{
      display: "inline-block",
      width: 28,
      height: 16,
      borderRadius: 10,
      background: active ? "var(--fg-1)" : "var(--bg-3)",
      border: "1px solid " + (active ? "var(--fg-1)" : "var(--line-2)"),
      position: "relative",
      transition: "background .15s, border-color .15s",
    }}
  >
    <span
      style={{
        position: "absolute",
        top: 1,
        left: active ? 13 : 1,
        width: 12,
        height: 12,
        borderRadius: "50%",
        background: active ? "var(--bg-0)" : "var(--fg-3)",
        transition: "left .15s",
      }}
    />
  </span>
);

// Inline limit row — value + Edit pill on the right; clicking the row OR Edit
// opens the inline number input. Mirrors the macOS prefs feel: one tap to edit.
const KeyLimitRow = ({ value, onChange }) => {
  const { t } = useT();
  const [editing, setEditing] = ucS(false);
  const [draft, setDraft] = ucS(value ?? "");
  const commit = () => {
    const v = draft === "" ? null : Number(draft);
    if (draft === "" || (!isNaN(v) && v >= 0)) onChange(v);
    setEditing(false);
  };
  return (
    <div
      onClick={() => {
        if (!editing) {
          setDraft(value ?? "");
          setEditing(true);
        }
      }}
      title={t("console.keys.limit.desc")}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
        padding: "10px 14px",
        cursor: editing ? "default" : "pointer",
        minHeight: 42,
        border: "1px solid var(--line)",
        borderRadius: 8,
        background: "var(--bg-0)",
      }}
    >
      <div
        style={{
          fontSize: 13,
          color: "var(--fg-1)",
          fontWeight: 500,
          flex: 1,
          minWidth: 0,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {t("console.keys.limit.title")}
      </div>
      {editing ? (
        <div
          className="row gap-2"
          style={{ flexShrink: 0 }}
          onClick={(e) => e.stopPropagation()}
        >
          <span className="mono" style={{ fontSize: 13, color: "var(--fg-3)" }}>
            $
          </span>
          <input
            autoFocus
            type="number"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === "Enter") commit();
              if (e.key === "Escape") {
                setDraft(value ?? "");
                setEditing(false);
              }
            }}
            className="input"
            placeholder={t("console.keys.limit.noLimit")}
            // Narrower input on mobile so the row never overflows; falls back to 110 on desktop.
            style={{
              width: "min(110px, 40vw)",
              padding: "4px 8px",
              fontSize: 13,
            }}
          />
        </div>
      ) : (
        <div
          className="row gap-2"
          style={{ flexShrink: 0, alignItems: "baseline" }}
        >
          <span className="mono" style={{ fontSize: 13, color: "var(--fg-1)" }}>
            {value != null ? `$${value}` : t("console.keys.limit.noLimit")}
          </span>
          {value != null && (
            <span
              className="micro-mono"
              style={{
                textTransform: "none",
                letterSpacing: 0,
                color: "var(--fg-4)",
                fontSize: 10,
              }}
            >
              {t("console.keys.limit.perMonth")}
            </span>
          )}
          <button
            className="btn btn-sm btn-ghost"
            style={{ padding: "2px 8px", fontSize: 11 }}
          >
            {t("console.keys.limit.edit")}
          </button>
        </div>
      )}
    </div>
  );
};

// Provider glyph — Twitter/X uses the brand icon, everything else falls back
// to the first letter of provider.name. Defined at module scope so both the
// PC inline row cells AND the OAuth dialog (also at module scope) can use it.
const ProviderGlyph = ({ provider, size = 13 }) => {
  const id = String(provider?.id || "").toLowerCase();
  const name = String(provider?.name || "").toLowerCase();
  const type = String(provider?.type || "").toLowerCase();
  const isTwitter =
    id.includes("twitter") ||
    id === "x" ||
    name.includes("twitter") ||
    name === "x" ||
    type.includes("twitter");
  if (isTwitter) return <XIcons.IconTwitter size={size} />;
  return (
    <span style={{ fontFamily: "var(--font-mono)", fontSize: size }}>
      {(provider?.name || "?")[0]}
    </span>
  );
};

// Avatar for a bound row — prefers the user's real OAuth avatar
// (binding.avatar), falls back to the provider glyph (Twitter X, letter).
const BoundAvatar = ({ provider, binding, size = 32 }) => {
  const [errored, setErrored] = React.useState(false);
  const avatar = binding?.avatar;
  const showImg = !!avatar && !errored;
  const base = {
    width: size,
    height: size,
    borderRadius: Math.round(size * 0.18),
    background: "var(--bg-3)",
    border: "1px solid var(--line-2)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "var(--fg-1)",
    flexShrink: 0,
    overflow: "hidden",
  };
  if (showImg) {
    return (
      <div style={base}>
        <img
          src={avatar}
          alt={binding?.account || provider?.name || ""}
          onError={() => setErrored(true)}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      </div>
    );
  }
  return (
    <div style={base}>
      <ProviderGlyph provider={provider} size={Math.round(size * 0.45)} />
    </div>
  );
};

// ============== Desktop inline-row cells (xapi-new ConsolePage.tsx:867+ mirror) ==============
// All three are used by the PC keys table only; mobile uses KeySettingsList in
// the bottom drawer. Keeping them small enough to fit single-line at typical
// container widths.

// Compact OAuth cell — single button for legacy keys, avatar chips + "+ add"
// for keys with at least one binding, plain "Connect" button for new+empty.
// Each chip exposes a hover × to revoke directly (xapi-new OAuthCell:1097-1104 pattern).
const InlineOAuthCell = ({
  k,
  bound,
  avail,
  onEnable,
  onAdd,
  onClickBinding,
  onRevoke,
  t,
}) => {
  if (!k.oauth) {
    return (
      <button
        className="btn btn-sm"
        onClick={onEnable}
        style={{ borderStyle: "dashed", color: "var(--fg-3)" }}
        title={t("console.keys.oauth.legacyHint")}
      >
        <XIcons.IconKey size={11} /> {t("console.keys.oauth.enableCta")}
      </button>
    );
  }
  if (bound.length === 0) {
    return (
      <button
        className="btn btn-sm"
        onClick={onAdd}
        style={{ borderStyle: "dashed", color: "var(--fg-3)" }}
        disabled={avail.length === 0}
      >
        <XIcons.IconPlus size={11} /> {t("console.keys.oauth.connect")}
      </button>
    );
  }
  return (
    <div
      className="row gap-1"
      style={{ alignItems: "center", flexWrap: "wrap" }}
    >
      {bound.map(({ provider: p, binding: b }) => (
        <span
          key={p.id}
          className="oauth-chip"
          title={`${b.account} · ${p.name}`}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            border: "1px solid var(--line-2)",
            background: "var(--bg-3)",
            borderRadius: 14,
            padding: "2px 4px 2px 2px",
            position: "relative",
          }}
        >
          <button
            onClick={() => onClickBinding(p, b)}
            className="row gap-1"
            style={{
              alignItems: "center",
              background: "transparent",
              border: "none",
              padding: 0,
              cursor: "pointer",
              color: "inherit",
            }}
          >
            <BoundAvatar provider={p} binding={b} size={22} />
            <span
              className="mono"
              style={{
                fontSize: 11,
                color: "var(--fg-2)",
                maxWidth: 120,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {b.account}
            </span>
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onRevoke(p, b);
            }}
            title={t("console.oauth.revoke")}
            aria-label={t("console.oauth.revoke")}
            className="oauth-chip-revoke"
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 16,
              height: 16,
              padding: 0,
              background: "transparent",
              border: "none",
              cursor: "pointer",
              color: "var(--fg-4)",
              borderRadius: "50%",
            }}
          >
            <svg
              width="9"
              height="9"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
            >
              <path d="M3.5 3.5l9 9 M12.5 3.5l-9 9" />
            </svg>
          </button>
        </span>
      ))}
      {avail.length > 0 && (
        <button
          onClick={onAdd}
          className="btn btn-sm btn-ghost btn-icon"
          title={t("console.keys.oauth.addMore")}
          style={{ width: 24, height: 24, padding: 0 }}
        >
          <XIcons.IconPlus size={11} />
        </button>
      )}
    </div>
  );
};

// Inline limit display + click-to-edit input. Shows progress bar when limit
// is set; "no cap" + Edit button otherwise.
// Whole-cell click-to-edit. The earlier design had an explicit "编辑" button
// pushed to the right edge via marginLeft:auto, which on wider columns left a
// large empty gap between the value and the action — visually disconnected.
// Now the value block itself is the trigger; a small pencil icon sits inline
// (and fades in on hover) as the affordance, so action stays close to value.
const InlineLimitCell = ({ k, onSetLimit, t }) => {
  const [editing, setEditing] = ucS(false);
  const [draft, setDraft] = ucS(k.limit ?? "");
  const commit = () => {
    const v = draft === "" ? null : Number(draft);
    if (draft === "" || (!isNaN(v) && v >= 0)) onSetLimit(v);
    setEditing(false);
  };
  if (editing) {
    return (
      <div className="row gap-1" style={{ alignItems: "center" }}>
        <span className="mono" style={{ fontSize: 12, color: "var(--fg-3)" }}>
          $
        </span>
        <input
          autoFocus
          type="number"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") commit();
            if (e.key === "Escape") {
              setDraft(k.limit ?? "");
              setEditing(false);
            }
          }}
          className="input"
          placeholder={t("console.keys.limit.noLimit")}
          style={{ width: 90, padding: "2px 6px", fontSize: 12 }}
        />
      </div>
    );
  }
  const startEdit = (initial) => {
    setDraft(initial);
    setEditing(true);
  };
  const trigger = (children, initial) => (
    <button
      type="button"
      onClick={() => startEdit(initial)}
      title={t("console.keys.limit.edit")}
      className="limit-cell-trigger"
    >
      {children}
      <XIcons.IconEdit size={11} className="limit-cell-pencil" />
    </button>
  );
  if (k.limit == null) {
    return trigger(
      <div className="limit-cell-content">
        <div className="mono" style={{ fontSize: 12, color: "var(--fg-1)" }}>
          ${k.used.toFixed(2)}
        </div>
        <div
          className="micro-mono"
          style={{
            textTransform: "none",
            letterSpacing: 0,
            color: "var(--fg-4)",
            fontSize: 10,
            marginTop: 2,
          }}
        >
          {t("console.keys.limit.usedNoLimit")}
        </div>
      </div>,
      "",
    );
  }
  if (k.limit === 0) {
    return trigger(
      <span
        className="micro-mono limit-cell-content"
        style={{ color: "var(--err)" }}
      >
        {t("console.keys.limit.zero")}
      </span>,
      "0",
    );
  }
  return trigger(
    <div className="limit-cell-content" style={{ minWidth: 110 }}>
      <div className="row between" style={{ fontSize: 11, gap: 8 }}>
        <span className="mono">${k.used.toFixed(2)}</span>
        <span className="mono" style={{ color: "var(--fg-4)" }}>
          ${k.limit}
        </span>
      </div>
      <div className="bar-track" style={{ marginTop: 4 }}>
        <div
          className="bar-fill"
          style={{ width: (k.used / k.limit) * 100 + "%" }}
        />
      </div>
    </div>,
    k.limit,
  );
};

// Feature toggle — single switch per row (sandbox / allow-register).
// `title=` holds the description for hover tooltip. Label collapses with
// `text-overflow: ellipsis` so the row fits any column width.
const FeatureToggle = ({ active, label, title, onClick }) => (
  <button
    onClick={onClick}
    title={title}
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 10,
      width: "100%",
      padding: "4px 0",
      background: "transparent",
      border: "none",
      cursor: "pointer",
      color: "inherit",
      textAlign: "left",
    }}
  >
    <span
      style={{
        fontSize: 12,
        color: active ? "var(--fg-1)" : "var(--fg-3)",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        flex: 1,
        minWidth: 0,
      }}
    >
      {label}
    </span>
    <MiniToggle active={active} />
  </button>
);

const KeyLimitEditor = ({ value, onChange }) => {
  const { t } = useT();
  const [editing, setEditing] = ucS(false);
  const [draft, setDraft] = ucS(value ?? "");
  const commit = () => {
    const v = draft === "" ? null : Number(draft);
    if (draft === "" || (!isNaN(v) && v >= 0)) onChange(v);
    setEditing(false);
  };
  return (
    <div className="card" style={{ padding: 12, background: "var(--bg-0)" }}>
      <div className="micro" style={{ marginBottom: 4 }}>
        {t("console.keys.limit.title")}
      </div>
      {editing ? (
        <div className="row gap-2" style={{ marginTop: 4 }}>
          <span className="mono" style={{ fontSize: 13, color: "var(--fg-3)" }}>
            $
          </span>
          <input
            autoFocus
            type="number"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === "Enter") commit();
              if (e.key === "Escape") {
                setDraft(value ?? "");
                setEditing(false);
              }
            }}
            className="input"
            placeholder={t("console.keys.limit.noLimit")}
            style={{ flex: 1, padding: "4px 8px", fontSize: 13 }}
          />
        </div>
      ) : (
        <div
          className="row gap-2"
          style={{ alignItems: "baseline", cursor: "pointer" }}
          onClick={() => {
            setDraft(value ?? "");
            setEditing(true);
          }}
        >
          <span className="mono" style={{ fontSize: 16, color: "var(--fg-1)" }}>
            {value != null ? `$${value}` : "—"}
          </span>
          <span
            className="micro-mono"
            style={{ color: "var(--fg-4)", fontSize: 10 }}
          >
            {value != null
              ? t("console.keys.limit.perMonth")
              : t("console.keys.limit.noLimit")}
          </span>
          <button
            className="btn btn-sm btn-ghost"
            style={{ marginLeft: "auto", padding: "2px 8px", fontSize: 11 }}
          >
            {t("console.keys.limit.edit")}
          </button>
        </div>
      )}
    </div>
  );
};

const TabHeader = ({ title, subtitle, actions }) => (
  <div
    className="tab-header row between"
    style={{ marginBottom: 24, alignItems: "flex-end", gap: 14 }}
  >
    <div className="tab-header-text" style={{ minWidth: 0, flex: 1 }}>
      <h1
        className="tab-header-title"
        style={{
          margin: 0,
          fontSize: 22,
          letterSpacing: "-0.01em",
          fontWeight: 500,
          wordBreak: "break-word",
        }}
      >
        {title}
      </h1>
      {subtitle && (
        <p style={{ margin: "4px 0 0", color: "var(--fg-3)", fontSize: 13 }}>
          {subtitle}
        </p>
      )}
    </div>
    {actions && (
      <div className="tab-header-actions" style={{ flexShrink: 0 }}>
        {actions}
      </div>
    )}
  </div>
);

// Overview > "Calls" area chart — Recharts.
// Replaces a previous handwritten SVG polyline that had no axes, grid, or hover.
// Driven by GET /keys/usage/daily which returns [{ date: 'YYYY-MM-DD', calls, spent }].
const OverviewChartTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0].payload;
  const spent = parseFloat(p.spent || 0);
  return (
    <div
      style={{
        background: "var(--bg-1)",
        border: "1px solid var(--line)",
        borderRadius: 6,
        padding: "8px 12px",
        fontSize: 11,
        color: "var(--fg-1)",
        boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
        minWidth: 160,
      }}
    >
      <div style={{ fontSize: 11, color: "var(--fg-2)", marginBottom: 6 }}>
        {p.date}
      </div>
      <div className="row between" style={{ marginBottom: 2 }}>
        <span style={{ color: "var(--fg-3)" }}>Calls</span>
        <span className="mono">{Number(p.calls || 0).toLocaleString()}</span>
      </div>
      <div className="row between">
        <span style={{ color: "var(--fg-3)" }}>Spent</span>
        <span className="mono">${spent.toFixed(spent < 1 ? 4 : 2)}</span>
      </div>
    </div>
  );
};

const OverviewCallsChart = ({ daily }) => {
  if (!daily || daily.length === 0) {
    return (
      <div
        style={{
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "var(--fg-3)",
          fontSize: 12,
        }}
      >
        No activity yet
      </div>
    );
  }
  const data = daily.map((d) => ({
    date: d.date || "",
    label: (d.date || "").slice(5), // MM-DD
    calls: Number(d.calls) || 0,
    spent: parseFloat(d.spent) || 0,
  }));
  return (
    <ResponsiveContainer width="100%" height="100%">
      {/* margin.left + YAxis width sized for abbreviated K/M ticks. The default
          width=32 clipped 6-digit numbers (e.g. "257016") into "017" — labels
          looked broken on heavy-traffic accounts. */}
      <AreaChart data={data} margin={{ top: 8, right: 8, left: 4, bottom: 0 }}>
        <defs>
          <linearGradient id="overviewCallsFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--fg-1)" stopOpacity={0.25} />
            <stop offset="100%" stopColor="var(--fg-1)" stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid
          strokeDasharray="2 4"
          stroke="var(--line)"
          vertical={false}
        />
        <XAxis
          dataKey="label"
          stroke="var(--fg-4)"
          tick={{ fontSize: 10 }}
          axisLine={false}
          tickLine={false}
        />
        <YAxis
          stroke="var(--fg-4)"
          tick={{ fontSize: 10 }}
          axisLine={false}
          tickLine={false}
          width={42}
          allowDecimals={false}
          tickFormatter={compactNumber}
        />
        <Tooltip
          content={<OverviewChartTooltip />}
          cursor={{ stroke: "var(--fg-3)", strokeDasharray: "3 3" }}
        />
        <Area
          type="monotone"
          dataKey="calls"
          stroke="var(--fg-1)"
          strokeWidth={1.5}
          fill="url(#overviewCallsFill)"
          activeDot={{
            r: 4,
            stroke: "var(--bg-1)",
            strokeWidth: 2,
            fill: "var(--fg-1)",
          }}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
};

const StatCard = ({ label, value, change, changeHint, series, loading }) => (
  <div className="card" style={{ padding: 18 }}>
    <div className="micro">{label}</div>
    <div
      className="row between"
      style={{ marginTop: 10, alignItems: "flex-end" }}
    >
      {loading ? (
        <span
          className="skeleton"
          style={{ display: "inline-block", width: 96, height: 26, borderRadius: 4 }}
        />
      ) : (
        <div className="mono" style={{ fontSize: 26, letterSpacing: "-0.02em", fontVariantNumeric: "tabular-nums" }}>
          {value}
        </div>
      )}
      {series && !loading && (
        <XPrim.Sparkline
          values={series}
          width={64}
          height={24}
          stroke="var(--fg-2)"
        />
      )}
    </div>
    {change != null && !loading && (
      <div className="row gap-2" style={{ marginTop: 10, fontSize: 11 }}>
        <span
          className="mono"
          style={{ color: change > 0 ? "var(--ok)" : change < 0 ? "var(--err)" : "var(--fg-4)" }}
        >
          {change > 0 ? "↑" : change < 0 ? "↓" : "→"} {Math.abs(change)}%
        </span>
        <span style={{ color: "var(--fg-4)" }}>{changeHint || "vs last 7d"}</span>
      </div>
    )}
  </div>
);

// An account boundary remounts all data, cursors, selections and drawers on
// the first render, and runs cleanup for every pending request of the old owner.
const accountScope = ({ authed, user }) => JSON.stringify([Boolean(authed), user?._raw?.id ?? user?.id ?? null]);
const OverviewTab = () => <OverviewTabState key={accountScope(XShell.useAuth())} />;
const OverviewTabState = () => {
  const { t } = useT();
  const { user, authed } = XShell.useAuth();
  const { navigate } = XShell.useRoute();
  const [overview, setOverview] = ucS(null);
  const [daily, setDaily] = ucS([]);
  const [recent, setRecent] = ucS([]);
  const [byApi, setByApi] = ucS([]);
  const [loading, setLoading] = ucS(true);
  // True when any of the four fetches rejected — the tab keeps whatever
  // landed but shows a retry bar instead of silently rendering $0.00 (which
  // used to be indistinguishable from "genuinely no data").
  const [loadErr, setLoadErr] = ucS(false);
  const [reloadFlag, setReloadFlag] = ucS(0);
  // Chart-level API filter — '' = all APIs (chart/daily-spend then reuse the
  // global `daily` payload; StatCards always show the global totals). A
  // specific selection fires its own filtered daily fetch into `apiDaily`.
  const [selApiId, setSelApiId] = ucS('');
  const [apiDaily, setApiDaily] = ucS(null);
  const [apiDailyLoading, setApiDailyLoading] = ucS(false);
  // Page-wide time range — drives daily/sparkline + StatCard "Calls" / "Spend" labels.
  // Persisted to localStorage so the user's choice survives navigation.
  const [range, setRange] = ucS(() => {
    try {
      return localStorage.getItem("xapi.overview.range") || "7d";
    } catch {
      return "7d";
    }
  });
  React.useEffect(() => {
    try {
      localStorage.setItem("xapi.overview.range", range);
    } catch {}
  }, [range]);
  const rangeDays = range === "1d" ? 1 : range === "30d" ? 30 : 7;
  const rangeLabel =
    range === "1d"
      ? t("common.lastDay")
      : range === "30d"
        ? t("common.last30d")
        : t("common.last7d");

  React.useEffect(() => {
    if (!authed) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setLoadErr(false);
    // daily is pulled for 2× the selected range so the StatCards can compute
    // a period-over-period delta (current window vs the window before it)
    // without a second endpoint. byApi covers exactly the selected range.
    Promise.allSettled([
      XApi.statsService.overview(),
      XApi.usageService.daily(rangeDays * 2),
      XApi.usageService.all(6, 0),
      XApi.usageService.byApi(rangeDays),
    ]).then(([o, d, r, b]) => {
      if (cancelled) return;
      if (o.status === 'fulfilled') setOverview(o.value);
      if (d.status === 'fulfilled') setDaily(d.value || []);
      if (r.status === 'fulfilled') setRecent((r.value && r.value.records) || []);
      if (b.status === 'fulfilled') setByApi(b.value || []);
      if ([o, d, r, b].some((x) => x.status === 'rejected')) setLoadErr(true);
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // NB: no user?.balance dep — topping up must not re-fire all four fetches;
    // the balance figure itself comes back in `overview` anyway.
  }, [authed, rangeDays, reloadFlag]);

  // Per-API chart fetch — only fires when a specific API is selected; '' means
  // "all" and simply reuses the global `daily` payload (zero extra requests).
  React.useEffect(() => {
    if (!authed || !selApiId) {
      setApiDaily(null);
      return;
    }
    let cancelled = false;
    setApiDailyLoading(true);
    XApi.usageService.daily(rangeDays * 2, selApiId)
      .then((rows) => { if (!cancelled) setApiDaily(rows || []); })
      .catch(() => { if (!cancelled) setApiDaily([]); })
      .finally(() => { if (!cancelled) setApiDailyLoading(false); });
    return () => { cancelled = true; };
  }, [authed, selApiId, rangeDays]);

  // Drop a stale API selection: when the range's byApi payload no longer
  // contains the selected service (it had no calls in this window), fall back
  // to "all APIs" — otherwise the Select trigger renders a raw UUID.
  React.useEffect(() => {
    if (!selApiId || loading) return;
    if (!byApi.some((a) => a.apiId === selApiId)) setSelApiId('');
  }, [byApi, selApiId, loading]);

  // Range selector — three pill buttons shared by all widgets on this tab.
  const RangePicker = () => (
    <div
      className="row gap-1 hairline"
      style={{ borderRadius: 6, padding: 2, background: "var(--bg-2)" }}
    >
      {[
        { k: "1d", l: t("common.lastDay") },
        { k: "7d", l: t("common.last7d") },
        { k: "30d", l: t("common.last30d") },
      ].map((r) => (
        <button
          key={r.k}
          onClick={() => setRange(r.k)}
          className="btn btn-sm"
          style={{
            border: "none",
            background: range === r.k ? "var(--bg-3)" : "transparent",
            color: range === r.k ? "var(--fg-1)" : "var(--fg-3)",
            fontSize: 11,
            padding: "4px 10px",
          }}
        >
          {r.l}
        </button>
      ))}
    </div>
  );

  if (!authed) {
    return (
      <>
        <TabHeader
          title={t("console.overview.title")}
          subtitle={t("console.overview.signinSubtitle")}
        />
        <div
          className="stat-grid-4"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: 12,
          }}
        >
          <StatCard label={t("console.overview.stat.calls")} value="—" />
          <StatCard label={t("console.overview.stat.spent")} value="—" />
          <StatCard label={t("console.overview.stat.apiKeys")} value="—" />
          <StatCard label={t("console.overview.stat.balance")} value="—" />
        </div>
      </>
    );
  }

  // daily covers 2× the range — split into current vs previous window so
  // StatCards can show a period-over-period delta. Dates arrive as YYYY-MM-DD
  // strings so a lexicographic cutoff comparison is safe.
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - rangeDays);
  const cutoffStr = cutoff.toISOString().split("T")[0];
  const curDaily = daily.filter((d) => (d.date || "") >= cutoffStr);
  const prevDaily = daily.filter((d) => (d.date || "") < cutoffStr);

  const callsTotal = curDaily.reduce((s, x) => s + (parseInt(x.calls, 10) || 0), 0);
  const spendTotal = curDaily.reduce((s, x) => s + (parseFloat(x.spent) || 0), 0);
  const prevCalls = prevDaily.reduce((s, x) => s + (parseInt(x.calls, 10) || 0), 0);
  const prevSpend = prevDaily.reduce((s, x) => s + (parseFloat(x.spent) || 0), 0);
  // % delta; null when the previous window had no activity (a "new vs nothing"
  // comparison reads as noise, not signal).
  const pctChange = (cur, prev) =>
    prev <= 0 ? null : Math.max(-999, Math.min(999, Math.round(((cur - prev) / prev) * 100)));
  const callsChange = pctChange(callsTotal, prevCalls);
  const spendChange = pctChange(spendTotal, prevSpend);

  const vals = curDaily.length
    ? curDaily.map((d) => parseInt(d.calls, 10) || 0)
    : [0, 0, 0, 0, 0, 0, 0];
  // Spend card gets the SPEND series (it was wrongly wired to the calls
  // series, so both cards drew the same curve).
  const spendVals = curDaily.length
    ? curDaily.map((d) => parseFloat(d.spent) || 0)
    : [0, 0, 0, 0, 0, 0, 0];
  // Chart + daily-spend follow the API selector: '' reuses the global daily,
  // a specific API uses its own filtered fetch. StatCards above stay global.
  const chartSource = selApiId ? (apiDaily || []) : daily;
  const curChartDaily = chartSource.filter((d) => (d.date || "") >= cutoffStr);
  const chartLoading = selApiId ? apiDailyLoading : loading;
  const balance =
    overview?.currentBalance != null
      ? parseFloat(overview.currentBalance)
      : user?.balance || 0;

  return (
    <>
      <TabHeader
        title={t("console.overview.welcome").replace(
          "{USER}",
          user?.handle || "",
        )}
        subtitle={t("console.overview.subtitle").replace("{RANGE}", rangeLabel)}
        actions={<RangePicker />}
      />
      <div
        className="stat-grid-4"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: 12,
        }}
      >
        <StatCard
          label={`${t("console.overview.stat.calls")} · ${rangeLabel}`}
          value={XPrim.fmt(callsTotal)}
          series={vals}
          change={callsChange}
          changeHint={t("console.overview.vsPrev")}
          loading={loading}
        />
        <StatCard
          label={`${t("console.overview.stat.spent")} · ${rangeLabel}`}
          value={XPrim.fmtTinyUsd(spendTotal)}
          series={spendVals}
          change={spendChange}
          changeHint={t("console.overview.vsPrev")}
          loading={loading}
        />
        <StatCard
          label={t("console.overview.stat.apiKeys")}
          value={String(overview?.apiKeyCount ?? "—")}
          loading={loading}
        />
        <StatCard
          label={t("console.overview.stat.balance")}
          value={"$" + balance.toFixed(2)}
          loading={loading}
        />
      </div>

      {loadErr && (
        <div
          className="row between"
          style={{
            marginTop: 12,
            padding: "10px 14px",
            borderRadius: 6,
            background: "rgba(239,68,68,0.08)",
            border: "1px solid rgba(239,68,68,0.3)",
            color: "var(--err)",
            fontSize: 12,
            alignItems: "center",
          }}
        >
          <span>{t("console.overview.loadErr")}</span>
          <button className="btn btn-sm" onClick={() => setReloadFlag((f) => f + 1)}>
            {t("common.retry")}
          </button>
        </div>
      )}

      {user?.referredBy && (
        <div className="card" style={{ marginTop: 16, padding: "14px 18px" }}>
          <div
            className="row gap-3"
            style={{ alignItems: "center", flexWrap: "wrap" }}
          >
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 6,
                background: "var(--bg-3)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <XIcons.IconUsers size={14} style={{ color: "var(--fg-3)" }} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="micro" style={{ marginBottom: 2 }}>
                Invited by
              </div>
              <div style={{ fontSize: 13, color: "var(--fg-1)" }}>
                {user.referredBy.twitterScreenName ? (
                  <>
                    @
                    <span className="mono">
                      {user.referredBy.twitterScreenName}
                    </span>
                    {user.referredBy.twitterName ? (
                      <span style={{ color: "var(--fg-3)" }}>
                        {" "}
                        · {user.referredBy.twitterName}
                      </span>
                    ) : null}
                  </>
                ) : (
                  <span style={{ color: "var(--fg-3)" }}>(no profile)</span>
                )}
                {user.referredBy.referralCode && (
                  <span
                    className="mono"
                    style={{
                      marginLeft: 10,
                      padding: "2px 8px",
                      borderRadius: 4,
                      background: "var(--bg-3)",
                      fontSize: 11,
                      color: "var(--fg-2)",
                    }}
                  >
                    code: {user.referredBy.referralCode}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <div
        className="overview-split"
        style={{
          display: "grid",
          gridTemplateColumns: "2fr 1fr",
          gap: 16,
          marginTop: 16,
        }}
      >
        <div className="card">
          <div
            className="row between"
            style={{
              padding: "14px 18px",
              borderBottom: "1px solid var(--line)",
              gap: 12,
            }}
          >
            <div className="micro" style={{ flexShrink: 0 }}>
              {t("console.overview.callsTitle").replace("{RANGE}", rangeLabel)}
            </div>
            <div className="row gap-2" style={{ alignItems: "center", minWidth: 0 }}>
              <Select
                value={selApiId}
                onChange={setSelApiId}
                options={[
                  { value: '', label: t("console.overview.allApis") },
                  ...byApi.filter(a => a.apiId).map((a) => ({ value: a.apiId, label: a.name })),
                ]}
                width={170}
              />
              <div className="micro-mono" style={{ flexShrink: 0 }}>
                {curChartDaily.reduce((s, x) => s + (parseInt(x.calls, 10) || 0), 0).toLocaleString()} total
              </div>
            </div>
          </div>
          <div style={{ padding: 12, height: 220 }}>
            {chartLoading ? (
              <div className="skeleton" style={{ width: "100%", height: "100%", borderRadius: 6 }} />
            ) : (
              <OverviewCallsChart daily={curChartDaily} />
            )}
          </div>
        </div>
        <div className="card">
          <div
            className="row between"
            style={{
              padding: "14px 18px",
              borderBottom: "1px solid var(--line)",
            }}
          >
            <div className="micro">{t("console.overview.dailySpend")}</div>
          </div>
          {/* maxHeight 220 keeps the right column visually aligned with the
              left chart body (also height 220). 30+ day rows would otherwise
              stretch this card to 1000+px and break the grid layout. */}
          <div style={{ padding: 4, maxHeight: 220, overflowY: "auto" }}>
            {chartLoading && (
              <div style={{ padding: 8 }}>
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="skeleton" style={{ display: "block", height: 28, borderRadius: 4, margin: "8px 6px" }} />
                ))}
              </div>
            )}
            {!chartLoading && curChartDaily.length === 0 && (
              <div
                style={{
                  padding: 20,
                  textAlign: "center",
                  color: "var(--fg-3)",
                  fontSize: 12,
                }}
              >
                No spend yet
              </div>
            )}
            {!chartLoading &&
              curChartDaily.length > 0 &&
              (() => {
                const max = Math.max(
                  ...curChartDaily.map((d) => parseFloat(d.spent) || 0),
                  0.0001,
                );
                return curChartDaily.map((d, i) => {
                  const v = parseFloat(d.spent) || 0;
                  return (
                    <div key={d.date || i} style={{ padding: "8px 14px" }}>
                      <div
                        className="row between"
                        style={{ fontSize: 12, marginBottom: 4 }}
                      >
                        <span
                          className="mono"
                          style={{ fontSize: 11, color: "var(--fg-3)" }}
                        >
                          {(d.date || "").slice(5)}
                        </span>
                        <span className="mono" style={{ color: "var(--fg-3)" }}>
                          {XPrim.fmtTinyUsd(v)}
                        </span>
                      </div>
                      <div className="bar-track">
                        <div
                          className="bar-fill"
                          style={{ width: (v / max) * 100 + "%" }}
                        />
                      </div>
                    </div>
                  );
                });
              })()}
          </div>
        </div>
      </div>

      {/* Top APIs — usage aggregated per service by the backend
          (GET /keys/usage/by-api), covering exactly the selected range.
          Rows link through to the marketplace detail page. */}
      <div className="card" style={{ marginTop: 16 }}>
        <div
          className="row between"
          style={{
            padding: "14px 18px",
            borderBottom: "1px solid var(--line)",
          }}
        >
          <div className="micro">
            {t("console.overview.topApis").replace("{RANGE}", rangeLabel)}
          </div>
          {!loading && byApi.length > 0 && (
            <div className="micro-mono">
              {byApi.length} {t("console.overview.topApisCount")}
            </div>
          )}
        </div>
        <div style={{ padding: "6px 0" }}>
          {loading && (
            <div style={{ padding: 8 }}>
              {[1, 2, 3].map((i) => (
                <div key={i} className="skeleton" style={{ display: "block", height: 34, borderRadius: 4, margin: "8px 10px" }} />
              ))}
            </div>
          )}
          {!loading && byApi.length === 0 && (
            <div style={{ padding: 20, textAlign: "center", color: "var(--fg-3)", fontSize: 12 }}>
              {t("console.overview.topApisEmpty")}
            </div>
          )}
          {!loading && byApi.length > 0 && (() => {
            const shown = topApisByCalls(byApi);
            return shown.map((a) => (
              <div
                key={a.apiId || a.name}
                className="top-api-row"
                onClick={() => a.apiId && navigate("api-detail", { id: XApi.helpers.apiSlug({ id: a.apiId, host: a.host }) })}
                style={{
                  display: "grid",
                  gridTemplateColumns: "minmax(160px, 240px) 1fr 90px 90px",
                  alignItems: "center",
                  gap: 14,
                  padding: "9px 18px",
                  cursor: a.apiId ? "pointer" : "default",
                }}
              >
                <div className="row gap-2" style={{ minWidth: 0, alignItems: "center" }}>
                  <ApiAvatar name={a.name} logoUrl={a.logoUrl} size={24} radius={5} fontSize={11} />
                  <span style={{ fontSize: 13, color: "var(--fg-1)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {a.name}
                  </span>
                </div>
                <div className="bar-track">
                  <div className="bar-fill" style={{ width: `${a.usagePercent}%` }} />
                </div>
                <span className="mono" style={{ fontSize: 11, color: "var(--fg-3)", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                  {XPrim.fmt(a.calls)} {t("market.callsSuffix")}
                </span>
                <span className="mono" style={{ fontSize: 11, color: "var(--fg-1)", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                  {XPrim.fmtTinyUsd(a.cost || 0)}
                </span>
              </div>
            ));
          })()}
        </div>
      </div>

      <div className="card" style={{ marginTop: 16 }}>
        <div
          className="row between"
          style={{
            padding: "14px 18px",
            borderBottom: "1px solid var(--line)",
          }}
        >
          <div className="micro">{t("console.overview.activityTitle")}</div>
        </div>
        {loading && (
          <div style={{ padding: 20, textAlign: "center" }}>
            <div className="spinner" />
          </div>
        )}
        {!loading && recent.length === 0 && (
          <div
            style={{
              padding: 32,
              textAlign: "center",
              color: "var(--fg-3)",
              fontSize: 13,
            }}
          >
            {t("console.overview.empty")}
          </div>
        )}
        {!loading && recent.length > 0 && (
          <table className="table">
            <thead>
              <tr>
                <th>{t("console.overview.col.time")}</th>
                <th>{t("console.overview.col.api")}</th>
                <th>{t("console.overview.col.path")}</th>
                <th>{t("console.overview.col.status")}</th>
                <th>{t("console.overview.col.cost")}</th>
              </tr>
            </thead>
            <tbody>
              {recent.map((c, i) => {
                const s = usageStatus(c);
                // Same field-resolution as UsageTab — server returns nested
                // endpoint.version.api / apiKey, not flattened. See key.service.getAllUsageRecords.
                const apiName =
                  c.endpoint?.version?.api?.name ||
                  c.apiServiceName ||
                  c.apiName ||
                  "—";
                const path = c.endpoint?.path || c.path || "";
                const method = c.method || c.endpoint?.method || "";
                const costN = parseFloat(c.amount || c.cost || 0);
                const costStr =
                  costN === 0
                    ? "$0"
                    : costN < 0.0001
                      ? "<$0.0001"
                      : costN < 1
                        ? "$" +
                          costN.toFixed(6).replace(/0+$/, "").replace(/\.$/, "")
                        : "$" + costN.toFixed(4);
                return (
                  <tr key={c.id || i}>
                    <td
                      className="mono"
                      style={{ fontSize: 11, color: "var(--fg-3)" }}
                    >
                      {(c.timestamp || c.createdAt || "")
                        .replace("T", " ")
                        .slice(0, 19)}
                    </td>
                    <td style={{ fontSize: 12 }}>
                      <span className="tag" style={{ fontSize: 10 }}>
                        {apiName}
                      </span>
                    </td>
                    <td
                      className="mono"
                      style={{ fontSize: 11, whiteSpace: "nowrap" }}
                    >
                      {method && (
                        <span style={{ color: "var(--fg-3)", marginRight: 6 }}>
                          {method}
                        </span>
                      )}
                      <span style={{ color: "var(--fg-2)" }}>
                        {path || "—"}
                      </span>
                    </td>
                    <td>
                      <span
                        className="mono"
                        style={{
                          fontSize: 11,
                          color:
                            s > 0 && s < 300
                              ? "var(--ok)"
                              : s >= 300 && s < 500
                                ? "var(--warn)"
                                : "var(--err)",
                        }}
                      >
                        {s}
                      </span>
                    </td>
                    <td
                      className="mono"
                      style={{ fontSize: 12 }}
                      title={`$${costN.toFixed(8)}`}
                    >
                      {costStr}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
};

const formatKeyLastUsed = (iso, t) => {
  if (!iso) return t("console.keys.lastUsed.never");
  const timestamp = new Date(iso).getTime();
  if (!Number.isFinite(timestamp)) return "—";
  const elapsed = Date.now() - timestamp;
  if (elapsed < 60_000) return t("console.keys.lastUsed.now");
  if (elapsed < 3_600_000) return t("console.keys.lastUsed.minutes", { n: Math.floor(elapsed / 60_000) });
  if (elapsed < 86_400_000) return t("console.keys.lastUsed.hours", { n: Math.floor(elapsed / 3_600_000) });
  if (elapsed < 30 * 86_400_000) return t("console.keys.lastUsed.days", { n: Math.floor(elapsed / 86_400_000) });
  return String(iso).slice(0, 10);
};

const KeysTab = () => {
  const { t } = useT();
  const [modal, setModal] = ucS(null);
  // (expanded-row state removed — desktop table is now fully inline like xapi-new.
  // Mobile still uses drawer via drawerKeyId.)
  const [drawerKeyId, setDrawerKeyId] = ucS(null); // mobile bottom drawer
  const [permsKeyId, setPermsKeyId] = ucS(null); // 权限弹窗目标 key id
  const [ipKeyId, setIpKeyId] = ucS(null);
  const [ipPending, setIpPending] = ucS(false);
  const [connectDlg, setConnectDlg] = ucS(null);
  const [revokeDlg, setRevokeDlg] = ucS(null);
  const [keys, setKeys] = ucS([]);
  const [loading, setLoading] = ucS(true);
  const [loadErr, setLoadErr] = ucS(null);
  const [scopePendingKeyIds, setScopePendingKeyIds] = ucS(() => new Set());
  const scopePendingRef = React.useRef(new Set());
  const [workerOptions, setWorkerOptions] = ucS([]);
  const [workerAccessPendingKeyId, setWorkerAccessPendingKeyId] = ucS(null);

  // Lock body scroll while the keys drawer is open.
  React.useEffect(() => {
    if (!drawerKeyId) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [drawerKeyId]);
  const {
    authed,
    canManageKeys,
    canUseXntCloud,
    isApiKeyLogin,
    isTwitterLogin,
  } =
    XShell.useAuth();
  const { toast } = XPrim.useToast();
  const { navigate: navConsoleRoot } = XShell.useRoute();
  const blockWrite = () => {
    toast(t("console.keys.verifyRequired"));
    navConsoleRoot("bind");
  };

  const reloadKeys = React.useCallback(async () => {
    if (!authed) {
      setKeys([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadErr(null);
    try {
      const list = await XApi.keyService.list();
      const adapted = list.map(XApi.adapters.apiKey);
      setKeys(adapted);
      // (Used to auto-expand the first key — no longer applicable, rows are inline.)
    } catch (e) {
      setLoadErr(e.message || t("console.keys.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [authed]);

  React.useEffect(() => {
    reloadKeys();
  }, [reloadKeys]);

  React.useEffect(() => {
    if (!permsKeyId) return;
    let cancelled = false;
    listWorkers()
      .then((items) => {
        if (!cancelled) setWorkerOptions(Array.isArray(items) ? items : []);
      })
      .catch(() => {
        if (!cancelled) setWorkerOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [permsKeyId]);

  // Optimistic local patch + remote update; revert on failure.
  const updateKey = (id, patch) =>
    setKeys((ks) => ks.map((k) => (k.id === id ? { ...k, ...patch } : k)));
  // Provider avatar helper — renders the correct brand icon for Twitter/X
  const remoteToggleSandbox = async (k) => {
    if (!canManageKeys) return blockWrite();
    const next = !k.sandbox;
    updateKey(k.id, { sandbox: next });
    try {
      await XApi.keyService.updateAllowSandbox(k.id, next);
      toast(next ? t("console.keys.sandboxEnabled") : t("console.keys.sandboxDisabled"));
    } catch (e) {
      updateKey(k.id, { sandbox: !next });
      toast(e.message || t("console.keys.updateFailed"));
    }
  };
  // Agent 程序化权限：增删一个 scope（含 service:create 时后端同步 allowRegister）。
  const remoteToggleScope = async (k, scope) => {
    if (!canManageKeys) return blockWrite();
    if (scopePendingRef.current.has(k.id)) return;
    scopePendingRef.current.add(k.id);
    setScopePendingKeyIds(new Set(scopePendingRef.current));
    const cur = k.scopes || [];
    const next = toggleScope(cur, scope);
    updateKey(k.id, {
      scopes: next,
      register: next.includes("service:create"),
    });
    try {
      const saved = await XApi.keyService.updateScopes(k.id, next);
      const savedScopes = Array.isArray(saved?.scopes) ? saved.scopes : next;
      updateKey(k.id, {
        scopes: savedScopes,
        register: savedScopes.includes("service:create"),
      });
    } catch (e) {
      updateKey(k.id, {
        scopes: cur,
        register: cur.includes("service:create"),
      });
      toast(e.message || t("console.keys.updateFailed"));
    } finally {
      scopePendingRef.current.delete(k.id);
      setScopePendingKeyIds(new Set(scopePendingRef.current));
    }
  };
  const remoteSetLimit = async (k, v) => {
    if (!canManageKeys) return blockWrite();
    const prev = k.limit;
    updateKey(k.id, { limit: v });
    try {
      if (v == null) await XApi.keyService.removeLimit(k.id);
      else await XApi.keyService.setLimit(k.id, v);
      toast(t("console.keys.limit.updated"));
    } catch (e) {
      updateKey(k.id, { limit: prev });
      toast(e.message || t("console.keys.limit.failed"));
    }
  };
  const remoteSetIpAllowlist = async (k, allowedCidrs) => {
    if (!canManageKeys) return blockWrite();
    setIpPending(true);
    try {
      const saved = await XApi.keyService.updateIpAllowlist(k.id, allowedCidrs);
      updateKey(k.id, {
        allowedCidrs: Array.isArray(saved?.allowedCidrs)
          ? saved.allowedCidrs
          : allowedCidrs,
      });
      setIpKeyId(null);
      toast(t("console.keys.ip.updated"));
    } catch (e) {
      toast(e.message || t("console.keys.ip.failed"));
    } finally {
      setIpPending(false);
    }
  };
  const remoteSetWorkerAccess = async (k, mode, workerIds) => {
    if (!canManageKeys) return blockWrite('Updating Worker access');
    if (workerAccessPendingKeyId) return;
    const previous = {
      workerAccessMode: k.workerAccessMode,
      workerAccessIds: k.workerAccessIds,
    };
    const nextIds = mode === 'OWN_AND_SELECTED' ? workerIds : [];
    setWorkerAccessPendingKeyId(k.id);
    updateKey(k.id, { workerAccessMode: mode, workerAccessIds: nextIds });
    try {
      const saved = await XApi.keyService.updateWorkerAccess(
        k.id,
        mode,
        nextIds,
      );
      updateKey(k.id, {
        workerAccessMode: saved?.workerAccessMode || mode,
        workerAccessIds: Array.isArray(saved?.workerAccessIds)
          ? saved.workerAccessIds
          : nextIds,
      });
      toast(t("console.keys.workers.updated"));
    } catch (e) {
      updateKey(k.id, previous);
      toast(e.message || t("console.keys.workers.failed"));
    } finally {
      setWorkerAccessPendingKeyId(null);
    }
  };
  const remoteDelete = async (k) => {
    if (!canManageKeys) return blockWrite();
    if (!confirm(t("console.keys.deleteConfirm", { name: k.name }))) return;
    const prev = keys;
    setKeys((ks) => ks.filter((x) => x.id !== k.id));
    try {
      await XApi.keyService.delete(k.id);
      toast(t("console.keys.deleted"));
    } catch (e) {
      setKeys(prev);
      toast(e.message || t("console.keys.deleteFailed"));
    }
  };

  const [providers, setProviders] = ucS([]);
  const [bindings, setBindings] = ucS([]);
  const [acting, setActing] = ucS(false);

  const reloadOAuth = React.useCallback(async () => {
    if (!authed) {
      setProviders([]);
      setBindings([]);
      return;
    }
    try {
      const [p, b] = await Promise.allSettled([
        XApi.oauthService.listProviders(),
        XApi.oauthService.listBindings(),
      ]);
      if (p.status === "fulfilled") setProviders(p.value || []);
      if (b.status === "fulfilled") setBindings(b.value || []);
    } catch {}
  }, [authed]);

  React.useEffect(() => {
    reloadOAuth();
  }, [reloadOAuth]);

  // After OAuth callback, services.jsx seeded route { tab: 'keys', oauth: 'success'|'error' }.
  // Consume once and clear via navigate so a re-mount doesn't re-toast.
  const { route, navigate: navConsole } = XShell.useRoute();
  const oauthFlag = route.params && route.params.oauth;
  React.useEffect(() => {
    if (!oauthFlag) return;
    if (oauthFlag === "success") {
      toast(t("console.keys.oauth.connected"));
      reloadOAuth();
    } else if (oauthFlag === "error") {
      toast(t("console.keys.oauth.connectFailed"));
    }
    const next = { ...route.params };
    delete next.oauth;
    navConsole(route.page, next);
  }, [oauthFlag]);

  // OAuth scopes from the backend are space-separated strings (OAuth standard),
  // not arrays. Format them for display as a comma-separated list.
  const formatScopes = (s) => {
    if (Array.isArray(s)) return s.join(", ");
    if (typeof s === "string") return s.split(/\s+/).filter(Boolean).join(", ");
    return "";
  };

  const bindingsFor = (keyId) => {
    const own = (bindings || []).filter((b) => b && b.apiKeyId === keyId);
    return own.map((b) => {
      // Provider may live in two places — top-level provider record (when oauth
      // service inlines it) OR matched from the providers list by id. Falls
      // back to a stub so an orphan binding still renders something usable.
      const inlined = b.provider || null;
      const p = providers.find((pp) => pp.id === b.providerId) ||
        inlined || { id: b.providerId, name: t("console.keys.oauth.provider") };
      const bindingScopes = formatScopes(b.scopes);
      // Backend returns providerAccountName/AvatarUrl. We were reading b.account
      // (doesn't exist) → the row showed "_". Map them correctly here so the
      // bound row can render the user's real @handle + avatar (per design req).
      return {
        provider: {
          id: p.id,
          name: p.name,
          desc: p.description || "",
          scopes: bindingScopes || formatScopes(p.defaultScopes),
        },
        binding: {
          id: b.id,
          account: b.providerAccountName || b.providerAccountId || "—",
          avatar: b.providerAccountAvatarUrl || null,
          scopes: bindingScopes || "—",
          // OAuthBinding has createdAt/updatedAt; no lastUsedAt/linkedAt fields.
          last: XApi.helpers.formatRelative(b.updatedAt || b.createdAt),
          created: (b.createdAt || "").slice(0, 10),
        },
      };
    });
  };

  const availableFor = (keyId) => {
    const boundIds = new Set(
      (bindings || [])
        .filter((b) => b && b.apiKeyId === keyId)
        .map((b) => b.providerId),
    );
    return (providers || [])
      .filter((p) => p && !boundIds.has(p.id))
      .map((p) => ({
        id: p.id,
        name: p.name,
        desc: p.description || p.type || "",
        scopes: formatScopes(p.defaultScopes),
        // Preserve raw fields the connect dialog needs: scopeDefinitions drives
        // the checkbox UI; defaultScopes is the fallback when no definitions exist.
        defaultScopes: p.defaultScopes,
        scopeDefinitions: Array.isArray(p.scopeDefinitions)
          ? p.scopeDefinitions
          : null,
      }));
  };

  const doConnect = async (scopesStr, providerIdOverride) => {
    if (!connectDlg) return;
    // Dialog body may have picked the provider locally (when opened from the
    // "Enable OAuth" CTA with no preselected provider). Use whatever it
    // resolves to, falling back to the preselected one.
    const providerId = providerIdOverride || connectDlg.provider?.id;
    if (!providerId) {
      toast({ type: "error", title: t("console.keys.oauth.pickFirst") });
      setActing(false);
      return;
    }
    setActing(true);
    try {
      // scopesStr is space-separated (OAuth-standard). undefined = use provider defaultScopes.
      const r = await XApi.oauthService.authorize(
        connectDlg.keyId,
        providerId,
        scopesStr || undefined,
      );
      if (r && r.authorizationUrl) {
        window.location.href = r.authorizationUrl;
      } else {
        throw new Error(t("console.keys.oauth.noUrl"));
      }
    } catch (e) {
      toast(e.message || t("console.keys.oauth.startFailed"));
      setActing(false);
    }
  };

  const doRevoke = async () => {
    if (!revokeDlg) return;
    setActing(true);
    try {
      await XApi.oauthService.deleteBinding(revokeDlg.bindingId);
      toast(t("console.keys.oauth.revoked"));
      setRevokeDlg(null);
      reloadOAuth();
    } catch (e) {
      toast(e.message || t("console.keys.oauth.revokeFailed"));
    } finally {
      setActing(false);
    }
  };

  if (!authed) {
    return (
      <>
        <TabHeader
          title={t("console.keys.title")}
          subtitle={t("console.keys.signinSubtitle")}
        />
        <div className="card" style={{ padding: 32, textAlign: "center" }}>
          <p style={{ color: "var(--fg-3)", fontSize: 13, marginBottom: 16 }}>
            {t("console.keys.signedOut")}
          </p>
          <button
            className="btn btn-primary"
            onClick={() => navConsole("login")}
          >
            {t("console.keys.signIn")}
          </button>
        </div>
      </>
    );
  }

  // ---- Simplified view for apikey-only users WITHOUT a linked X handle ----
  // They have exactly one key (the one they signed in with) and zero CRUD permissions.
  // No table, no toggles, no OAuth bindings — just a single status card with the upgrade CTA.
  // Merged users (apikey login + twitterId set) skip this and see the full keys table.
  if (isApiKeyLogin && !isTwitterLogin) {
    const k = keys[0]; // backend returns at least the sign-in key
    return (
      <>
        <TabHeader
          title={t("console.keys.title")}
          subtitle={t("console.keys.readOnlySubtitle")}
        />
        {loading && (
          <div className="card" style={{ padding: 24, textAlign: "center" }}>
            <div className="spinner" />
          </div>
        )}
        {!loading && loadErr && (
          <div
            className="card"
            style={{ padding: 18, color: "var(--err)", fontSize: 13 }}
          >
            {t("console.keys.loadFailed")}: {loadErr}
            <button
              className="btn btn-sm"
              style={{ marginLeft: 12 }}
              onClick={reloadKeys}
            >
              <XIcons.IconRefresh size={11} /> {t("common.retry")}
            </button>
          </div>
        )}
        {!loading && !loadErr && k && (
          <div className="card" style={{ padding: 24 }}>
            <div
              className="row gap-3"
              style={{ alignItems: "flex-start", marginBottom: 16 }}
            >
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 8,
                  background: "var(--bg-3)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <XIcons.IconKey size={16} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="row gap-2" style={{ alignItems: "center" }}>
                  <span style={{ fontSize: 14, fontWeight: 500 }}>
                    {k.name || t("console.keys.signInKey")}
                  </span>
                  <span className="tag" style={{ fontSize: 10 }}>
                    <span className="tag-dot ok" /> {t("console.keys.active")}
                  </span>
                </div>
                <div
                  className="mono"
                  style={{ fontSize: 12, color: "var(--fg-3)", marginTop: 4 }}
                >
                  {k.preview}
                </div>
              </div>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: 12,
                marginBottom: 18,
              }}
            >
              <div>
                <div className="micro">{t("console.keys.totalCalls")}</div>
                <div className="mono" style={{ fontSize: 18, marginTop: 2 }}>
                  {k.usage.toLocaleString()}
                </div>
              </div>
              <div>
                <div className="micro">{t("console.keys.spent")}</div>
                <div className="mono" style={{ fontSize: 18, marginTop: 2 }}>
                  ${k.used.toFixed(2)}
                </div>
              </div>
              <div>
                <div className="micro">{t("console.keys.col.lastUsed")}</div>
                <div
                  style={{ fontSize: 14, marginTop: 4, color: "var(--fg-2)" }}
                >
                  {formatKeyLastUsed(k._raw?.lastUsedAt, t)}
                </div>
              </div>
            </div>

            <div
              className="card"
              style={{
                padding: 14,
                background: "rgba(245,165,36,0.06)",
                borderColor: "rgba(245,165,36,0.3)",
              }}
            >
              <div
                style={{ fontSize: 13, lineHeight: 1.65, color: "var(--fg-2)" }}
              >
                {t("console.keys.verifyHint")}
              </div>
              <button
                className="btn btn-primary"
                style={{
                  marginTop: 12,
                  width: "100%",
                  justifyContent: "center",
                }}
                onClick={() => navConsoleRoot("bind")}
              >
                <XIcons.IconTwitter size={13} /> {t("console.keys.verify")}
              </button>
            </div>
          </div>
        )}
        {!loading && !loadErr && !k && (
          <div
            className="card"
            style={{
              padding: 32,
              textAlign: "center",
              color: "var(--fg-3)",
              fontSize: 13,
            }}
          >
            {t("console.keys.notFound")}
          </div>
        )}
      </>
    );
  }

  // ---- Full view (ENTITY users) — banner kept as a defensive no-op for non-apikey users.
  // Merged users (apikey login + twitterId set) skip the banner — they've already verified X.
  const ReadOnlyBanner = () =>
    isApiKeyLogin && !isTwitterLogin ? (
      <div
        className="card"
        style={{
          padding: 14,
          marginBottom: 14,
          background: "rgba(245,165,36,0.06)",
          borderColor: "rgba(245,165,36,0.3)",
        }}
      >
        <div className="row gap-3" style={{ alignItems: "flex-start" }}>
          <XIcons.IconShield
            size={14}
            style={{ color: "#f5c87a", marginTop: 2 }}
          />
          <div
            style={{
              flex: 1,
              fontSize: 13,
              lineHeight: 1.6,
              color: "var(--fg-2)",
            }}
          >
            {t("console.keys.readOnlyBanner")}
          </div>
          <button className="btn btn-sm" onClick={() => navConsoleRoot("bind")}>
            <XIcons.IconTwitter size={11} /> {t("console.keys.verify")}
          </button>
        </div>
      </div>
    ) : null;

  return (
    <>
      <TabHeader
        title={t("console.keys.title")}
        subtitle={t("console.keys.subtitle")}
        actions={
          <button
            className="btn btn-primary"
            onClick={() =>
              canManageKeys ? setModal("create") : blockWrite()
            }
            disabled={!canManageKeys}
            title={!canManageKeys ? t("console.keys.verifyCreate") : ""}
            style={
              !canManageKeys ? { opacity: 0.5, cursor: "not-allowed" } : null
            }
          >
            <XIcons.IconPlus size={13} /> {t("console.keys.create")}
          </button>
        }
      />

      <ReadOnlyBanner />

      {loading && (
        <div className="card" style={{ padding: 24, textAlign: "center" }}>
          <div className="spinner" />{" "}
          <span style={{ marginLeft: 10, color: "var(--fg-3)", fontSize: 13 }}>
            {t("console.keys.loading")}
          </span>
        </div>
      )}
      {!loading && loadErr && (
        <div
          className="card"
          style={{ padding: 18, color: "var(--err)", fontSize: 13 }}
        >
          {t("console.keys.loadFailed")}: {loadErr}{" "}
          <button
            className="btn btn-sm"
            style={{ marginLeft: 12 }}
            onClick={reloadKeys}
          >
            <XIcons.IconRefresh size={11} /> {t("common.retry")}
          </button>
        </div>
      )}
      {!loading && !loadErr && keys.length === 0 && (
        <div className="card" style={{ padding: 32, textAlign: "center" }}>
          <p style={{ color: "var(--fg-3)", fontSize: 13, marginBottom: 16 }}>
            {t("console.keys.empty")}
          </p>
          {canManageKeys ? (
            <button
              className="btn btn-primary"
              onClick={() => setModal("create")}
            >
              <XIcons.IconPlus size={13} /> {t("console.keys.createFirst")}
            </button>
          ) : (
            <button
              className="btn btn-primary"
              onClick={() => navConsoleRoot("bind")}
            >
              <XIcons.IconTwitter size={13} /> {t("console.keys.verifyCreate")}
            </button>
          )}
        </div>
      )}

      <div
        className="card keys-table-wrap"
        style={{ display: loading || keys.length === 0 ? "none" : undefined }}
      >
        <table className="table keys-table">
          <thead>
            <tr>
              <th>{t("console.keys.col.name")}</th>
              <th>{t("console.keys.col.key")}</th>
              <th>{t("console.keys.col.connect")}</th>
              <th>{t("console.keys.col.limit")}</th>
              <th>{t("console.keys.col.ipAllowlist")}</th>
              <th style={{ textAlign: "center", width: 130 }}>{t("console.keys.permissions.title")}</th>
              <th>{t("console.keys.col.lastUsed")}</th>
              <th style={{ width: 40 }}></th>
            </tr>
          </thead>
          <tbody>
            {keys.map((k) => {
              const bound = bindingsFor(k.id);
              const avail = availableFor(k.id);
              return (
                <tr key={k.id} className="keys-row">
                  <td>
                    <div className="row gap-2" style={{ alignItems: "center" }}>
                      <div
                        style={{
                          width: 6,
                          height: 6,
                          borderRadius: "50%",
                          background: k.sandbox ? "var(--warn)" : "var(--ok)",
                        }}
                      />
                      <span>{k.name}</span>
                    </div>
                  </td>
                  <td>
                    <span
                      className="mono"
                      style={{ fontSize: 12, color: "var(--fg-2)" }}
                    >
                      {k.preview}
                    </span>
                  </td>
                  <td>
                    {/* OAuthCell mirror — xapi-new ConsolePage.tsx:880. Legacy keys
                        get a single dashed "Enable OAuth" CTA; new keys show their
                        bindings as avatar chips and a "+" Connect entry. */}
                    <InlineOAuthCell
                      k={k}
                      bound={bound}
                      avail={avail}
                      onEnable={() => setConnectDlg({ keyId: k.id })}
                      onAdd={() => setConnectDlg({ keyId: k.id })}
                      onClickBinding={(p) => {
                        // The `p` from bindingsFor is a minimal shape (no
                        // scopeDefinitions / defaultScopes) — look up the full
                        // provider record so the dialog can render the
                        // Advanced / scope checkbox UI on re-authorize.
                        const full = providers.find(
                          (pp) => pp && pp.id === p.id,
                        );
                        setConnectDlg({ keyId: k.id, provider: full || p });
                      }}
                      onRevoke={(p, b) =>
                        setRevokeDlg({
                          keyId: k.id,
                          providerId: p.id,
                          providerName: p.name,
                          bindingId: b.id,
                        })
                      }
                      t={t}
                    />
                  </td>
                  <td>
                    <InlineLimitCell
                      k={k}
                      onSetLimit={(v) => remoteSetLimit(k, v)}
                      t={t}
                    />
                  </td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-sm btn-ghost"
                      onClick={() => setIpKeyId(k.id)}
                      title={t("console.keys.ip.desc")}
                    >
                      <XIcons.IconShield size={11} />
                      {k.allowedCidrs.length
                        ? t("console.keys.ip.entries", { n: k.allowedCidrs.length })
                        : t("console.keys.ip.unrestricted")}
                    </button>
                  </td>
                  <td style={{ textAlign: "center" }}>
                    <PermSummaryButton
                      k={k}
                      onClick={() => setPermsKeyId(k.id)}
                    />
                  </td>
                  <td style={{ color: "var(--fg-3)", fontSize: 12 }}>
                    {formatKeyLastUsed(k._raw?.lastUsedAt, t)}
                  </td>
                  <td>
                    <button
                      className="btn btn-sm btn-ghost btn-icon"
                      title={t("console.keys.delete")}
                      onClick={() => remoteDelete(k)}
                    >
                      <XIcons.IconTrash size={12} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile cards — tap a card to open the bottom drawer with bindings. */}
      <div className="keys-cards">
        {keys.map((k) => {
          const bound = bindingsFor(k.id);
          return (
            <div key={k.id} className="card" style={{ padding: 14 }}>
              <div className="row between" style={{ marginBottom: 10 }}>
                <div className="row gap-2">
                  <div
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      background: k.sandbox ? "var(--warn)" : "var(--ok)",
                    }}
                  />
                  <span style={{ fontSize: 14, fontWeight: 500 }}>
                    {k.name}
                  </span>
                </div>
                <button
                  className="btn btn-sm btn-ghost btn-icon"
                  title={t("console.keys.delete")}
                  onClick={() => remoteDelete(k)}
                >
                  <XIcons.IconTrash size={12} />
                </button>
              </div>
              <div className="row gap-2" style={{ marginBottom: 10 }}>
                <span
                  className="mono"
                  style={{
                    fontSize: 12,
                    color: "var(--fg-2)",
                    flex: 1,
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {k.preview}
                </span>
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 10,
                  fontSize: 11,
                  color: "var(--fg-3)",
                }}
              >
                <div>
                  <div className="micro">{t("console.keys.usage")}</div>
                  <div
                    className="mono"
                    style={{ color: "var(--fg-1)", fontSize: 13, marginTop: 2 }}
                  >
                    {k.usage.toLocaleString()}
                  </div>
                </div>
                <div>
                  <div className="micro">{t("console.keys.col.lastUsed")}</div>
                  <div
                    style={{ color: "var(--fg-1)", fontSize: 13, marginTop: 2 }}
                  >
                    {formatKeyLastUsed(k._raw?.lastUsedAt, t)}
                  </div>
                </div>
              </div>
              {k.limit != null && (
                <div style={{ marginTop: 10 }}>
                  <div className="row between" style={{ fontSize: 11 }}>
                    <span className="mono">${k.used.toFixed(2)}</span>
                    <span className="mono" style={{ color: "var(--fg-4)" }}>
                      {t("console.keys.limit.of", { amount: k.limit })}
                    </span>
                  </div>
                  <div className="bar-track" style={{ marginTop: 4 }}>
                    <div
                      className="bar-fill"
                      style={{ width: (k.used / k.limit) * 100 + "%" }}
                    />
                  </div>
                </div>
              )}
              <button
                onClick={() => setDrawerKeyId(k.id)}
                className="btn btn-sm btn-ghost"
                style={{
                  marginTop: 12,
                  width: "100%",
                  justifyContent: "space-between",
                }}
              >
                <span className="row gap-2">
                  <XIcons.IconShield size={11} />
                  {k.oauth
                    ? t("console.keys.oauth.manageWithBindings", {
                        n: bound.length,
                      })
                    : t("console.keys.manage")}
                </span>
                <XIcons.IconChevronRight size={11} />
              </button>
            </div>
          );
        })}
      </div>

      {/* Mobile bottom drawer — bindings + add-provider for the selected key.
          Portal'd so the position-fixed sheet escapes any backdrop-filter
          ancestor and pins to the viewport. */}
      {drawerKeyId &&
        (() => {
          const k = keys.find((x) => x.id === drawerKeyId);
          if (!k) return null;
          const bound = bindingsFor(k.id);
          const avail = availableFor(k.id);
          return createPortal(
            <>
              <div
                className="mobile-sheet-backdrop"
                onClick={() => setDrawerKeyId(null)}
              />
              <div
                className="market-filter-sheet keys-detail-sheet"
                role="dialog"
                aria-label={`${t("console.keys.oauth.title")} · ${k.name}`}
                onClick={(e) => e.stopPropagation()}
              >
                <div
                  className="row between"
                  style={{ alignItems: "center", marginBottom: 14 }}
                >
                  <div>
                    <div className="micro">{t("console.keys.col.key")}</div>
                    <div
                      className="row gap-2"
                      style={{ alignItems: "center", marginTop: 2 }}
                    >
                      <div
                        style={{
                          width: 6,
                          height: 6,
                          borderRadius: "50%",
                          background: k.sandbox ? "var(--warn)" : "var(--ok)",
                        }}
                      />
                      <span style={{ fontSize: 15, fontWeight: 500 }}>
                        {k.name}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => setDrawerKeyId(null)}
                    className="btn btn-sm btn-ghost btn-icon"
                    aria-label="Close"
                  >
                    <XIcons.IconX size={12} />
                  </button>
                </div>

                {/* Key settings — same compact list as desktop. */}
                <div className="micro" style={{ marginBottom: 8 }}>
                  {t("console.keys.settings.title")}
                </div>
                <div style={{ marginBottom: 16 }}>
                  <KeySettingsList
                    k={k}
                    onOpenPerms={() => setPermsKeyId(k.id)}
                    onOpenIpAllowlist={() => setIpKeyId(k.id)}
                    onSetLimit={(v) => remoteSetLimit(k, v)}
                  />
                </div>

                {!k.oauth ? (
                  <div style={{ padding: "12px 0" }}>
                    <div className="micro" style={{ marginBottom: 8 }}>
                      {t("console.keys.oauth.title")}
                    </div>
                    <button
                      className="btn"
                      onClick={() => setConnectDlg({ keyId: k.id })}
                      style={{
                        width: "100%",
                        justifyContent: "center",
                        borderStyle: "dashed",
                        color: "var(--fg-3)",
                      }}
                    >
                      <XIcons.IconKey size={12} />{" "}
                      {t("console.keys.oauth.enableCta")}
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="micro" style={{ marginBottom: 8 }}>
                      {t("console.keys.oauth.title")} · {bound.length}
                    </div>
                    {bound.length > 0 ? (
                      <div className="col gap-2">
                        {bound.map(({ provider: p, binding: b }) => (
                          <div
                            key={p.id}
                            style={{
                              padding: 10,
                              background: "var(--bg-1)",
                              borderRadius: 6,
                              border: "1px solid var(--line)",
                            }}
                          >
                            <div
                              className="row between"
                              style={{ alignItems: "center" }}
                            >
                              <div
                                className="row gap-2"
                                style={{
                                  alignItems: "center",
                                  minWidth: 0,
                                  flex: 1,
                                }}
                              >
                                <BoundAvatar
                                  provider={p}
                                  binding={b}
                                  size={32}
                                />
                                <div style={{ minWidth: 0, flex: 1 }}>
                                  <div
                                    style={{
                                      fontSize: 13,
                                      fontWeight: 500,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {b.account}
                                  </div>
                                  <div
                                    className="micro-mono"
                                    style={{
                                      textTransform: "none",
                                      letterSpacing: 0,
                                      color: "var(--fg-4)",
                                      fontSize: 10.5,
                                      marginTop: 2,
                                    }}
                                  >
                                    {t("console.keys.oauth.via", { provider: p.name })}
                                  </div>
                                </div>
                              </div>
                              <button
                                className="btn btn-sm btn-ghost btn-icon"
                                aria-label={t("console.oauth.revoke")}
                                onClick={() =>
                                  setRevokeDlg({
                                    keyId: k.id,
                                    providerId: p.id,
                                    providerName: p.name,
                                    bindingId: b.id,
                                  })
                                }
                              >
                                <XIcons.IconTrash size={11} />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div
                        className="micro-mono"
                        style={{
                          textTransform: "none",
                          letterSpacing: 0,
                          color: "var(--fg-4)",
                          padding: "12px 0",
                        }}
                      >
                        {t("console.keys.oauth.empty")}
                      </div>
                    )}

                    {avail.length > 0 && (
                      <div style={{ marginTop: 18 }}>
                        <div className="micro" style={{ marginBottom: 8 }}>
                          {t("console.keys.oauth.addProvider")}
                        </div>
                        <div className="col gap-1">
                          {avail.map((p) => (
                            <button
                              key={p.id}
                              onClick={() =>
                                setConnectDlg({ keyId: k.id, provider: p })
                              }
                              className="btn btn-sm btn-ghost"
                              style={{ justifyContent: "space-between" }}
                            >
                              <span className="row gap-2">
                                <span
                                  style={{
                                    color: "var(--fg-3)",
                                    display: "inline-flex",
                                    alignItems: "center",
                                  }}
                                >
                                  <ProviderGlyph provider={p} size={12} />
                                </span>
                                <span>{p.name}</span>
                              </span>
                              <XIcons.IconPlus size={11} />
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}

                <div
                  style={{
                    marginTop: 22,
                    position: "sticky",
                    bottom: 0,
                    paddingTop: 14,
                    background: "var(--bg-0)",
                  }}
                >
                  <button
                    className="btn btn-primary"
                    onClick={() => setDrawerKeyId(null)}
                    style={{
                      width: "100%",
                      justifyContent: "center",
                      height: 40,
                    }}
                  >
                    {t("console.keys.done")}
                  </button>
                </div>
              </div>
            </>,
            document.body,
          );
        })()}

      {modal === "create" && (
        <CreateKeyModal
          onClose={() => setModal(null)}
          onCreated={async (name) => {
            try {
              const created = await XApi.keyService.create(name);
              // backend returns { id, apiKeyId, name, key, createdAt } — `key` is plaintext, shown ONCE.
              const value = created && created.key;
              // Stash plaintext so a same-session OAuth bind can auto-enable without
              // asking the user to paste it again (xapi-new parity).
              if (value) XApi.userKeyStash.set(value);
              setModal({ kind: "reveal", name: created.name || name, value });
              toast(t("console.keys.created"));
              reloadKeys();
            } catch (e) {
              toast(e.message || t("console.keys.createFailed"));
            }
          }}
        />
      )}
      {modal && modal.kind === "reveal" && (
        <RevealKeyModal
          name={modal.name}
          value={modal.value}
          onClose={() => setModal(null)}
        />
      )}

      <Dialog
        open={!!connectDlg}
        onClose={() => !acting && setConnectDlg(null)}
        maxWidth={500}
        responsive
      >
        {connectDlg && (
          <ConnectOAuthDialogBody
            connectDlg={connectDlg}
            keys={keys}
            providers={providers}
            bindings={bindings}
            acting={acting}
            onCancel={() => setConnectDlg(null)}
            onConfirm={doConnect}
            onKeyEnabled={reloadKeys}
            onRevoke={(binding) => {
              const p =
                providers.find((pp) => pp.id === binding.providerId) || {};
              setConnectDlg(null);
              setRevokeDlg({
                keyId: connectDlg.keyId,
                providerId: binding.providerId,
                providerName: p.name || binding.providerId,
                bindingId: binding.id,
              });
            }}
            t={t}
          />
        )}
      </Dialog>

      <KeyPermissionsModal
        k={keys.find((x) => x.id === permsKeyId) || null}
        onClose={() => setPermsKeyId(null)}
        canUseXntCloud={canUseXntCloud}
        pending={
          scopePendingKeyIds.has(permsKeyId) ||
          workerAccessPendingKeyId === permsKeyId
        }
        workerOptions={workerOptions}
        onWorkerAccessChange={(mode, workerIds) => {
          const pk = keys.find((x) => x.id === permsKeyId);
          if (pk) remoteSetWorkerAccess(pk, mode, workerIds);
        }}
        onTogglePerm={(id) => {
          const pk = keys.find((x) => x.id === permsKeyId);
          if (!pk) return;
          if (id === "sandbox") remoteToggleSandbox(pk);
          else remoteToggleScope(pk, id);
        }}
      />

      <KeyIpAllowlistModal
        k={keys.find((x) => x.id === ipKeyId) || null}
        pending={ipPending}
        onClose={() => !ipPending && setIpKeyId(null)}
        onSave={(allowedCidrs) => {
          const key = keys.find((x) => x.id === ipKeyId);
          if (key) remoteSetIpAllowlist(key, allowedCidrs);
        }}
      />

      <Dialog
        open={!!revokeDlg}
        onClose={() => !acting && setRevokeDlg(null)}
        maxWidth={420}
      >
        {revokeDlg && (
          <DialogBody padding={20}>
            <div style={{ fontSize: 15, fontWeight: 500, marginBottom: 8 }}>
              {t("console.keys.oauth.revokeTitle", { provider: revokeDlg.providerName })}
            </div>
            <p
              style={{
                fontSize: 12.5,
                color: "var(--fg-3)",
                lineHeight: 1.7,
                margin: 0,
              }}
            >
              {t("console.keys.oauth.revokeDesc", { provider: revokeDlg.providerName })}
            </p>
            <div className="row gap-2" style={{ marginTop: 18 }}>
              <button
                className="btn btn-ghost grow"
                style={{ justifyContent: "center" }}
                onClick={() => setRevokeDlg(null)}
                disabled={acting}
              >
                {t("common.cancel")}
              </button>
              <button
                className="btn grow"
                style={{
                  justifyContent: "center",
                  borderColor: "var(--err)",
                  color: "var(--err)",
                }}
                onClick={doRevoke}
                disabled={acting}
              >
                {acting ? (
                  <>
                    <div className="spinner" /> {t("console.keys.oauth.revoking")}
                  </>
                ) : (
                  <>
                    <XIcons.IconTrash size={12} /> {t("console.oauth.revoke")}
                  </>
                )}
              </button>
            </div>
          </DialogBody>
        )}
      </Dialog>
    </>
  );
};

// Connect-OAuth dialog body — drives scope selection from the provider's
// `scopeDefinitions` field. Two-section split: required (Basic, pre-checked
// and locked) vs optional (Advanced, user-toggleable). Falls back to a plain
// confirm if the provider has no scopeDefinitions (older providers, or types
// that don't use scoped OAuth).
//
// Ported from xapi-new/src/pages/Console/ConsolePage.tsx:OAuthDialog. Keeping
// state local so it resets every time the dialog opens for a different
// provider.
const ConnectOAuthDialogBody = ({
  connectDlg,
  keys,
  providers,
  bindings,
  acting,
  onCancel,
  onConfirm,
  onKeyEnabled,
  onRevoke,
  t,
}) => {
  const currentKey = keys.find((x) => x.id === connectDlg.keyId);
  // ── Provider selection step (xapi-new ConsolePage.tsx:1349-1448) ──
  // If the caller pre-selected a provider (clicking a provider tile in the
  // keys list) we skip straight to scopes. If they came through the "Enable
  // OAuth" CTA (no preselection), we render a provider list first.
  const [pickedId, setPickedId] = React.useState(
    connectDlg.provider?.id || null,
  );
  const provider =
    connectDlg.provider ||
    (pickedId ? providers.find((p) => p.id === pickedId) : null) ||
    null;
  const boundProviderIds = React.useMemo(
    () =>
      new Set(
        (bindings || [])
          .filter((b) => b.apiKeyId === connectDlg.keyId)
          .map((b) => b.providerId),
      ),
    [bindings, connectDlg.keyId],
  );
  const availableProviders = (providers || []).filter(
    (p) => !boundProviderIds.has(p.id),
  );
  // ── Enable-OAuth pre-step (legacy keys with no oauthPublicKey on file) ──
  // xapi-new pattern: auto-fire keyService.enableOAuth using the stashed
  // plaintext from sessionStorage (set on login/quickStart/key-create).
  // If stash is empty, show a small manual-input fallback so the user can
  // paste the plaintext once. After success the parent reloadKeys lets us
  // fall through to the normal scope-selection flow.
  const needsEnable = currentKey && !currentKey.oauth;
  const [enableState, setEnableState] = React.useState(
    needsEnable ? "pending" : "ready", // 'pending' | 'enabling' | 'manual' | 'ready'
  );
  const [enableError, setEnableError] = React.useState(null);
  const [manualKey, setManualKey] = React.useState("");

  // Re-evaluate when keys prop refreshes (e.g. after parent reloadKeys post-enable).
  React.useEffect(() => {
    if (!needsEnable && enableState !== "ready") setEnableState("ready");
  }, [needsEnable]);

  // On first mount with a legacy key: try stash auto-enable.
  React.useEffect(() => {
    if (enableState !== "pending") return;
    const stash = XApi.userKeyStash.get();
    if (!stash) {
      setEnableState("manual");
      return;
    }
    setEnableState("enabling");
    setEnableError(null);
    XApi.keyService
      .enableOAuth(connectDlg.keyId, stash)
      .then(() => {
        onKeyEnabled &&
          onKeyEnabled(); /* parent will refresh `keys`, flipping needsEnable */
      })
      .catch((e) => {
        // Stash was wrong / stale — discard it and prompt for a fresh paste.
        XApi.userKeyStash.clear();
        setEnableError(e.message || t("console.keys.oauth.enableFail"));
        setEnableState("manual");
      });
  }, [enableState, connectDlg.keyId]);

  const submitManualEnable = () => {
    const k = manualKey.trim();
    if (k.length < 8) return;
    setEnableState("enabling");
    setEnableError(null);
    XApi.keyService
      .enableOAuth(connectDlg.keyId, k)
      .then(() => {
        // Stash the verified plaintext so subsequent provider binds on this
        // session skip this step entirely.
        XApi.userKeyStash.set(k);
        setManualKey("");
        onKeyEnabled && onKeyEnabled();
      })
      .catch((e) => {
        setEnableError(e.message || t("console.keys.oauth.enableFail"));
        setEnableState("manual");
      });
  };

  // While enabling, render a compact pre-step UI inside the same dialog
  // shell — same header style so the user perceives one continuous flow.
  if (enableState !== "ready") {
    const isBusy = enableState === "enabling";
    return (
      <>
        <DialogHeader
          title={
            provider
              ? t("console.oauth.dialog.title", { provider: provider.name })
              : t("console.keys.oauth.enableTitle")
          }
          onClose={!isBusy ? onCancel : null}
        />
        <DialogBody padding={20}>
          <div
            className="micro-mono"
            style={{
              textTransform: "none",
              letterSpacing: 0,
              marginTop: -10,
              marginBottom: 14,
            }}
          >
            {t("console.oauth.dialog.toKey")} ·{" "}
            <span className="mono" style={{ color: "var(--fg-2)" }}>
              {currentKey?.name || "—"}
            </span>
          </div>
          <div
            className="card"
            style={{ padding: 13, background: "var(--bg-2)", marginBottom: 14 }}
          >
            <div className="row gap-3" style={{ alignItems: "flex-start" }}>
              <XIcons.IconKey
                size={13}
                style={{ color: "var(--fg-3)", marginTop: 2 }}
              />
              <div
                style={{
                  fontSize: 12.5,
                  lineHeight: 1.7,
                  color: "var(--fg-2)",
                }}
              >
                {t("console.keys.oauth.enableNote")}
              </div>
            </div>
          </div>

          {isBusy ? (
            <div
              className="row gap-2"
              style={{
                padding: "14px 0",
                justifyContent: "center",
                alignItems: "center",
                color: "var(--fg-3)",
                fontSize: 13,
              }}
            >
              <div className="spinner" /> {t("console.keys.oauth.enabling")}
            </div>
          ) : (
            <>
              <label className="field-label">
                {t("console.keys.oauth.enablePlaintextLabel")}
              </label>
              <input
                type="password"
                autoFocus
                autoComplete="off"
                className="input mono"
                placeholder="sk-…"
                value={manualKey}
                onChange={(e) => {
                  setManualKey(e.target.value);
                  if (enableError) setEnableError(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") submitManualEnable();
                }}
                style={{ width: "100%", fontSize: 13 }}
              />
              <div
                className="micro-mono"
                style={{
                  textTransform: "none",
                  letterSpacing: 0,
                  color: "var(--fg-4)",
                  marginTop: 6,
                  fontSize: 11,
                }}
              >
                {t("console.keys.oauth.enablePlaintextHint")}
              </div>
              {enableError && (
                <div
                  style={{ color: "var(--err)", fontSize: 12, marginTop: 8 }}
                >
                  {enableError}
                </div>
              )}
            </>
          )}
        </DialogBody>
        <DialogFooter>
          <button
            className="btn btn-ghost grow"
            style={{ justifyContent: "center" }}
            onClick={onCancel}
            disabled={isBusy}
          >
            {t("console.oauth.dialog.cancel")}
          </button>
          <button
            className="btn btn-primary grow"
            style={{ justifyContent: "center" }}
            onClick={submitManualEnable}
            disabled={isBusy || manualKey.trim().length < 8}
          >
            <XIcons.IconKey size={12} /> {t("console.keys.oauth.enableConfirm")}
          </button>
        </DialogFooter>
      </>
    );
  }

  // No provider yet — render provider-selection step (xapi-new ConsolePage.tsx:1349+).
  // Filters out already-bound providers so the user can't double-bind.
  if (!provider) {
    return (
      <>
        <DialogHeader
          title={t("console.oauth.dialog.pickProviderTitle")}
          onClose={!acting ? onCancel : null}
        />
        <DialogBody padding={20}>
          <div
            className="micro-mono"
            style={{
              textTransform: "none",
              letterSpacing: 0,
              marginTop: -10,
              marginBottom: 14,
            }}
          >
            {t("console.oauth.dialog.toKey")} ·{" "}
            <span className="mono" style={{ color: "var(--fg-2)" }}>
              {currentKey?.name || "—"}
            </span>
          </div>
          {availableProviders.length === 0 ? (
            <div
              className="micro-mono"
              style={{
                textTransform: "none",
                letterSpacing: 0,
                color: "var(--fg-4)",
                padding: "20px 0",
                textAlign: "center",
              }}
            >
              {t("console.oauth.dialog.noAvailableProviders")}
            </div>
          ) : (
            <div className="col gap-2">
              {availableProviders.map((p) => (
                <button
                  key={p.id}
                  className="card card-h"
                  onClick={() => setPickedId(p.id)}
                  style={{
                    padding: 12,
                    textAlign: "left",
                    cursor: "pointer",
                    color: "inherit",
                  }}
                >
                  <div className="row gap-3" style={{ alignItems: "center" }}>
                    <div
                      style={{
                        width: 32,
                        height: 32,
                        background: "var(--bg-3)",
                        borderRadius: 6,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        border: "1px solid var(--line-2)",
                        flexShrink: 0,
                      }}
                    >
                      <ProviderGlyph provider={p} size={14} />
                    </div>
                    <div className="grow" style={{ minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: 13.5,
                          color: "var(--fg-1)",
                          fontWeight: 500,
                        }}
                      >
                        {p.name}
                      </div>
                      {p.description && (
                        <div
                          className="micro-mono"
                          style={{
                            textTransform: "none",
                            letterSpacing: 0,
                            color: "var(--fg-4)",
                            marginTop: 2,
                            fontSize: 11,
                          }}
                        >
                          {p.description}
                        </div>
                      )}
                    </div>
                    <XIcons.IconChevronRight
                      size={12}
                      style={{ color: "var(--fg-4)", flexShrink: 0 }}
                    />
                  </div>
                </button>
              ))}
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <button
            className="btn btn-ghost grow"
            style={{ justifyContent: "center" }}
            onClick={onCancel}
            disabled={acting}
          >
            {t("console.oauth.dialog.cancel")}
          </button>
        </DialogFooter>
      </>
    );
  }

  // Provider chosen — defer to the scope-selection step (separate component so
  // hook order stays stable when transitioning between steps).
  const existingBinding = (bindings || []).find(
    (b) => b && b.apiKeyId === connectDlg.keyId && b.providerId === provider.id,
  );
  return (
    <ScopeSelectionStep
      provider={provider}
      currentKey={currentKey}
      existingBinding={existingBinding}
      acting={acting}
      onCancel={onCancel}
      onBackToProviders={!connectDlg.provider ? () => setPickedId(null) : null}
      onConfirm={onConfirm}
      onRevoke={onRevoke}
      t={t}
    />
  );
};

// Scope step — extracted from ConnectOAuthDialogBody so hooks order stays stable
// across the enable → provider → scope state transitions.
const ScopeSelectionStep = ({
  provider,
  currentKey,
  existingBinding,
  acting,
  onCancel,
  onBackToProviders,
  onConfirm,
  onRevoke,
  t,
}) => {
  // When a binding already exists for this (key, provider) the default view is
  // a read-only "Already connected" summary with a Revoke button. User can
  // explicitly switch to "Modify scopes" mode to re-authorize with a different
  // scope set.
  const [editMode, setEditMode] = React.useState(false);
  const isBound = !!existingBinding && !editMode;
  const rawDefs = Array.isArray(provider.scopeDefinitions)
    ? provider.scopeDefinitions
    : [];
  // If provider hasn't been seeded with scopeDefinitions, fall back to splitting
  // defaultScopes — synthesize one definition per scope with required=false so
  // every scope is at least toggleable (better than a read-only string blob).
  const synthDefs = React.useMemo(() => {
    if (rawDefs.length > 0) return null;
    const defaultStr =
      typeof provider.defaultScopes === "string"
        ? provider.defaultScopes
        : provider.scopes || "";
    return defaultStr
      .split(/[\s,]+/)
      .map((s) => s.trim())
      .filter(Boolean)
      .map((s) => ({
        scope: s,
        label: s,
        description: "",
        required: false,
        category: "",
      }));
  }, [rawDefs.length, provider.id, provider.defaultScopes, provider.scopes]);
  const defs = rawDefs.length > 0 ? rawDefs : synthDefs || [];
  const required = defs.filter((d) => d.required);
  const optional = defs.filter((d) => !d.required);

  const synthFallback = rawDefs.length === 0;
  // Pre-existing scopes from the binding, when present — used to pre-check
  // them in edit mode so the user sees what they already granted.
  const existingScopes = React.useMemo(
    () => new Set((existingBinding?.scopes || "").split(/\s+/).filter(Boolean)),
    [existingBinding?.scopes],
  );
  const initial = React.useMemo(() => {
    const s = synthFallback
      ? new Set(defs.map((d) => d.scope))
      : new Set(required.map((d) => d.scope));
    for (const x of existingScopes) s.add(x);
    return s;
  }, [provider.id, defs.length, synthFallback, existingScopes]);
  const [selected, setSelected] = React.useState(initial);
  React.useEffect(() => {
    setSelected(initial);
  }, [initial]);

  const toggle = (scope, isRequired) => {
    if (isRequired) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(scope)) next.delete(scope);
      else next.add(scope);
      return next;
    });
  };

  const allOptionalSelected =
    optional.length > 0 && optional.every((d) => selected.has(d.scope));
  const toggleAllOptional = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allOptionalSelected) {
        for (const d of optional) next.delete(d.scope);
      } else {
        for (const d of optional) next.add(d.scope);
      }
      return next;
    });
  };

  const hasDefs = defs.length > 0;
  const submit = () => {
    // Pass provider.id up — parent's doConnect uses it instead of (possibly
    // stale) connectDlg.provider, which is undefined on the "Enable OAuth"
    // path where the user picked the provider inside the dialog.
    if (!hasDefs) {
      onConfirm(undefined, provider.id);
      return;
    }
    const scopeStr = [...selected].join(" ");
    onConfirm(scopeStr || undefined, provider.id);
  };

  // ───── Read-only "Already connected" view ──────────────────────────────
  // Shown when this (key, provider) pair already has a binding AND the user
  // hasn't explicitly entered edit mode. Lists the granted scopes (greyed-out
  // checkboxes) with Revoke + "Modify scopes" actions.
  if (isBound) {
    const grantedDefs = defs.filter((d) => existingScopes.has(d.scope));
    const ungrantedFromDefs = defs.filter((d) => !existingScopes.has(d.scope));
    return (
      <>
        <DialogHeader
          title={t("console.oauth.dialog.alreadyConnectedTitle", {
            provider: provider.name,
          })}
          onClose={!acting ? onCancel : null}
        />
        <DialogBody padding={20}>
          {/* Show bound account front-and-center */}
          <div
            className="card"
            style={{ padding: 14, background: "var(--bg-2)", marginBottom: 14 }}
          >
            <div className="row gap-3" style={{ alignItems: "center" }}>
              <BoundAvatar
                provider={provider}
                binding={{ avatar: existingBinding.providerAccountAvatarUrl }}
                size={36}
              />
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 500 }}>
                  {existingBinding.providerAccountName ||
                    existingBinding.providerAccountId ||
                    "—"}
                </div>
                <div
                  className="micro-mono"
                  style={{
                    textTransform: "none",
                    letterSpacing: 0,
                    color: "var(--fg-4)",
                    marginTop: 2,
                    fontSize: 11,
                  }}
                >
                  {t("console.keys.oauth.via", { provider: provider.name })}
                </div>
              </div>
              <span
                className="tag"
                style={{ color: "var(--ok)", fontSize: 10 }}
              >
                <XIcons.IconCheck size={9} /> {t("console.oauth.dialog.bound")}
              </span>
            </div>
          </div>

          {grantedDefs.length > 0 ? (
            <>
              <div className="field-label" style={{ marginBottom: 8 }}>
                {t("console.oauth.dialog.grantedScopes")} · {grantedDefs.length}
              </div>
              <div className="col gap-1" style={{ marginBottom: 14 }}>
                {grantedDefs.map((d) => (
                  <ScopeRow
                    key={d.scope}
                    def={d}
                    checked={true}
                    locked
                    onToggle={() => {}}
                  />
                ))}
              </div>
            </>
          ) : (
            <div
              className="micro-mono"
              style={{
                textTransform: "none",
                letterSpacing: 0,
                color: "var(--fg-4)",
                padding: "10px 0",
              }}
            >
              {[...existingScopes].join(", ") || "—"}
            </div>
          )}

          {ungrantedFromDefs.length > 0 && (
            <div
              className="micro-mono"
              style={{
                textTransform: "none",
                letterSpacing: 0,
                color: "var(--fg-4)",
                fontSize: 11,
                marginTop: 4,
              }}
            >
              {t("console.oauth.dialog.modifyHint", {
                n: ungrantedFromDefs.length,
              })}
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <button
            className="btn"
            style={{
              justifyContent: "center",
              borderColor: "var(--err)",
              color: "var(--err)",
            }}
            onClick={() => onRevoke && onRevoke(existingBinding)}
            disabled={acting}
          >
            <XIcons.IconTrash size={12} /> {t("console.oauth.revoke")}
          </button>
          <button
            className="btn btn-ghost grow"
            style={{ justifyContent: "center" }}
            onClick={onCancel}
            disabled={acting}
          >
            {t("console.oauth.dialog.close")}
          </button>
          <button
            className="btn btn-primary grow"
            style={{ justifyContent: "center" }}
            onClick={() => setEditMode(true)}
            disabled={acting}
          >
            <XIcons.IconEdit size={12} />{" "}
            {t("console.oauth.dialog.modifyScopes")}
          </button>
        </DialogFooter>
      </>
    );
  }

  // ───── Edit / fresh-bind view ──────────────────────────────────────────
  return (
    <>
      <DialogHeader
        title={t("console.oauth.dialog.title", { provider: provider.name })}
        onClose={!acting ? onCancel : null}
      />
      <DialogBody padding={20}>
        <div
          className="micro-mono"
          style={{
            textTransform: "none",
            letterSpacing: 0,
            marginTop: -10,
            marginBottom: 14,
          }}
        >
          {t("console.oauth.dialog.toKey")} ·{" "}
          <span className="mono" style={{ color: "var(--fg-2)" }}>
            {currentKey?.name || "—"}
          </span>
        </div>
        <div
          className="card"
          style={{ padding: 13, background: "var(--bg-2)", marginBottom: 14 }}
        >
          <div className="row gap-3" style={{ alignItems: "flex-start" }}>
            <XIcons.IconShield
              size={13}
              style={{ color: "var(--fg-3)", marginTop: 2 }}
            />
            <div
              style={{ fontSize: 12.5, lineHeight: 1.7, color: "var(--fg-2)" }}
            >
              {t("console.oauth.dialog.consentNote", {
                provider: provider.name,
              })}
            </div>
          </div>
        </div>

        {!hasDefs ? (
          <div
            className="micro-mono"
            style={{
              textTransform: "none",
              letterSpacing: 0,
              color: "var(--fg-4)",
              padding: "10px 0",
            }}
          >
            {t("console.oauth.dialog.requestedScopes")}: —
          </div>
        ) : (
          <>
            {required.length > 0 && (
              <>
                <div className="field-label" style={{ marginBottom: 8 }}>
                  {t("console.oauth.dialog.basicTitle")}
                  <span
                    style={{
                      marginLeft: 6,
                      color: "var(--fg-4)",
                      fontWeight: 400,
                    }}
                  >
                    · {t("console.oauth.dialog.requiredHint")}
                  </span>
                </div>
                <div className="col gap-1" style={{ marginBottom: 14 }}>
                  {required.map((d) => (
                    <ScopeRow
                      key={d.scope}
                      def={d}
                      checked={true}
                      locked
                      onToggle={() => {}}
                    />
                  ))}
                </div>
              </>
            )}
            {optional.length > 0 && (
              <>
                <div
                  className="row between"
                  style={{ marginBottom: 8, alignItems: "center" }}
                >
                  <div className="field-label" style={{ margin: 0 }}>
                    {synthFallback ? (
                      t("console.oauth.dialog.scopesTitle")
                    ) : (
                      <>
                        {t("console.oauth.dialog.advancedTitle")}
                        <span
                          style={{
                            marginLeft: 6,
                            color: "var(--fg-4)",
                            fontWeight: 400,
                          }}
                        >
                          · {t("console.oauth.dialog.optionalHint")}
                        </span>
                      </>
                    )}
                  </div>
                  <button
                    className="btn btn-sm btn-ghost"
                    onClick={toggleAllOptional}
                    disabled={acting}
                  >
                    {allOptionalSelected
                      ? t("console.oauth.dialog.deselectAll")
                      : t("console.oauth.dialog.selectAll")}
                  </button>
                </div>
                <div className="col gap-1">
                  {optional.map((d) => (
                    <ScopeRow
                      key={d.scope}
                      def={d}
                      checked={selected.has(d.scope)}
                      onToggle={() => toggle(d.scope, false)}
                    />
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </DialogBody>
      <DialogFooter>
        {onBackToProviders ? (
          <button
            className="btn btn-ghost"
            style={{ justifyContent: "center" }}
            onClick={onBackToProviders}
            disabled={acting}
          >
            <XIcons.IconArrowLeft size={12} /> {t("console.oauth.dialog.back")}
          </button>
        ) : existingBinding ? (
          <button
            className="btn btn-ghost"
            style={{ justifyContent: "center" }}
            onClick={() => setEditMode(false)}
            disabled={acting}
          >
            <XIcons.IconArrowLeft size={12} /> {t("console.oauth.dialog.back")}
          </button>
        ) : (
          <button
            className="btn btn-ghost grow"
            style={{ justifyContent: "center" }}
            onClick={onCancel}
            disabled={acting}
          >
            {t("console.oauth.dialog.cancel")}
          </button>
        )}
        <button
          className="btn btn-primary grow"
          style={{ justifyContent: "center" }}
          onClick={submit}
          disabled={acting}
        >
          {acting ? (
            <>
              <div className="spinner" /> {t("console.oauth.dialog.opening")}
            </>
          ) : (
            <>
              <XIcons.IconExternal size={12} />{" "}
              {existingBinding
                ? t("console.oauth.dialog.reauthorize")
                : t("console.oauth.dialog.continue")}
            </>
          )}
        </button>
      </DialogFooter>
    </>
  );
};

const ScopeRow = ({ def, checked, locked, onToggle }) => (
  <label
    style={{
      display: "flex",
      gap: 10,
      alignItems: "flex-start",
      padding: "10px 12px",
      border: "1px solid var(--line-2)",
      borderRadius: 8,
      background: checked ? "var(--bg-2)" : "transparent",
      cursor: locked ? "default" : "pointer",
      opacity: locked ? 0.85 : 1,
    }}
  >
    <input
      type="checkbox"
      checked={checked}
      disabled={locked}
      onChange={onToggle}
      style={{ marginTop: 3, accentColor: "var(--accent, var(--fg-1))" }}
    />
    <div style={{ minWidth: 0, flex: 1 }}>
      <div className="mono" style={{ fontSize: 12.5, color: "var(--fg-1)" }}>
        {def.label || def.scope}
      </div>
      {def.description && (
        <div
          style={{
            fontSize: 11.5,
            color: "var(--fg-3)",
            marginTop: 3,
            lineHeight: 1.5,
          }}
        >
          {def.description}
        </div>
      )}
      <div
        className="micro-mono"
        style={{
          textTransform: "none",
          letterSpacing: 0,
          color: "var(--fg-4)",
          marginTop: 3,
          fontSize: 10,
        }}
      >
        {def.scope}
        {def.category ? ` · ${def.category}` : ""}
      </div>
    </div>
  </label>
);

const CreateKeyModal = ({ onClose, onCreated }) => {
  const { t } = useT();
  const [name, setName] = ucS("");
  const [err, setErr] = ucS(null);
  const submit = (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setErr("console.keys.nameRequired");
      return;
    }
    onCreated(name.trim());
  };
  return (
    <Dialog open={true} onClose={onClose} maxWidth={420}>
      <DialogHeader title={t("console.keys.createTitle")} onClose={onClose} />
      <DialogBody>
        <form onSubmit={submit}>
          <label className="field-label">{t("console.keys.col.name")}</label>
          <input
            autoFocus
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setErr(null);
            }}
            className="input"
            placeholder={t("console.keys.namePlaceholder")}
          />
          {err && (
            <div style={{ color: "var(--err)", fontSize: 12, marginTop: 6 }}>
              {t(err)}
            </div>
          )}
          <div
            className="micro-mono"
            style={{
              textTransform: "none",
              letterSpacing: 0,
              marginTop: 12,
              color: "var(--fg-4)",
              lineHeight: 1.6,
            }}
          >
            {t("console.keys.createHint")}
          </div>
          <div
            className="row gap-2"
            style={{ marginTop: 22, justifyContent: "flex-end" }}
          >
            <button type="button" className="btn" onClick={onClose}>
              {t("common.cancel")}
            </button>
            <button type="submit" className="btn btn-primary">
              {t("console.keys.create")}
            </button>
          </div>
        </form>
      </DialogBody>
    </Dialog>
  );
};

const RevealKeyModal = ({ name, value, onClose }) => {
  const { t } = useT();
  const { toast } = XPrim.useToast();
  const copy = () => {
    navigator.clipboard?.writeText(value);
    toast(t("common.copied"));
  };
  return (
    <Dialog open={true} onClose={onClose} maxWidth={520}>
      <DialogHeader title={t("console.keys.reveal.title", { name })} onClose={onClose} />
      <DialogBody>
        <div
          className="card"
          style={{
            padding: 12,
            background: "rgba(230, 168, 90, 0.08)",
            borderColor: "rgba(230, 168, 90, 0.3)",
            marginBottom: 14,
          }}
        >
          <div className="row gap-2" style={{ alignItems: "flex-start" }}>
            <XIcons.IconShield
              size={13}
              style={{ color: "var(--warn)", marginTop: 2 }}
            />
            <div
              style={{ fontSize: 12.5, lineHeight: 1.7, color: "var(--fg-2)" }}
            >
              <b style={{ color: "var(--fg-1)" }}>{t("console.keys.reveal.copyNow")}</b>{" "}
              {t("console.keys.reveal.warning")}
            </div>
          </div>
        </div>
        <div className="field-label">{t("console.keys.reveal.label")}</div>
        <div className="row gap-2">
          <div
            className="hairline mono"
            style={{
              flex: 1,
              padding: "12px 14px",
              borderRadius: 6,
              background: "var(--bg-2)",
              fontSize: 12.5,
              wordBreak: "break-all",
            }}
          >
            {value}
          </div>
          <button className="btn btn-primary" onClick={copy}>
            <XIcons.IconCopy size={13} /> {t("common.copy")}
          </button>
        </div>
        <div
          className="row gap-2"
          style={{ marginTop: 20, justifyContent: "space-between" }}
        >
          <button
            className="btn"
            onClick={() => {
              try {
                XEnv.set({
                  name: "XAPI_KEY",
                  value,
                  scope: "global",
                  description: t("console.keys.reveal.envDesc"),
                });
              } catch {}
              toast(t("console.keys.reveal.envUpdated"));
            }}
          >
            <XIcons.IconKey size={11} /> {t("console.keys.reveal.useEnv")}
          </button>
          <button className="btn btn-primary" onClick={onClose}>
            {t("console.keys.reveal.saved")}
          </button>
        </div>
      </DialogBody>
    </Dialog>
  );
};

// ============ Environment Variables Tab ============
const EnvTab = ({ focus }) => {
  const { t } = useT();
  const vars = XEnv.useEnv();
  const { toast } = XPrim.useToast();
  const [showValues, setShowValues] = ucS({});
  const [editing, setEditing] = ucS({});
  const [creating, setCreating] = ucS(false);

  React.useEffect(() => {
    if (focus) {
      // Auto-open edit mode for the focused variable
      setEditing((e) => ({
        ...e,
        [focus]: vars.find((v) => v.name === focus)?.value || "",
      }));
      setTimeout(() => {
        const el = document.querySelector(`[data-env-row="${focus}"] input`);
        if (el) {
          el.focus();
          el.select();
        }
      }, 100);
    }
  }, [focus]);

  const handleSave = (name, value) => {
    const existing = vars.find((v) => v.name === name);
    XEnv.set({
      name,
      value,
      scope: existing?.scope || "global",
      description: existing?.description || "",
    });
    toast(`${name} saved locally`);
    setEditing((e) => {
      const { [name]: _, ...rest } = e;
      return rest;
    });
  };

  // Show first 4 + 4 fixed dots + last 4 — enough to identify a key without
  // letting it overflow on mobile when the value is long (50+ chars).
  const maskValue = (v) =>
    v
      ? v.length <= 12
        ? "•".repeat(v.length)
        : v.slice(0, 4) + "••••" + v.slice(-4)
      : "";

  return (
    <>
      <TabHeader
        title={t("console.env.title")}
        subtitle={t("console.env.subtitle")}
        actions={
          <button
            className="btn btn-primary btn-sm"
            onClick={() => setCreating(true)}
          >
            <XIcons.IconPlus size={12} /> New variable
          </button>
        }
      />

      {/* Privacy banner */}
      <div
        className="card"
        style={{ padding: 16, marginBottom: 18, borderColor: "var(--line-2)" }}
      >
        <div className="row gap-3" style={{ alignItems: "flex-start" }}>
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: 6,
              background: "var(--bg-2)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <XIcons.IconShield size={14} style={{ color: "var(--fg-2)" }} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 4 }}>
              Client-side storage
            </div>
            <div
              className="micro-mono"
              style={{
                textTransform: "none",
                letterSpacing: 0,
                color: "var(--fg-3)",
                lineHeight: 1.65,
              }}
            >
              These values are persisted to{" "}
              <span className="mono">localStorage</span> on this device and this
              browser only. They are not synced to your xapi account, not
              visible on other devices, and never transmitted to xapi servers.
              They're used purely to fill in placeholders in the dashboard UI
              (Try-It, MCP config, code snippets).
            </div>
          </div>
        </div>
      </div>

      {/* Desktop table — hidden on mobile (CSS .env-table-wrap rule) */}
      <div className="card env-table-wrap" style={{ overflow: "hidden" }}>
        <table className="table">
          <thead>
            <tr>
              <th style={{ width: "25%" }}>Name</th>
              <th>Value</th>
              <th style={{ width: "15%" }}>Scope</th>
              <th style={{ width: 140, textAlign: "right" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {vars.map((v) => {
              const isEditing = editing[v.name] !== undefined;
              const isShown = showValues[v.name];
              return (
                <tr key={v.name} data-env-row={v.name}>
                  <td style={{ verticalAlign: "top", paddingTop: 14 }}>
                    <div
                      className="mono"
                      style={{ fontSize: 13, color: "var(--fg-1)" }}
                    >
                      {v.name}
                    </div>
                    {v.required && (
                      <span
                        className="tag"
                        style={{
                          fontSize: 10,
                          marginTop: 4,
                          display: "inline-block",
                        }}
                      >
                        required
                      </span>
                    )}
                    {v.description && (
                      <div
                        className="micro-mono"
                        style={{
                          textTransform: "none",
                          letterSpacing: 0,
                          color: "var(--fg-4)",
                          marginTop: 6,
                          lineHeight: 1.55,
                          maxWidth: 260,
                        }}
                      >
                        {v.description}
                      </div>
                    )}
                  </td>
                  <td style={{ verticalAlign: "top", paddingTop: 10 }}>
                    {isEditing ? (
                      <div className="row gap-2">
                        <input
                          className="input"
                          type={isShown ? "text" : "password"}
                          placeholder={`Paste ${v.name} value…`}
                          value={editing[v.name]}
                          onChange={(e) =>
                            setEditing((ed) => ({
                              ...ed,
                              [v.name]: e.target.value,
                            }))
                          }
                          onKeyDown={(e) => {
                            if (e.key === "Enter")
                              handleSave(v.name, editing[v.name]);
                            if (e.key === "Escape")
                              setEditing((ed) => {
                                const { [v.name]: _, ...rest } = ed;
                                return rest;
                              });
                          }}
                          style={{
                            flex: 1,
                            fontFamily: "var(--font-mono)",
                            fontSize: 12,
                          }}
                          autoFocus
                        />
                        <button
                          className="btn btn-sm btn-icon"
                          onClick={() =>
                            setShowValues((s) => ({ ...s, [v.name]: !isShown }))
                          }
                          title={isShown ? "Hide" : "Show"}
                        >
                          {isShown ? (
                            <XIcons.IconEyeOff size={12} />
                          ) : (
                            <XIcons.IconEye size={12} />
                          )}
                        </button>
                      </div>
                    ) : v.value ? (
                      <div
                        className="row gap-2"
                        style={{ alignItems: "center" }}
                      >
                        <span
                          className="mono"
                          style={{ fontSize: 12, color: "var(--fg-2)" }}
                        >
                          {isShown ? v.value : maskValue(v.value)}
                        </span>
                        <button
                          className="btn btn-sm btn-ghost btn-icon"
                          onClick={() =>
                            setShowValues((s) => ({ ...s, [v.name]: !isShown }))
                          }
                          title={isShown ? "Hide" : "Show"}
                        >
                          {isShown ? (
                            <XIcons.IconEyeOff size={11} />
                          ) : (
                            <XIcons.IconEye size={11} />
                          )}
                        </button>
                      </div>
                    ) : (
                      <span
                        className="micro-mono"
                        style={{
                          textTransform: "none",
                          letterSpacing: 0,
                          color: "var(--warn)",
                        }}
                      >
                        <span
                          style={{
                            display: "inline-block",
                            width: 6,
                            height: 6,
                            borderRadius: "50%",
                            background: "var(--warn)",
                            marginRight: 6,
                            verticalAlign: "middle",
                          }}
                        />
                        Not set
                      </span>
                    )}
                  </td>
                  <td style={{ verticalAlign: "top", paddingTop: 14 }}>
                    <span className="tag" style={{ fontSize: 10 }}>
                      {v.scope}
                    </span>
                  </td>
                  <td
                    style={{
                      textAlign: "right",
                      verticalAlign: "top",
                      paddingTop: 10,
                    }}
                  >
                    {isEditing ? (
                      <div
                        className="row gap-1"
                        style={{ justifyContent: "flex-end" }}
                      >
                        <button
                          className="btn btn-sm btn-ghost"
                          onClick={() =>
                            setEditing((ed) => {
                              const { [v.name]: _, ...rest } = ed;
                              return rest;
                            })
                          }
                        >
                          Cancel
                        </button>
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => handleSave(v.name, editing[v.name])}
                        >
                          Save
                        </button>
                      </div>
                    ) : (
                      <div
                        className="row gap-1"
                        style={{ justifyContent: "flex-end" }}
                      >
                        <button
                          className="btn btn-sm"
                          onClick={() =>
                            setEditing((e) => ({
                              ...e,
                              [v.name]: v.value || "",
                            }))
                          }
                        >
                          {v.value ? "Edit" : "Set value"}
                        </button>
                        {!v.required && (
                          <button
                            className="btn btn-sm btn-ghost btn-icon"
                            onClick={() => {
                              XEnv.remove(v.name);
                              toast("Variable removed");
                            }}
                            title="Delete"
                          >
                            <XIcons.IconTrash size={12} />
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile — stacked cards. Same data, vertical layout per variable.
          Hidden on desktop via CSS (.env-cards default display: none). */}
      <div className="env-cards">
        {vars.map((v) => {
          const isEditing = editing[v.name] !== undefined;
          const isShown = showValues[v.name];
          return (
            <div
              key={v.name}
              className="card env-card"
              data-env-row={v.name}
              style={{ padding: 14 }}
            >
              <div
                className="row between"
                style={{ alignItems: "flex-start", gap: 10 }}
              >
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div
                    className="row gap-2"
                    style={{ alignItems: "center", flexWrap: "wrap" }}
                  >
                    <span
                      className="mono"
                      style={{ fontSize: 13, color: "var(--fg-1)" }}
                    >
                      {v.name}
                    </span>
                    {v.required && (
                      <span className="tag" style={{ fontSize: 10 }}>
                        required
                      </span>
                    )}
                    <span className="tag" style={{ fontSize: 10 }}>
                      {v.scope}
                    </span>
                  </div>
                  {v.description && (
                    <div
                      className="micro-mono"
                      style={{
                        textTransform: "none",
                        letterSpacing: 0,
                        color: "var(--fg-4)",
                        marginTop: 6,
                        lineHeight: 1.55,
                      }}
                    >
                      {v.description}
                    </div>
                  )}
                </div>
                {!v.required && !isEditing && (
                  <button
                    className="btn btn-sm btn-ghost btn-icon"
                    onClick={() => {
                      XEnv.remove(v.name);
                      toast("Variable removed");
                    }}
                    title="Delete"
                  >
                    <XIcons.IconTrash size={12} />
                  </button>
                )}
              </div>

              <div style={{ marginTop: 12 }}>
                {isEditing ? (
                  <div className="row gap-2">
                    <input
                      className="input"
                      type={isShown ? "text" : "password"}
                      placeholder={`Paste ${v.name} value…`}
                      value={editing[v.name]}
                      onChange={(e) =>
                        setEditing((ed) => ({
                          ...ed,
                          [v.name]: e.target.value,
                        }))
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter")
                          handleSave(v.name, editing[v.name]);
                        if (e.key === "Escape")
                          setEditing((ed) => {
                            const { [v.name]: _, ...rest } = ed;
                            return rest;
                          });
                      }}
                      style={{
                        flex: 1,
                        fontFamily: "var(--font-mono)",
                        fontSize: 12,
                        minWidth: 0,
                      }}
                      autoFocus
                    />
                    <button
                      className="btn btn-sm btn-icon"
                      onClick={() =>
                        setShowValues((s) => ({ ...s, [v.name]: !isShown }))
                      }
                      title={isShown ? "Hide" : "Show"}
                    >
                      {isShown ? (
                        <XIcons.IconEyeOff size={12} />
                      ) : (
                        <XIcons.IconEye size={12} />
                      )}
                    </button>
                  </div>
                ) : v.value ? (
                  <div className="row gap-2" style={{ alignItems: "center" }}>
                    <span
                      className="mono"
                      style={{
                        fontSize: 12,
                        color: "var(--fg-2)",
                        flex: 1,
                        minWidth: 0,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {isShown ? v.value : maskValue(v.value)}
                    </span>
                    <button
                      className="btn btn-sm btn-ghost btn-icon"
                      onClick={() =>
                        setShowValues((s) => ({ ...s, [v.name]: !isShown }))
                      }
                      title={isShown ? "Hide" : "Show"}
                    >
                      {isShown ? (
                        <XIcons.IconEyeOff size={11} />
                      ) : (
                        <XIcons.IconEye size={11} />
                      )}
                    </button>
                  </div>
                ) : (
                  <span
                    className="micro-mono"
                    style={{
                      textTransform: "none",
                      letterSpacing: 0,
                      color: "var(--warn)",
                    }}
                  >
                    <span
                      style={{
                        display: "inline-block",
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        background: "var(--warn)",
                        marginRight: 6,
                        verticalAlign: "middle",
                      }}
                    />
                    Not set
                  </span>
                )}
              </div>

              <div className="row gap-2" style={{ marginTop: 12 }}>
                {isEditing ? (
                  <>
                    <button
                      className="btn btn-sm btn-ghost grow"
                      style={{ justifyContent: "center" }}
                      onClick={() =>
                        setEditing((ed) => {
                          const { [v.name]: _, ...rest } = ed;
                          return rest;
                        })
                      }
                    >
                      Cancel
                    </button>
                    <button
                      className="btn btn-sm btn-primary grow"
                      style={{ justifyContent: "center" }}
                      onClick={() => handleSave(v.name, editing[v.name])}
                    >
                      Save
                    </button>
                  </>
                ) : (
                  <button
                    className="btn btn-sm grow"
                    style={{ justifyContent: "center" }}
                    onClick={() =>
                      setEditing((e) => ({ ...e, [v.name]: v.value || "" }))
                    }
                  >
                    {v.value ? "Edit" : "Set value"}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* How they're used */}
      <div style={{ marginTop: 24 }}>
        <div className="micro" style={{ marginBottom: 10 }}>
          How these are used
        </div>
        <div
          className="env-howused-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 12,
          }}
        >
          {[
            {
              icon: <XIcons.IconZap size={14} />,
              title: "Try-It panel",
              body: "The API detail page reads ${XAPI_KEY} when building curl snippets and firing requests from your browser.",
            },
            {
              icon: <XIcons.IconLayers size={14} />,
              title: "MCP config",
              body: "The Claude/Cursor config on the MCP page substitutes your key directly, ready to paste.",
            },
            {
              icon: <XIcons.IconTerminal size={14} />,
              title: "Code examples",
              body: "Any snippet you copy from xapi references ${XAPI_KEY} so your key never lands in markdown or links.",
            },
          ].map((b) => (
            <div key={b.title} className="card" style={{ padding: 14 }}>
              <div
                className="row gap-2"
                style={{ marginBottom: 6, color: "var(--fg-2)" }}
              >
                {b.icon}
                <span
                  style={{
                    fontSize: 13,
                    fontWeight: 500,
                    color: "var(--fg-1)",
                  }}
                >
                  {b.title}
                </span>
              </div>
              <div
                className="micro-mono"
                style={{
                  textTransform: "none",
                  letterSpacing: 0,
                  color: "var(--fg-3)",
                  lineHeight: 1.6,
                }}
              >
                {b.body}
              </div>
            </div>
          ))}
        </div>
      </div>

      {creating && <CreateVarModal onClose={() => setCreating(false)} />}
    </>
  );
};

const CreateVarModal = ({ onClose }) => {
  const { toast } = XPrim.useToast();
  const [name, setName] = ucS("");
  const [value, setValue] = ucS("");
  const [desc, setDesc] = ucS("");
  const nameOk = /^[A-Z][A-Z0-9_]*$/.test(name);
  const canSave = nameOk && value;
  const save = () => {
    XEnv.set({ name, value, scope: "global", description: desc });
    toast(`${name} created`);
    onClose();
  };
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.6)",
        zIndex: 100,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
      onClick={onClose}
    >
      <div
        className="card"
        style={{ width: "100%", maxWidth: 480, background: "var(--bg-1)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="row between"
          style={{
            padding: "14px 18px",
            borderBottom: "1px solid var(--line)",
          }}
        >
          <div style={{ fontSize: 14, fontWeight: 500 }}>
            New environment variable
          </div>
          <button className="btn btn-ghost btn-sm btn-icon" onClick={onClose}>
            <XIcons.IconX size={12} />
          </button>
        </div>
        <div style={{ padding: 18 }} className="col gap-3">
          <div>
            <label className="field-label">Name</label>
            <input
              className="input"
              placeholder="OPENAI_API_KEY"
              value={name}
              onChange={(e) =>
                setName(e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, ""))
              }
              autoFocus
              style={{ fontFamily: "var(--font-mono)" }}
            />
            <div
              className="micro-mono"
              style={{
                textTransform: "none",
                letterSpacing: 0,
                color: "var(--fg-4)",
                marginTop: 6,
              }}
            >
              Uppercase letters, digits and underscores. Must start with a
              letter.
            </div>
          </div>
          <div>
            <label className="field-label">Value</label>
            <input
              className="input"
              type="password"
              placeholder="sk-…"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              style={{ fontFamily: "var(--font-mono)" }}
            />
          </div>
          <div>
            <label className="field-label">
              Description{" "}
              <span style={{ color: "var(--fg-4)" }}>(optional)</span>
            </label>
            <input
              className="input"
              placeholder="What this variable is for"
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
            />
          </div>
        </div>
        <div
          style={{ padding: "14px 18px", borderTop: "1px solid var(--line)" }}
          className="row between"
        >
          <div
            className="micro-mono"
            style={{
              textTransform: "none",
              letterSpacing: 0,
              color: "var(--fg-4)",
            }}
          >
            Stored in this browser only
          </div>
          <div className="row gap-2">
            <button className="btn btn-sm" onClick={onClose}>
              Cancel
            </button>
            <button
              className="btn btn-sm btn-primary"
              disabled={!canSave}
              onClick={save}
              style={!canSave ? { opacity: 0.5, cursor: "not-allowed" } : null}
            >
              Create
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const UsageTab = () => <UsageTabState key={accountScope(XShell.useAuth())} />;
const UsageTabState = () => {
  const { t } = useT();
  const { authed, user } = XShell.useAuth();
  const ownerId = user?._raw?.id ?? user?.id ?? null;
  const [records, setRecords] = ucS([]);
  const [total, setTotal] = ucS(0);
  const [page, setPage] = ucS(0);
  const [apiReload, setApiReload] = ucS(0);
  const PAGE = 50;
  const cursors = React.useRef(new Map([[0, null]]));
  const cursorOwner = React.useRef(ownerId);
  const [hasNextPage, setHasNextPage] = ucS(false);
  const [loading, setLoading] = ucS(true);
  const [err, setErr] = ucS(null);
  const [status, setStatus] = ucS("all");
  // 调用来源：API 代理走 UsageRecord；Sandbox 的数据面调用（exec/files/ports）记在
  // SandboxOperation —— 两套账本口径不同（按次 vs 按时长计费），不能混进一张表，
  // 但「统一日志」的入口应该两种都看得到，这里用来源切换而不是硬合并。
  const [source, setSource] = ucS("api"); // api | sandbox
  const [sbOps, setSbOps] = ucS({ items: [], total: 0 });
  const [sbPage, setSbPage] = ucS(1);
  const [sbReload, setSbReload] = ucS(0);
  // 单条详情 drawer（含 timing waterfall）
  const [detailId, setDetailId] = ucS(null);
  const [detailData, setDetailData] = ucS(null);
  const [detailLoading, setDetailLoading] = ucS(false);

  React.useEffect(() => {
    if (cursorOwner.current !== ownerId) {
      cursorOwner.current = ownerId;
      cursors.current = new Map([[0, null]]);
      setRecords([]);
      setTotal(0);
      setHasNextPage(false);
      if (page !== 0) { setPage(0); return; }
    }
    if (!authed) {
      setRecords([]);
      setLoading(false);
      return;
    }
    if (source !== "api") return;
    // Never turn a missing cursor into an offset request for an unseen page.
    if (page > 0 && !cursors.current.get(page)) {
      setHasNextPage(false); setRecords([]); setErr("分页游标不可用，请返回首页重试"); setLoading(false);
      return;
    }
    let cancelled = false;
    setHasNextPage(false);
    setLoading(true);
    setErr(null);
    XApi.usageService
      .all(PAGE, page * PAGE, { cursor: cursors.current.get(page) })
      .then((d) => {
        if (!cancelled) {
          setRecords(d.records || []);
          setTotal(d.total ?? 0);
          setHasNextPage(Boolean(d.nextCursor) && (d.hasMore ?? (page + 1) * PAGE < (d.total ?? 0)));
          if (d.nextCursor) cursors.current.set(page + 1, d.nextCursor);
          else cursors.current.delete(page + 1);
        }
      })
      .catch((e) => {
        if (!cancelled) { setRecords([]); setHasNextPage(false); setErr(e.message || "Failed"); }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [authed, ownerId, page, source, apiReload]);

  // Sandbox 调用日志（source=sandbox 时）
  React.useEffect(() => {
    if (!authed || source !== "sandbox") return undefined;
    let cancelled = false;
    setLoading(true);
    setErr(null);
    XApi.apiSvc.sandboxOperations({ page: sbPage, pageSize: 50 })
      .then((d) => { if (!cancelled) { setSbOps(d || { items: [] }); setErr(null); } })
      .catch((e) => { if (!cancelled) setErr(e.message || "Failed"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [authed, source, sbPage, sbReload]);

  // detail drawer fetch
  React.useEffect(() => {
    if (!detailId) {
      setDetailData(null);
      return;
    }
    let cancelled = false;
    setDetailLoading(true);
    setDetailData(null);
    XApi.usageService
      .detail(detailId)
      .then((d) => {
        if (!cancelled) setDetailData(d);
      })
      .catch((e) => {
        if (!cancelled) setDetailData({ __err: e.message || "Failed" });
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [detailId]);

  const list = records.filter((r) => {
    if (status === "all") return true;
    const s = usageStatus(r);
    if (status === "ok") return s >= 200 && s < 400;
    return s === 0 || s >= 400;
  });

  const fmtTs = (ts) => {
    if (!ts) return "—";
    try {
      return new Date(ts).toISOString().replace("T", " ").slice(0, 19);
    } catch {
      return String(ts);
    }
  };

  if (!authed)
    return (
      <>
        <TabHeader
          title={t("console.usage.title")}
          subtitle={t("console.usage.signinSubtitle")}
        />
      </>
    );

  return (
    <>
      <TabHeader
        title={t("console.usage.title")}
        subtitle={
          total > 0
            ? t("console.usage.subtitleN").replace(
                "{N}",
                total.toLocaleString(),
              )
            : t("console.usage.subtitle")
        }
      />
      <div
        className="row gap-2 console-toolbar"
        style={{ marginBottom: 14, flexWrap: "wrap" }}
      >
        <div className="row gap-1">
          {[["api", "API 调用"], ["sandbox", "Sandbox 调用"]].map(([k, l]) => (
            <button key={k} type="button" className={`btn btn-sm ${source === k ? "" : "btn-ghost"}`} onClick={() => setSource(k)}>{l}</button>
          ))}
        </div>
        {source === "api" && (
          <Select
            value={status}
            onChange={setStatus}
            width={180}
            options={[
              { value: "all", label: "All status" },
              { value: "ok", label: "2xx/3xx Success" },
              { value: "err", label: "4xx/5xx Errors" },
            ]}
          />
        )}
        <div className="grow" />
        {source === "api" ? (
          <XPrim.Pagination
            page={page + 1}
            sequential
            hasNextPage={hasNextPage}
            disabled={loading}
            pageSize={PAGE}
            total={total}
            onPage={(p) => { if (!loading && (p === 1 || cursors.current.has(p - 1))) setPage(p - 1); }}
          />
        ) : (
          <XPrim.Pagination
            page={sbPage}
            pageSize={50}
            total={sbOps.total}
            onPage={setSbPage}
          />
        )}
      </div>
      {source === "sandbox" && (
        <div className="card" style={{ padding: "12px 16px" }}>
          {loading ? <div style={{ padding: 16, color: "var(--fg-3)", fontSize: 13 }}>Loading…</div>
            : err ? <div role="alert" style={{ padding: 16, color: "var(--err)" }}>{err} <button onClick={() => setSbReload(n => n + 1)}>重试</button></div>
            : !(sbOps.items || []).length ? (
              <div style={{ padding: 16, color: "var(--fg-4)", fontSize: 13 }}>
                暂无 Sandbox 调用记录。创建并使用 Sandbox 后，exec / 文件 / 端口等调用会记录在这里。
              </div>
            ) : (
              <>
                <div className="micro" style={{ marginBottom: 6 }}>
                  全部实例的接口调用（含生命周期与数据面调用）· 点行展开命令与输出 · 完整实例视图在「Sandboxes」
                </div>
                {sbOps.items.map((op) => <SbOpRow key={op.id} op={op} showInstance />)}
              </>
            )}
        </div>
      )}
      {source === "api" && (
      <div className="card">
        {loading && (
          <div style={{ padding: 20, textAlign: "center" }}>
            <div className="spinner" />{" "}
            <span
              style={{ marginLeft: 10, color: "var(--fg-3)", fontSize: 13 }}
            >
              Loading…
            </span>
          </div>
        )}
        {!loading && err && (
          <div role="alert" style={{ padding: 18, color: "var(--err)", fontSize: 13 }}>
            {err} <button onClick={() => setApiReload(n => n + 1)}>重试</button>
          </div>
        )}
        {!loading && !err && list.length === 0 && (
          <div
            style={{
              padding: 32,
              textAlign: "center",
              color: "var(--fg-3)",
              fontSize: 13,
            }}
          >
            No calls yet.
          </div>
        )}
        {!loading && !err && list.length > 0 && (
          <>
            <div className="table-scroll">
              <table className="table">
                <thead>
                  <tr>
                    <th>{t("console.overview.col.time")}</th>
                    <th>{t("console.overview.col.api")}</th>
                    <th>{t("console.overview.col.endpoint")}</th>
                    <th>{t("console.overview.col.path")}</th>
                    <th style={{ textAlign: "center" }}>
                      {t("console.overview.col.status")}
                    </th>
                    <th style={{ textAlign: "right" }}>
                      {t("console.aiLogs.col.latency")}
                    </th>
                    <th style={{ textAlign: "right" }}>
                      {t("console.overview.col.cost")}
                    </th>
                    <th>{t("console.overview.col.key")}</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((c, i) => {
                    const s = usageStatus(c);
                    // Backend (key.service.getAllUsageRecords) returns nested `endpoint.version.api`
                    // and full `apiKey`. Pull the human-readable names from there before falling
                    // back to UUIDs — the previous version surfaced raw apiServiceId/apiKeyId,
                    // which is unreadable. We also keep flat-field fallbacks (apiServiceName,
                    // apiKeyName) so this component still works if backend ever returns a
                    // pre-flattened shape.
                    const apiName =
                      c.endpoint?.version?.api?.name ||
                      c.apiServiceName ||
                      c.apiName ||
                      "—";
                    const endpointName = c.endpoint?.name || "";
                    const path = c.endpoint?.path || c.path || "";
                    const method = c.method || c.endpoint?.method || "";
                    const keyName = c.apiKey?.name || c.apiKeyName || "";
                    const keyLabel =
                      keyName ||
                      (c.apiKeyId ? c.apiKeyId.slice(0, 8) + "…" : "—");
                    const costN = parseFloat(c.amount || c.cost || 0);
                    // Friendly cost: tiny values → "<$0.0001", otherwise fixed-precision
                    const costStr =
                      costN === 0
                        ? "$0"
                        : costN < 0.0001
                          ? "<$0.0001"
                          : costN < 1
                            ? "$" +
                              costN
                                .toFixed(6)
                                .replace(/0+$/, "")
                                .replace(/\.$/, "")
                            : "$" + costN.toFixed(4);
                    return (
                      <tr
                        key={c.id || i}
                        data-usage-id={c.id}
                        style={{ cursor: c.id ? "pointer" : "default" }}
                        onClick={() => c.id && setDetailId(c.id)}
                      >
                        <td
                          className="mono"
                          style={{
                            fontSize: 11,
                            color: "var(--fg-3)",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {fmtTs(c.timestamp || c.createdAt)}
                        </td>
                        <td style={{ whiteSpace: "nowrap", fontSize: 12 }}>
                          <span className="tag" style={{ fontSize: 10 }}>
                            {apiName}
                          </span>
                        </td>
                        <td
                          style={{
                            whiteSpace: "nowrap",
                            fontSize: 12,
                            color: "var(--fg-2)",
                          }}
                        >
                          {endpointName || (
                            <span style={{ color: "var(--fg-4)" }}>—</span>
                          )}
                        </td>
                        <td
                          className="mono"
                          style={{ fontSize: 11, whiteSpace: "nowrap" }}
                        >
                          {method && (
                            <span
                              style={{ color: "var(--fg-3)", marginRight: 6 }}
                            >
                              {method}
                            </span>
                          )}
                          <span style={{ color: "var(--fg-2)" }}>
                            {path || "—"}
                          </span>
                        </td>
                        <td style={{ textAlign: "center" }}>
                          <span
                            className="mono"
                            style={{
                              fontSize: 11,
                              color:
                                s > 0 && s < 300
                                  ? "var(--ok)"
                                  : s >= 300 && s < 500
                                    ? "var(--warn)"
                                    : "var(--err)",
                            }}
                          >
                            {s}
                          </span>
                        </td>
                        <td
                          className="mono"
                          style={{
                            textAlign: "right",
                            fontSize: 11,
                            color: "var(--fg-3)",
                          }}
                        >
                          {(() => {
                            const ms =
                              c.upstreamLatencyMs ?? c.responseTime ?? null;
                            if (ms == null) return "—";
                            return ms >= 1000
                              ? `${(ms / 1000).toFixed(1)}s`
                              : `${Math.round(ms)}ms`;
                          })()}
                        </td>
                        <td
                          className="mono"
                          style={{ textAlign: "right", fontSize: 12 }}
                          title={`$${costN.toFixed(8)}`}
                        >
                          {costStr}
                        </td>
                        <td
                          style={{
                            fontSize: 12,
                            color: "var(--fg-3)",
                            whiteSpace: "nowrap",
                          }}
                          title={c.apiKeyId}
                        >
                          {keyLabel}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <XPrim.Pagination
              inline
              page={page + 1}
            sequential
            hasNextPage={hasNextPage}
            disabled={loading}
              pageSize={PAGE}
              total={total}
              onPage={(p) => { if (!loading && (p === 1 || cursors.current.has(p - 1))) setPage(p - 1); }}
            />
          </>
        )}
      </div>
      )}
      {/* 单条详情 drawer：点击表行打开，含 timing waterfall（含非 AI） */}
      {detailId && (
        <UsageDetailDrawer
          id={detailId}
          data={detailData}
          loading={detailLoading}
          onClose={() => setDetailId(null)}
        />
      )}
    </>
  );
};

// ── 通用 UsageRecord 详情 drawer（非 AI 用量 + provider analytics 共用）─────
const UsageDetailDrawer = ({ id, data, loading, onClose }) => {
  const { t } = useT();
  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  const fmtMs = (ms) =>
    ms == null
      ? "—"
      : ms >= 1000
        ? `${(ms / 1000).toFixed(1)}s`
        : `${Math.round(ms)}ms`;
  const Row = ({ label, value }) => (
    <div
      className="row between"
      style={{ padding: "6px 0", borderBottom: "1px dashed var(--line)" }}
    >
      <div style={{ fontSize: 11, color: "var(--fg-4)" }}>{label}</div>
      <div
        className="mono"
        style={{
          fontSize: 12,
          color: "var(--fg-1)",
          textAlign: "right",
          wordBreak: "break-all",
          maxWidth: 360,
        }}
      >
        {value ?? "—"}
      </div>
    </div>
  );
  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.4)",
          zIndex: 60,
        }}
      />
      <div
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          bottom: 0,
          width: "min(520px, 100vw)",
          background: "var(--bg-1)",
          borderLeft: "1px solid var(--line)",
          zIndex: 61,
          padding: 24,
          overflowY: "auto",
          boxShadow: "-8px 0 24px rgba(0,0,0,0.2)",
        }}
        role="dialog"
      >
        <div className="row between" style={{ marginBottom: 18 }}>
          <div className="micro">
            {t("console.usage.drawer.title") || "Call detail"}
          </div>
          <button className="btn btn-sm btn-ghost btn-icon" onClick={onClose}>
            <XIcons.IconX size={12} />
          </button>
        </div>
        {loading && (
          <div style={{ textAlign: "center", padding: 40 }}>
            <div className="spinner" />
          </div>
        )}
        {!loading && data?.__err && (
          <div style={{ color: "var(--err)", fontSize: 13 }}>{data.__err}</div>
        )}
        {!loading && data && !data.__err && (
          <>
            <Row
              label="Time"
              value={
                data.createdAt &&
                new Date(data.createdAt)
                  .toISOString()
                  .replace("T", " ")
                  .slice(0, 19)
              }
            />
            <Row label="Service" value={data.apiService?.name || "—"} />
            <Row
              label="Endpoint"
              value={
                data.endpoint
                  ? `${data.endpoint.method} ${data.endpoint.path}`
                  : "—"
              }
            />
            <Row label="API Key" value={data.apiKey?.name || data.apiKeyId} />
            <Row label="Status" value={String(data.statusCode)} />
            <Row
              label="Cost"
              value={`$${parseFloat(data.cost || 0).toFixed(6)}`}
            />
            <div style={{ marginTop: 18, marginBottom: 8 }} className="micro">
              Timing
            </div>
            <TimingWaterfall
              data={{
                gatewayMs: data.gatewayOverheadMs ?? null,
                dnsMs: data.dnsMs ?? null,
                tcpMs: data.tcpMs ?? null,
                tlsMs: data.tlsMs ?? null,
                // Upstream wait = TTFB - connect 那段；keep-alive 复用时 connect=null → 整个 upstream 都是 wait
                waitMs:
                  data.upstreamLatencyMs != null
                    ? Math.max(
                        data.upstreamLatencyMs - (data.connectMs ?? 0),
                        0,
                      )
                    : null,
                generationMs: data.streamDurationMs ?? null,
                totalMs: data.responseTime ?? null,
                reusedConnection: data.reusedConnection,
                isStreaming: data.isStreaming,
              }}
              labels={{
                gateway: t("console.aiLogs.drawer.gatewayOverhead"),
                wait: t("console.aiLogs.drawer.wait"),
                generation: t("console.aiLogs.drawer.generation"),
                reusedConn: t("console.aiLogs.drawer.reusedConn"),
                nonStream: t("console.aiLogs.drawer.nonStream"),
              }}
            />
            <Row
              label={t("console.aiLogs.drawer.connect")}
              value={
                data.connectMs != null
                  ? fmtMs(data.connectMs)
                  : data.reusedConnection
                    ? t("console.aiLogs.drawer.reused")
                    : "—"
              }
            />
            <Row
              label={t("console.aiLogs.drawer.providerLatency")}
              value={fmtMs(data.upstreamLatencyMs)}
            />
            <Row
              label={t("console.aiLogs.drawer.responseTime")}
              value={fmtMs(data.responseTime)}
            />
            <div style={{ marginTop: 18, marginBottom: 8 }} className="micro">
              URL
            </div>
            <Row label="URL" value={data.requestUrl} />
          </>
        )}
      </div>
    </>
  );
};

// ============================================================================
// AI Logs tab — Console > AI Logs
//
// 仅展示 billingType=PER_TOKEN 的 UsageRecord（区分于 UsageTab 的全部调用日志）。
// 数据通过 4 个 endpoint 拉：
//   GET /statistics/ai/logs              主表（游标分页）
//   GET /statistics/ai/logs/timeseries   顶部柱状图（读 UserAiUsageDaily 聚合表）
//   GET /statistics/ai/logs/:id          行详情
//   GET /statistics/ai/logs/facets       下拉枚举
//
// 列：Time / API Key / Provider / Model / Input / Output / Cache / Cost / Speed / Status
//   • Cache 列 hover tooltip 展示 read/create 分项 token 数（不含花费 —— 当前不对 cache 计费）
//   • Speed 列：outputTokens / (streamDurationMs/1000)；非流式或老数据为 "—"
//   • 点行打开右侧 drawer，展示完整 token 分项 + 延迟 + 脱敏 URL
// ============================================================================
export const AiLogsTab = () => <AiLogsTabState key={accountScope(XShell.useAuth())} />;
const AiLogsTabState = () => {
  const { t } = useT();
  const { authed } = XShell.useAuth();
  // Range selector + 筛选状态
  const [range, setRange] = ucS("7d"); // '1d' | '7d' | '30d'
  const rangeDays = range === "1d" ? 1 : range === "30d" ? 30 : 7;
  const logBounds = React.useMemo(() => { const to = new Date(); return { from: new Date(+to - rangeDays * 86400_000).toISOString(), to: to.toISOString() }; }, [rangeDays]);
  const [filterApiKey, setFilterApiKey] = ucS("");
  const [filterProvider, setFilterProvider] = ucS("");
  const [filterModel, setFilterModel] = ucS("");
  const [filterEndpoint, setFilterEndpoint] = ucS(""); // 接口（endpoint）层面筛选
  // 结果筛选：'' 全部 | 'ok' 成功 | 'err' 全部失败 | USER/REQUEST/UPSTREAM/GATEWAY 按责任方
  const [filterOutcome, setFilterOutcome] = ucS("");
  // Data states
  const [series, setSeries] = ucS([]);
  // summary comes from /ai/logs response (DB aggregate < today + Redis today),
  // so the cards stay consistent with the list across the 5-min rollup window.
  const [summaryFromApi, setSummaryFromApi] = ucS(null);
  const [rows, setRows] = ucS([]);
  const [cursor, setCursor] = ucS(null);
  const [hasMore, setHasMore] = ucS(false);
  const [facets, setFacets] = ucS({
    apiKeys: [],
    providers: [],
    models: [],
    endpoints: [],
  });
  const [loading, setLoading] = ucS(true);
  const [err, setErr] = ucS(null);
  // Detail drawer
  const [openId, setOpenId] = ucS(null);
  const [detail, setDetail] = ucS(null);
  const [detailLoading, setDetailLoading] = ucS(false);

  const listGeneration = React.useRef(0);
  const moreInFlight = React.useRef(null);
  const detailRequest = React.useRef(null);

  // Cursor 重置 trigger —— 筛选变化时回到第一页
  const filtersKey = React.useMemo(
    () =>
      [
        range,
        filterApiKey,
        filterProvider,
        filterModel,
        filterEndpoint,
        filterOutcome,
      ].join("|"),
    [
      range,
      filterApiKey,
      filterProvider,
      filterModel,
      filterEndpoint,
      filterOutcome,
    ],
  );

  // 结果筛选 → 后端参数：ok/err 走 status，责任方走 errorOrigin（二者互斥）
  const outcomeParams = React.useMemo(() => {
    if (!filterOutcome) return {};
    if (filterOutcome === "ok" || filterOutcome === "err")
      return { status: filterOutcome };
    return { errorOrigin: filterOutcome };
  }, [filterOutcome]);

  // 拉 facets 一次（用户调用过的下拉项）
  React.useEffect(() => {
    if (!authed) return;
    let cancelled = false;
    XApi.aiLogsService
      .facets()
      .then((d) => {
        if (!cancelled) setFacets(d);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [authed]);

  // 拉列表 + 时序
  React.useEffect(() => {
    if (!authed) {
      setLoading(false);
      return;
    }
    const generation = ++listGeneration.current;
    moreInFlight.current = null;
    let cancelled = false;
    setRows([]); setSeries([]); setCursor(null); setHasMore(false); setSummaryFromApi(null);
    setOpenId(null); setDetail(null); detailRequest.current = null; setDetailLoading(false);
    setLoading(true);
    setErr(null);
    const commonFilters = {
      ...logBounds,
      apiKeyId: filterApiKey || undefined,
      apiServiceId: filterProvider || undefined,
      modelName: filterModel || undefined,
    };
    Promise.allSettled([
      XApi.aiLogsService.list({
        ...commonFilters,
        endpointId: filterEndpoint || undefined,
        ...outcomeParams,
        limit: 50,
      }),
      // timeseries/summary 仍按 key/provider/model 维度（不随接口/结果细分，保持图表稳定）
      XApi.aiLogsService.timeseries(commonFilters),
    ])
      .then(([listRes, tsRes]) => {
        if (cancelled) return;
        if (listRes.status === "fulfilled") {
          setRows(listRes.value.rows || []);
          setCursor(listRes.value.nextCursor);
          setHasMore(!!listRes.value.hasMore);
          // New: server returns aggregate totals (DB <today + Redis today merged).
          // Falls back to client-side summing of timeseries if a stale backend doesn't.
          setSummaryFromApi(listRes.value.summary || null);
        } else {
          setErr(listRes.reason?.message || "Failed");
        }
        if (tsRes.status === "fulfilled") setSeries(tsRes.value || []);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
      if (listGeneration.current === generation) listGeneration.current++;
    };
  }, [authed, filtersKey, logBounds]);

  // Load more (cursor pagination)
  const loadMore = React.useCallback(async () => {
    if (!cursor || loading || moreInFlight.current) return;
    const generation = listGeneration.current;
    const request = {}; moreInFlight.current = request;
    setLoading(true);
    try {
      const r = await XApi.aiLogsService.list({
        ...logBounds,
        apiKeyId: filterApiKey || undefined,
        apiServiceId: filterProvider || undefined,
        modelName: filterModel || undefined,
        endpointId: filterEndpoint || undefined,
        ...outcomeParams,
        cursor,
        limit: 50,
      });
      if (generation !== listGeneration.current) return;
      setRows((prev) => prev.concat(r.rows || []));
      setCursor(r.nextCursor);
      setHasMore(!!r.hasMore);
    } catch (e) {
      if (generation === listGeneration.current) setErr(e.message);
    } finally {
      if (moreInFlight.current === request) moreInFlight.current = null;
      if (generation === listGeneration.current) setLoading(false);
    }
  }, [
    cursor,
    loading,
    logBounds,
    filterApiKey,
    filterProvider,
    filterModel,
    filterEndpoint,
    outcomeParams,
  ]);

  // 行点击 → 拉详情 + 打开 drawer
  const openDetail = async (id) => {
    const request = {}; detailRequest.current = request;
    setOpenId(id);
    setDetail(null);
    setDetailLoading(true);
    try {
      const d = await XApi.aiLogsService.detail(id);
      if (detailRequest.current === request) setDetail(d);
    } catch (e) {
      if (detailRequest.current === request) setDetail({ __err: e.message || "Failed to load" });
    } finally {
      if (detailRequest.current === request) setDetailLoading(false);
    }
  };

  // Summary preference:
  //   1) Server-supplied `summary` (DB aggregate < today + Redis today) — exact match
  //      with the documented aggregate/Redis freshness; endpoint/outcome filters
  //      are not represented by every summary dimension.
  //   2) Fallback: sum the timeseries client-side (legacy 5-min-lag path). Kept so
  //      the UI doesn't break if the backend ever stops returning summary.
  const summary = React.useMemo(() => {
    if (summaryFromApi) {
      return {
        calls: Number(summaryFromApi.calls || 0),
        inTok: Number(summaryFromApi.inputTokens || 0),
        outTok: Number(summaryFromApi.outputTokens || 0),
        cost: parseFloat(summaryFromApi.totalCost || 0),
        avgLatencyMs: summaryFromApi.avgUpstreamLatencyMs ?? null,
        avgTokPerSec: summaryFromApi.avgThroughputTokPerSec ?? null,
      };
    }
    let calls = 0,
      inTok = 0,
      outTok = 0,
      cost = 0;
    for (const p of series) {
      calls += p.calls || 0;
      inTok += p.inputTokens || 0;
      outTok += p.outputTokens || 0;
      cost += parseFloat(p.totalCost || 0);
    }
    return {
      calls,
      inTok,
      outTok,
      cost,
      avgLatencyMs: null,
      avgTokPerSec: null,
    };
  }, [summaryFromApi, series]);

  // Provider latency 友好格式：ms < 1000 显 ms，否则 s
  const fmtLatency = (ms) => {
    if (ms == null) return "—";
    return ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${Math.round(ms)}ms`;
  };

  const RangePicker = () => (
    <div
      className="row gap-1 hairline"
      style={{ borderRadius: 6, padding: 2, background: "var(--bg-2)" }}
    >
      {[
        { k: "1d", l: t("common.lastDay") },
        { k: "7d", l: t("common.last7d") },
        { k: "30d", l: t("common.last30d") },
      ].map((r) => (
        <button
          key={r.k}
          onClick={() => setRange(r.k)}
          className="btn btn-sm"
          style={{
            border: "none",
            background: range === r.k ? "var(--bg-3)" : "transparent",
            color: range === r.k ? "var(--fg-1)" : "var(--fg-3)",
            fontSize: 11,
            padding: "4px 10px",
          }}
        >
          {r.l}
        </button>
      ))}
    </div>
  );

  if (!authed)
    return (
      <>
        <TabHeader
          title={t("console.aiLogs.title")}
          subtitle={t("console.aiLogs.signinSubtitle")}
        />
      </>
    );

  return (
    <>
      <TabHeader
        title={t("console.aiLogs.title")}
        subtitle={t("console.aiLogs.subtitle")}
        actions={<RangePicker />}
      />

      {/* Summary stat cards */}
      <div
        className="stat-grid-4"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
          gap: 12,
          marginBottom: 16,
        }}
      >
        <StatCard
          label={t("console.aiLogs.stat.calls")}
          value={XPrim.fmt(summary.calls)}
        />
        <StatCard
          label={t("console.aiLogs.stat.inputTokens")}
          value={XPrim.fmt(summary.inTok)}
        />
        <StatCard
          label={t("console.aiLogs.stat.outputTokens")}
          value={XPrim.fmt(summary.outTok)}
        />
        <StatCard
          label={t("console.aiLogs.stat.cost")}
          value={"$" + summary.cost.toFixed(4)}
        />
        <StatCard
          label={t("console.aiLogs.stat.providerLatency")}
          value={fmtLatency(summary.avgLatencyMs)}
        />
        <StatCard
          label={t("console.aiLogs.stat.throughput")}
          value={
            summary.avgTokPerSec != null ? `${summary.avgTokPerSec} tok/s` : "—"
          }
        />
      </div>

      {/* Bar chart — 每日 calls 量。简单 SVG，不引入 chart 库 */}
      {series.length > 0 && (
        <div
          className="card"
          style={{ padding: "14px 18px", marginBottom: 14 }}
        >
          <div className="micro" style={{ marginBottom: 10 }}>
            {t("console.aiLogs.chartTitle")}
          </div>
          <AiLogsBarChart series={series} />
        </div>
      )}

      {/* Filters */}
      <div
        className="row gap-2 console-toolbar"
        style={{ marginBottom: 14, flexWrap: "wrap" }}
      >
        <Select
          value={filterApiKey}
          onChange={setFilterApiKey}
          width={200}
          options={[
            { value: "", label: t("console.aiLogs.filter.allKeys") },
            ...facets.apiKeys.map((k) => ({ value: k.id, label: k.label })),
          ]}
        />
        <Select
          value={filterProvider}
          onChange={setFilterProvider}
          width={200}
          options={[
            { value: "", label: t("console.aiLogs.filter.allProviders") },
            ...facets.providers.map((p) => ({ value: p.id, label: p.label })),
          ]}
        />
        <Select
          value={filterModel}
          onChange={setFilterModel}
          width={200}
          options={[
            { value: "", label: t("console.aiLogs.filter.allModels") },
            ...facets.models.map((m) => ({ value: m.id, label: m.label })),
          ]}
        />
        <Select
          value={filterEndpoint}
          onChange={setFilterEndpoint}
          width={220}
          options={[
            { value: "", label: t("console.aiLogs.filter.allEndpoints") },
            ...facets.endpoints.map((e) => ({ value: e.id, label: e.label })),
          ]}
        />
        <Select
          value={filterOutcome}
          onChange={setFilterOutcome}
          width={180}
          options={[
            { value: "", label: t("console.aiLogs.filter.allOutcomes") },
            { value: "ok", label: t("console.aiLogs.filter.success") },
            { value: "err", label: t("console.aiLogs.filter.errors") },
            { value: "UPSTREAM", label: t("console.aiLogs.origin.UPSTREAM") },
            { value: "REQUEST", label: t("console.aiLogs.origin.REQUEST") },
            { value: "USER", label: t("console.aiLogs.origin.USER") },
            { value: "GATEWAY", label: t("console.aiLogs.origin.GATEWAY") },
          ]}
        />
      </div>

      {/* Table */}
      <div className="card">
        {loading && rows.length === 0 && (
          <div style={{ padding: 20, textAlign: "center" }}>
            <div className="spinner" />
          </div>
        )}
        {!loading && err && (
          <div style={{ padding: 18, color: "var(--err)", fontSize: 13 }}>
            {err}
          </div>
        )}
        {!loading && !err && rows.length === 0 && (
          <div
            style={{
              padding: 32,
              textAlign: "center",
              color: "var(--fg-3)",
              fontSize: 13,
            }}
          >
            {t("console.aiLogs.empty")}
          </div>
        )}
        {rows.length > 0 && (
          <>
            <div className="table-scroll">
              <table className="table">
                <thead>
                  <tr>
                    <th>{t("console.aiLogs.col.time")}</th>
                    <th>{t("console.aiLogs.col.apiKey")}</th>
                    <th>{t("console.aiLogs.col.provider")}</th>
                    <th>{t("console.aiLogs.col.model")}</th>
                    <th style={{ textAlign: "right" }}>
                      {t("console.aiLogs.col.input")}
                    </th>
                    <th style={{ textAlign: "right" }}>
                      {t("console.aiLogs.col.output")}
                    </th>
                    <th style={{ textAlign: "right" }}>
                      {t("console.aiLogs.col.cache")}
                    </th>
                    <th style={{ textAlign: "right" }}>
                      {t("console.aiLogs.col.latency")}
                    </th>
                    <th style={{ textAlign: "right" }}>
                      {t("console.aiLogs.col.cost")}
                    </th>
                    <th style={{ textAlign: "right" }}>
                      {t("console.aiLogs.col.speed")}
                    </th>
                    <th>{t("console.aiLogs.col.status")}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <AiLogRow key={r.id} row={r} onOpen={openDetail} />
                  ))}
                </tbody>
              </table>
            </div>
            <div
              className="row"
              style={{
                padding: "12px 18px",
                borderTop: "1px solid var(--line)",
                justifyContent: "center",
              }}
            >
              {hasMore ? (
                <button
                  className="btn btn-sm"
                  disabled={loading}
                  onClick={loadMore}
                >
                  {loading ? (
                    <>
                      <div className="spinner" /> {t("common.loading")}
                    </>
                  ) : (
                    t("console.aiLogs.loadMore")
                  )}
                </button>
              ) : (
                <div className="micro" style={{ color: "var(--fg-4)" }}>
                  {t("console.aiLogs.noMore")}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Detail drawer */}
      {openId && (
        <AiLogDetailDrawer
          id={openId}
          data={detail}
          loading={detailLoading}
          onClose={() => {
            setOpenId(null);
            setDetail(null);
          }}
        />
      )}
    </>
  );
};

// Bar chart for daily AI call volume — Recharts.
// Choice rationale:
//   - Declarative React API matches the rest of the codebase.
//   - Hover behavior (axis-row highlight + content tooltip) is built in and
//     correctly handles touch/pointer events without us re-implementing it.
//   - Styling uses CSS variables so theme switches automatically work.
//
// The data series shape is from /statistics/ai/logs/timeseries:
//   { date: "YYYY-MM-DD", calls, inputTokens, outputTokens,
//     cacheReadTokens, cacheCreateTokens, totalCost: "0.00" }
const AiLogsChartTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0].payload;
  const cache = (p.cacheReadTokens || 0) + (p.cacheCreateTokens || 0);
  const fmt = (n) => Number(n || 0).toLocaleString();
  const costNum = parseFloat(p.totalCost || 0);
  const costStr =
    costNum === 0
      ? "$0"
      : costNum < 0.0001
        ? "<$0.0001"
        : "$" + costNum.toFixed(6).replace(/0+$/, "").replace(/\.$/, "");
  return (
    <div
      style={{
        background: "var(--bg-1)",
        border: "1px solid var(--line)",
        borderRadius: 6,
        padding: "8px 12px",
        fontSize: 11,
        color: "var(--fg-1)",
        boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
        minWidth: 180,
      }}
    >
      <div style={{ fontSize: 11, color: "var(--fg-2)", marginBottom: 6 }}>
        {p.date}
      </div>
      <div className="row between" style={{ marginBottom: 2 }}>
        <span style={{ color: "var(--fg-3)" }}>Calls</span>
        <span className="mono">{fmt(p.calls)}</span>
      </div>
      <div className="row between" style={{ marginBottom: 2 }}>
        <span style={{ color: "var(--fg-3)" }}>Input tokens</span>
        <span className="mono">{fmt(p.inputTokens)}</span>
      </div>
      <div className="row between" style={{ marginBottom: 2 }}>
        <span style={{ color: "var(--fg-3)" }}>Output tokens</span>
        <span className="mono">{fmt(p.outputTokens)}</span>
      </div>
      {cache > 0 && (
        <div className="row between" style={{ marginBottom: 2 }}>
          <span style={{ color: "var(--fg-3)" }}>Cache tokens</span>
          <span className="mono">{fmt(cache)}</span>
        </div>
      )}
      <div
        className="row between"
        style={{
          marginTop: 4,
          paddingTop: 4,
          borderTop: "1px dashed var(--line)",
        }}
      >
        <span style={{ color: "var(--fg-3)" }}>Cost</span>
        <span className="mono">{costStr}</span>
      </div>
    </div>
  );
};

const AiLogsBarChart = ({ series }) => {
  const [hoverIdx, setHoverIdx] = React.useState(-1);
  // Compress YYYY-MM-DD to MM-DD for axis ticks
  const data = series.map((p) => ({ ...p, label: p.date.slice(5) }));
  return (
    <div style={{ width: "100%", height: 140 }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 6, right: 8, left: 4, bottom: 0 }}>
          <CartesianGrid
            strokeDasharray="2 4"
            stroke="var(--line)"
            vertical={false}
          />
          <XAxis
            dataKey="label"
            stroke="var(--fg-4)"
            tick={{ fontSize: 10 }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            stroke="var(--fg-4)"
            tick={{ fontSize: 10 }}
            axisLine={false}
            tickLine={false}
            width={42}
            allowDecimals={false}
            tickFormatter={compactNumber}
          />
          <Tooltip
            content={<AiLogsChartTooltip />}
            cursor={{ fill: "var(--bg-3)", opacity: 0.4 }}
          />
          <Bar
            dataKey="calls"
            radius={[3, 3, 0, 0]}
            onMouseEnter={(_, i) => setHoverIdx(i)}
            onMouseLeave={() => setHoverIdx(-1)}
          >
            {data.map((entry, i) => (
              <Cell
                key={entry.date}
                fill={
                  hoverIdx === i
                    ? "var(--fg-1)"
                    : (entry.calls || 0) === 0
                      ? "var(--bg-3)"
                      : "var(--fg-3)"
                }
                style={{ transition: "fill 120ms" }}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

// 一行 AI Log。Cache 列 hover 显示 read/create 分项。
// errorOrigin → 徽章配色（与责任方语义对应）：
//   USER 账户/预检(非 provider 锅) 中性 · REQUEST 请求错(你的锅) 琥珀
//   UPSTREAM provider 可靠性(provider 锅) 红 · GATEWAY 我们的锅 紫
const ORIGIN_STYLE = {
  USER: { bg: "rgba(140,150,170,.16)", fg: "var(--fg-3)" },
  REQUEST: { bg: "rgba(214,160,0,.16)", fg: "var(--warn)" },
  UPSTREAM: { bg: "rgba(225,90,90,.16)", fg: "var(--err)" },
  GATEWAY: { bg: "rgba(180,120,220,.18)", fg: "#b884e0" },
};

const AiLogRow = ({ row, onOpen }) => {
  const { t } = useT();
  const fmtTs = (ts) => {
    if (!ts) return "—";
    try {
      return new Date(ts).toISOString().replace("T", " ").slice(0, 19);
    } catch {
      return String(ts);
    }
  };
  const sc = row.statusCode;
  const cacheTotal = (row.cacheReadTokens || 0) + (row.cacheCreateTokens || 0);
  // Speed 直接用后端算好的 throughputTokPerSec（统一口径）
  const speed = row.throughputTokPerSec;
  // 显示规范化模型 id（canonicalModelId 优先），hover 看上游原始名
  const displayModel = row.canonicalModelId || row.modelName || "—";
  const fmtMs = (ms) =>
    ms == null
      ? "—"
      : ms >= 1000
        ? `${(ms / 1000).toFixed(1)}s`
        : `${Math.round(ms)}ms`;
  return (
    <tr style={{ cursor: "pointer" }} onClick={() => onOpen(row.id)}>
      <td
        className="mono"
        style={{ fontSize: 11, color: "var(--fg-3)", whiteSpace: "nowrap" }}
      >
        {fmtTs(row.createdAt)}
      </td>
      <td style={{ whiteSpace: "nowrap", fontSize: 12 }}>
        {row.apiKeyName || (row.apiKeyId ? row.apiKeyId.slice(0, 8) : "—")}
      </td>
      <td>
        <span className="tag" style={{ fontSize: 10 }}>
          {row.apiServiceName || "—"}
        </span>
      </td>
      <td>
        <span
          className="tag"
          style={{ fontSize: 10, background: "var(--bg-3)" }}
          title={
            row.canonicalModelId &&
            row.modelName &&
            row.canonicalModelId !== row.modelName
              ? `upstream: ${row.modelName}`
              : ""
          }
        >
          {displayModel}
        </span>
      </td>
      <td className="mono" style={{ textAlign: "right", fontSize: 11 }}>
        {row.inputTokens != null ? row.inputTokens.toLocaleString() : "—"}
      </td>
      <td className="mono" style={{ textAlign: "right", fontSize: 11 }}>
        {row.outputTokens != null ? row.outputTokens.toLocaleString() : "—"}
      </td>
      <td
        className="mono"
        style={{ textAlign: "right", fontSize: 11 }}
        title={
          cacheTotal > 0
            ? `read: ${row.cacheReadTokens || 0} tok\ncreate: ${row.cacheCreateTokens || 0} tok`
            : ""
        }
      >
        {cacheTotal > 0 ? cacheTotal.toLocaleString() : "—"}
      </td>
      <td
        className="mono"
        style={{ textAlign: "right", fontSize: 11, color: "var(--fg-3)" }}
      >
        {fmtMs(row.upstreamLatencyMs)}
      </td>
      <td className="mono" style={{ textAlign: "right", fontSize: 11 }}>
        <XPrim.TinyAmount value={parseFloat(row.cost || 0)} />
      </td>
      <td
        className="mono"
        style={{ textAlign: "right", fontSize: 11, color: "var(--fg-3)" }}
        title={
          speed == null
            ? row.isStreaming === false
              ? t("console.aiLogs.speed.nonStreamHint")
              : t("console.aiLogs.speed.noData")
            : ""
        }
      >
        {speed != null
          ? `${speed.toFixed ? speed.toFixed(1) : speed} tok/s`
          : "—"}
      </td>
      <td>
        <span
          className="mono"
          style={{
            fontSize: 11,
            color:
              sc < 300 ? "var(--ok)" : sc < 500 ? "var(--warn)" : "var(--err)",
          }}
        >
          {sc}
        </span>
        {row.errorOrigin && ORIGIN_STYLE[row.errorOrigin] && (
          <span
            className="tag"
            style={{
              fontSize: 9,
              marginLeft: 6,
              padding: "1px 5px",
              borderRadius: 3,
              background: ORIGIN_STYLE[row.errorOrigin].bg,
              color: ORIGIN_STYLE[row.errorOrigin].fg,
            }}
            title={[row.errorCode, row.errorMessage].filter(Boolean).join("\n")}
          >
            {t("console.aiLogs.origin." + row.errorOrigin)}
          </span>
        )}
        {row.isStreaming != null && (
          <span
            className="tag"
            style={{
              fontSize: 9,
              marginLeft: 6,
              padding: "1px 5px",
              borderRadius: 3,
              background: row.isStreaming
                ? "rgba(95,185,101,.14)"
                : "rgba(160,160,170,.12)",
              color: row.isStreaming ? "var(--ok)" : "var(--fg-4)",
            }}
            title={
              row.isStreaming
                ? t("console.aiLogs.tag.streamTitle")
                : t("console.aiLogs.tag.syncTitle")
            }
          >
            {row.isStreaming
              ? t("console.aiLogs.tag.stream")
              : t("console.aiLogs.tag.sync")}
          </span>
        )}
      </td>
    </tr>
  );
};

// 行详情 drawer（右滑）
const AiLogDetailDrawer = ({ id, data, loading, onClose }) => {
  const { t } = useT();
  // ESC 关闭
  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const Row = ({ label, value, mono = true }) => (
    <div
      className="row between"
      style={{ padding: "6px 0", borderBottom: "1px dashed var(--line)" }}
    >
      <div style={{ fontSize: 11, color: "var(--fg-4)" }}>{label}</div>
      <div
        className={mono ? "mono" : ""}
        style={{
          fontSize: 12,
          color: "var(--fg-1)",
          textAlign: "right",
          wordBreak: "break-all",
          maxWidth: 360,
        }}
      >
        {value ?? "—"}
      </div>
    </div>
  );

  const speed =
    data?.throughputTokPerSec != null
      ? `${data.throughputTokPerSec} tok/s`
      : data?.streamDurationMs && data?.outputTokens
        ? (data.outputTokens / (data.streamDurationMs / 1000)).toFixed(1) +
          " tok/s"
        : "—";

  // 完整 HAR 瀑布：网关 / DNS / TCP / TLS / 上游等待 / 生成 —— 用共享组件
  const upMs = data?.upstreamLatencyMs ?? null;
  const genMs = data?.streamDurationMs ?? null;
  const totalMs = data?.responseTime ?? null;
  const fmtMs = (ms) =>
    ms == null
      ? "—"
      : ms >= 1000
        ? `${(ms / 1000).toFixed(1)}s`
        : `${Math.round(ms)}ms`;

  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.4)",
          zIndex: 60,
        }}
      />
      <div
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          bottom: 0,
          width: "min(520px, 100vw)",
          background: "var(--bg-1)",
          borderLeft: "1px solid var(--line)",
          zIndex: 61,
          padding: 24,
          overflowY: "auto",
          boxShadow: "-8px 0 24px rgba(0,0,0,0.2)",
        }}
        role="dialog"
        aria-label={t("console.aiLogs.drawer.title")}
      >
        <div className="row between" style={{ marginBottom: 18 }}>
          <div className="micro">{t("console.aiLogs.drawer.title")}</div>
          <button className="btn btn-sm btn-ghost btn-icon" onClick={onClose}>
            <XIcons.IconX size={12} />
          </button>
        </div>
        {loading && (
          <div style={{ textAlign: "center", padding: 40 }}>
            <div className="spinner" />
          </div>
        )}
        {!loading && data?.__err && (
          <div style={{ color: "var(--err)", fontSize: 13 }}>{data.__err}</div>
        )}
        {!loading && data && !data.__err && (
          <>
            <Row
              label={t("console.aiLogs.col.time")}
              value={
                data.createdAt &&
                new Date(data.createdAt)
                  .toISOString()
                  .replace("T", " ")
                  .slice(0, 19)
              }
            />
            <Row
              label={t("console.aiLogs.col.apiKey")}
              value={data.apiKey?.name || data.apiKeyId}
            />
            <Row
              label={t("console.aiLogs.col.provider")}
              value={data.apiService?.name || "—"}
            />
            <Row
              label={t("console.aiLogs.col.model")}
              value={data.canonicalModelId || data.modelName}
            />
            {data.canonicalModelId &&
              data.modelName &&
              data.canonicalModelId !== data.modelName && (
                <Row
                  label={t("console.aiLogs.drawer.upstreamModel")}
                  value={data.modelName}
                />
              )}
            <Row
              label={t("console.aiLogs.drawer.endpoint")}
              value={
                data.endpoint
                  ? `${data.endpoint.method} ${data.endpoint.path}`
                  : "—"
              }
            />
            <div style={{ marginTop: 18, marginBottom: 8 }} className="micro">
              {t("console.aiLogs.drawer.tokens")}
            </div>
            <Row
              label={t("console.aiLogs.col.input")}
              value={data.inputTokens?.toLocaleString()}
            />
            <Row
              label={t("console.aiLogs.col.output")}
              value={data.outputTokens?.toLocaleString()}
            />
            <Row
              label={t("console.aiLogs.drawer.cacheRead")}
              value={data.cacheReadInputTokens?.toLocaleString() ?? 0}
            />
            <Row
              label={t("console.aiLogs.drawer.cacheCreate")}
              value={data.cacheCreationInputTokens?.toLocaleString() ?? 0}
            />
            <Row
              label={t("console.aiLogs.drawer.total")}
              value={data.totalTokens?.toLocaleString()}
            />
            <div style={{ marginTop: 18, marginBottom: 8 }} className="micro">
              {t("console.aiLogs.drawer.timing")}
            </div>
            <TimingWaterfall
              data={{
                gatewayMs: data.gatewayOverheadMs ?? null,
                dnsMs: data.dnsMs ?? null,
                tcpMs: data.tcpMs ?? null,
                tlsMs: data.tlsMs ?? null,
                // Upstream wait = TTFB - connect 那段；keep-alive 复用时 connect=null → 整个 upstream 都是 wait
                waitMs:
                  data.upstreamLatencyMs != null
                    ? Math.max(
                        data.upstreamLatencyMs - (data.connectMs ?? 0),
                        0,
                      )
                    : null,
                generationMs: genMs,
                totalMs,
                reusedConnection: data.reusedConnection,
                isStreaming: data.isStreaming,
                note: data.outputTokens
                  ? `${data.outputTokens} tok · ${speed}`
                  : "",
              }}
              labels={{
                gateway: t("console.aiLogs.drawer.gatewayOverhead"),
                wait: t("console.aiLogs.drawer.wait"),
                generation: t("console.aiLogs.drawer.generation"),
                reusedConn: t("console.aiLogs.drawer.reusedConn"),
                nonStream: t("console.aiLogs.drawer.nonStream"),
              }}
            />
            <Row
              label={t("console.aiLogs.drawer.gatewayOverhead")}
              value={fmtMs(data.gatewayOverheadMs)}
            />
            <Row
              label={t("console.aiLogs.drawer.connect")}
              value={
                data.connectMs != null
                  ? fmtMs(data.connectMs)
                  : data.reusedConnection
                    ? t("console.aiLogs.drawer.reused")
                    : "—"
              }
            />
            <Row
              label={t("console.aiLogs.drawer.providerLatency")}
              value={fmtMs(upMs)}
            />
            <Row
              label={t("console.aiLogs.drawer.generation")}
              value={fmtMs(genMs)}
            />
            <Row
              label={t("console.aiLogs.drawer.responseTime")}
              value={fmtMs(totalMs)}
            />
            <Row label={t("console.aiLogs.col.speed")} value={speed} />
            <div style={{ marginTop: 18, marginBottom: 8 }} className="micro">
              {t("console.aiLogs.drawer.billing")}
            </div>
            <Row
              label={t("console.aiLogs.col.cost")}
              value={`$${parseFloat(data.cost || 0).toFixed(6)}`}
            />
            <Row
              label={t("console.aiLogs.col.status")}
              value={String(data.statusCode)}
            />
            {(data.errorOrigin || data.statusCode >= 400) && (
              <>
                <div
                  style={{
                    marginTop: 18,
                    marginBottom: 8,
                    color: "var(--err)",
                  }}
                  className="micro"
                >
                  {t("console.aiLogs.drawer.error")}
                </div>
                {data.errorOrigin && (
                  <Row
                    label={t("console.aiLogs.drawer.errorOrigin")}
                    value={
                      <span
                        style={{
                          color: (ORIGIN_STYLE[data.errorOrigin] || {}).fg,
                        }}
                      >
                        {t("console.aiLogs.origin." + data.errorOrigin)}
                      </span>
                    }
                    mono={false}
                  />
                )}
                {data.errorCode && (
                  <Row
                    label={t("console.aiLogs.drawer.errorCode")}
                    value={data.errorCode}
                  />
                )}
                {data.errorMessage && (
                  <Row
                    label={t("console.aiLogs.drawer.errorMessage")}
                    value={data.errorMessage}
                    mono={false}
                  />
                )}
              </>
            )}
            <div style={{ marginTop: 18, marginBottom: 8 }} className="micro">
              {t("console.aiLogs.drawer.request")}
            </div>
            <Row label="URL" value={data.requestUrl} mono={false} />
          </>
        )}
      </div>
    </>
  );
};

// ============================================================================
// Tasks tab — async tasks list scoped to the current logged-in user.
//
// Backed by `/user/tasks` (api app, JWT-authed). The list is NOT filtered by a
// specific API key — every signed-in user sees every task they "own":
//   • Virtual users typically own a single API key, so they see only their
//     own task history.
//   • Entity users may own multiple API keys (different integrations), so they
//     see the union across every one of their keys.
// The proxy app's `/v1/tasks` endpoint still exists for the Try-It panel which
// already has an apiKey in hand, but the dashboard doesn't depend on it.
//
// Auto-refresh: while any task in the visible page is in pending/processing,
// we re-poll the list every 5s. Once everything is finalized, we stop. The
// document.visibility listener pauses when the tab is hidden so background
// users don't keep traffic flowing.
//
// Detail drawer: clicking a row opens a portal'd drawer that calls
// `/user/tasks/:id` for full result + signed URL.
// ============================================================================

// Cost cell for a task. While a task is in flight (pending/processing) the only
// number we have is the reserved hold ($5) — but that's NOT the cost, just a
// temporary reservation released to the actual usage on completion. Showing it
// as "Cost" is misleading, so in-flight rows show "on completion" instead (with
// the held amount available on hover). Finalized rows show the real final_cost.
const TaskCostCell = ({ tk, t }) => {
  const inFlight = tk.status === "pending" || tk.status === "processing";
  const finalCost = tk.final_cost != null ? Number(tk.final_cost) : 0;
  const reserved = tk.reserved_cost != null ? Number(tk.reserved_cost) : 0;
  if (inFlight) {
    return (
      <span
        style={{ color: "var(--fg-4)" }}
        title={
          reserved > 0
            ? t("console.tasks.cost.heldHint").replace(
                "${amount}",
                "$" + reserved.toFixed(2),
              )
            : undefined
        }
      >
        {t("console.tasks.cost.pending")}
      </span>
    );
  }
  return finalCost > 0 ? (
    <XPrim.TinyAmount value={finalCost} />
  ) : (
    <span style={{ color: "var(--fg-4)" }}>—</span>
  );
};

const TasksTab = () => {
  const { t } = useT();
  const { authed } = XShell.useAuth();
  const { route, navigate } = XShell.useRoute();
  const { toast } = XPrim.useToast();

  const [filter, setFilter] = ucS("all"); // all | inProgress | completed | failed
  const [tasks, setTasks] = ucS([]);
  const [cursor, setCursor] = ucS(null);
  const [hasMore, setHasMore] = ucS(false);
  const [loading, setLoading] = ucS(true);
  const [err, setErr] = ucS(null);
  const PAGE = 20;

  // Detail drawer state — opening a row triggers a fresh poll so signed URLs
  // come back live (poll regenerates them every call).
  const [openTaskId, setOpenTaskId] = ucS(null);
  const [openTaskData, setOpenTaskData] = ucS(null);
  const [openTaskLoading, setOpenTaskLoading] = ucS(false);

  // Map UI filter chip → backend status param.
  const filterToStatuses = (f) => {
    if (f === "inProgress") return "pending,processing";
    if (f === "completed") return "succeeded";
    if (f === "failed") return "failed,expired";
    return undefined;
  };

  const fetchPage = React.useCallback(
    async (opts = {}) => {
      if (!authed) return;
      const append = !!opts.append;
      if (!append) setLoading(true);
      setErr(null);
      try {
        const res = await XApi.taskService.listMine({
          status: filterToStatuses(filter),
          limit: PAGE,
          cursor: append ? cursor : undefined,
        });
        const items = Array.isArray(res.tasks) ? res.tasks : [];
        setTasks((prev) => (append ? prev.concat(items) : items));
        setCursor(res.next_cursor || null);
        setHasMore(!!res.has_more);
      } catch (e) {
        setErr(e.message || "Failed to load tasks");
      } finally {
        if (!append) setLoading(false);
      }
    },
    [authed, filter, cursor],
  );

  // Initial + filter-change fetch.
  React.useEffect(() => {
    if (!authed) return;
    setCursor(null);
    setTasks([]);
    fetchPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed, filter]);

  // Auto-refresh loop while any visible task is in pending/processing. Pauses
  // when the document is hidden.
  const hasInFlight = tasks.some(
    (x) => x.status === "pending" || x.status === "processing",
  );
  React.useEffect(() => {
    if (!hasInFlight) return;
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      if (typeof document !== "undefined" && document.hidden) return;
      try {
        const res = await XApi.taskService.listMine({
          status: filterToStatuses(filter),
          limit: tasks.length || PAGE,
        });
        if (!cancelled && Array.isArray(res.tasks)) setTasks(res.tasks);
      } catch {}
    };
    const id = setInterval(tick, 5000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [hasInFlight, filter, tasks.length]);

  // Open the detail drawer when ?taskId is present (from Try-It link).
  React.useEffect(() => {
    const tid = route.params && route.params.taskId;
    if (tid && tid !== openTaskId) setOpenTaskId(tid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [route.params && route.params.taskId]);

  // Detail fetch.
  React.useEffect(() => {
    if (!openTaskId || !authed) {
      setOpenTaskData(null);
      return;
    }
    let cancelled = false;
    setOpenTaskLoading(true);
    setOpenTaskData(null);
    XApi.taskService
      .pollMine(openTaskId)
      .then((d) => {
        if (!cancelled) setOpenTaskData(d);
      })
      .catch((e) => {
        if (!cancelled) toast(e.message || "Failed to load task");
      })
      .finally(() => {
        if (!cancelled) setOpenTaskLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openTaskId, authed]);

  // Auto-refresh open task while in flight.
  React.useEffect(() => {
    if (!openTaskData) return;
    if (
      openTaskData.status !== "pending" &&
      openTaskData.status !== "processing"
    )
      return;
    const id = setTimeout(async () => {
      try {
        const d = await XApi.taskService.pollMine(openTaskId);
        setOpenTaskData(d);
      } catch {}
    }, 2500);
    return () => clearTimeout(id);
  }, [openTaskData, openTaskId]);

  const closeDrawer = () => {
    setOpenTaskId(null);
    setOpenTaskData(null);
    // Strip taskId from URL so refresh doesn't re-open.
    if (route.params && route.params.taskId) {
      const { taskId: _drop, ...rest } = route.params;
      navigate("console", rest);
    }
  };

  const fmtDuration = (createdIso, completedIso) => {
    if (!createdIso) return "—";
    const start = new Date(createdIso).getTime();
    const end = completedIso ? new Date(completedIso).getTime() : Date.now();
    const ms = Math.max(0, end - start);
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
    if (ms < 3_600_000)
      return `${Math.floor(ms / 60_000)}m ${Math.floor((ms % 60_000) / 1000)}s`;
    return `${Math.floor(ms / 3_600_000)}h ${Math.floor((ms % 3_600_000) / 60_000)}m`;
  };

  const statusColor = (s) => {
    if (s === "succeeded") return "var(--ok)";
    if (s === "failed" || s === "expired") return "var(--err)";
    if (s === "processing") return "var(--c-mint)";
    return "var(--fg-3)";
  };

  if (!authed)
    return (
      <>
        <TabHeader
          title={t("console.tasks.title")}
          subtitle={t("console.tasks.signinSubtitle")}
        />
      </>
    );

  return (
    <>
      <TabHeader
        title={t("console.tasks.title")}
        subtitle={t("console.tasks.subtitle")}
        actions={
          <button
            className="btn btn-sm"
            onClick={() => fetchPage()}
            disabled={loading}
          >
            <XIcons.IconRefresh size={11} /> {t("console.tasks.refresh")}
          </button>
        }
      />
      {/* List is JWT-scoped — every signed-in user sees their own tasks across
          all keys. No XAPI_KEY env-var requirement, no per-key gating. */}
      {
        <>
          {/* Toolbar: filter chips + key indicator */}
          <div
            className="row gap-2 console-toolbar"
            style={{ marginBottom: 14, flexWrap: "wrap", alignItems: "center" }}
          >
            <div className="row gap-1" style={{ flexWrap: "wrap" }}>
              {[
                { k: "all", l: t("console.tasks.filter.all") },
                { k: "inProgress", l: t("console.tasks.filter.inProgress") },
                { k: "completed", l: t("console.tasks.filter.completed") },
                { k: "failed", l: t("console.tasks.filter.failed") },
              ].map((c) => (
                <button
                  key={c.k}
                  onClick={() => setFilter(c.k)}
                  className={`btn btn-sm ${filter === c.k ? "btn-primary" : ""}`}
                >
                  {c.l}
                </button>
              ))}
            </div>
            <div className="grow" />
            {hasInFlight && (
              <span
                className="micro-mono"
                style={{
                  textTransform: "none",
                  letterSpacing: 0,
                  color: "var(--c-mint)",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <div className="spinner" style={{ width: 10, height: 10 }} />
                {t("console.tasks.autoRefresh")}
              </span>
            )}
          </div>

          {err && (
            <div
              className="card"
              style={{ padding: 14, color: "var(--err)", fontSize: 13 }}
            >
              {err}
            </div>
          )}

          <div className="card">
            {loading && tasks.length === 0 && (
              <div style={{ padding: 32, textAlign: "center" }}>
                <div className="spinner" />{" "}
                <span
                  style={{ marginLeft: 10, color: "var(--fg-3)", fontSize: 13 }}
                >
                  Loading…
                </span>
              </div>
            )}
            {!loading && tasks.length === 0 && !err && (
              <div
                style={{
                  padding: 40,
                  textAlign: "center",
                  color: "var(--fg-3)",
                }}
              >
                <XIcons.IconZap
                  size={20}
                  style={{ color: "var(--fg-4)", marginBottom: 10 }}
                />
                <div style={{ fontSize: 14, color: "var(--fg-2)" }}>
                  {t("console.tasks.empty")}
                </div>
                <div
                  className="micro-mono"
                  style={{
                    textTransform: "none",
                    letterSpacing: 0,
                    marginTop: 6,
                    color: "var(--fg-4)",
                  }}
                >
                  {t("console.tasks.emptyHint")}
                </div>
              </div>
            )}
            {tasks.length > 0 && (
              <>
                {/* Desktop: table layout. Mobile: stacked cards (CSS swaps below 720px). */}
                <div className="tasks-table-wrap table-scroll">
                  <table className="table tasks-table">
                    <thead>
                      <tr>
                        <th>{t("console.tasks.col.status")}</th>
                        <th>{t("console.tasks.col.task")}</th>
                        <th>{t("console.tasks.col.endpoint")}</th>
                        <th>{t("console.tasks.col.key")}</th>
                        <th>{t("console.tasks.col.created")}</th>
                        <th>{t("console.tasks.col.duration")}</th>
                        <th style={{ textAlign: "right" }}>
                          {t("console.tasks.col.cost")}
                        </th>
                        <th style={{ width: 28 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {tasks.map((tk) => {
                        const finalized = [
                          "succeeded",
                          "failed",
                          "expired",
                        ].includes(tk.status);
                        const completedRef = finalized
                          ? tk.completed_at || tk.expires_at
                          : null;
                        return (
                          <tr
                            key={tk.task_id}
                            className="tasks-row"
                            onClick={() => setOpenTaskId(tk.task_id)}
                            style={{ cursor: "pointer" }}
                          >
                            <td style={{ whiteSpace: "nowrap" }}>
                              <span
                                className="row gap-2"
                                style={{ alignItems: "center" }}
                              >
                                <span
                                  style={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: "50%",
                                    background: statusColor(tk.status),
                                    boxShadow:
                                      tk.status === "processing"
                                        ? "0 0 8px var(--c-mint)"
                                        : "none",
                                    flexShrink: 0,
                                  }}
                                />
                                <span
                                  className="mono"
                                  style={{
                                    fontSize: 11,
                                    color: statusColor(tk.status),
                                  }}
                                >
                                  {t(`console.tasks.status.${tk.status}`)}
                                </span>
                              </span>
                            </td>
                            <td
                              className="mono"
                              style={{
                                fontSize: 11,
                                color: "var(--fg-2)",
                                whiteSpace: "nowrap",
                              }}
                              title={tk.task_id}
                            >
                              {tk.task_id.slice(0, 8)}…{tk.task_id.slice(-4)}
                            </td>
                            <td style={{ minWidth: 0, maxWidth: 320 }}>
                              <div
                                style={{
                                  fontSize: 12,
                                  color: "var(--fg-1)",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {tk.endpoint_name || "—"}
                              </div>
                              <div
                                className="mono"
                                style={{
                                  fontSize: 10,
                                  color: "var(--fg-4)",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  marginTop: 2,
                                }}
                              >
                                {tk.api_service_name
                                  ? tk.api_service_name + " · "
                                  : ""}
                                {tk.method || ""} {tk.request_path || ""}
                              </div>
                            </td>
                            <td
                              style={{
                                minWidth: 0,
                                maxWidth: 200,
                                whiteSpace: "nowrap",
                              }}
                            >
                              <div
                                style={{
                                  fontSize: 12,
                                  color: "var(--fg-2)",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {tk.api_key_name || "—"}
                              </div>
                              {(tk.api_key_prefix || tk.api_key_suffix) && (
                                <div
                                  className="mono"
                                  style={{
                                    fontSize: 10,
                                    color: "var(--fg-4)",
                                    marginTop: 2,
                                  }}
                                >
                                  {tk.api_key_prefix || ""}…
                                  {tk.api_key_suffix || ""}
                                </div>
                              )}
                            </td>
                            <td
                              className="mono"
                              style={{
                                fontSize: 11,
                                color: "var(--fg-3)",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {XApi.helpers.formatRelative(tk.created_at)}
                            </td>
                            <td
                              className="mono"
                              style={{
                                fontSize: 11,
                                color: "var(--fg-3)",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {fmtDuration(tk.created_at, completedRef)}
                            </td>
                            <td
                              className="mono"
                              style={{
                                fontSize: 11,
                                textAlign: "right",
                                whiteSpace: "nowrap",
                              }}
                            >
                              <TaskCostCell tk={tk} t={t} />
                            </td>
                            <td style={{ textAlign: "right" }}>
                              <XIcons.IconChevronRight
                                size={11}
                                style={{ color: "var(--fg-4)" }}
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {/* Mobile cards — only renders below 720px via CSS */}
                <div className="tasks-cards">
                  {tasks.map((tk) => {
                    const finalized = [
                      "succeeded",
                      "failed",
                      "expired",
                    ].includes(tk.status);
                    const completedRef = finalized
                      ? tk.completed_at || tk.expires_at
                      : null;
                    return (
                      <button
                        key={tk.task_id}
                        onClick={() => setOpenTaskId(tk.task_id)}
                        className="tasks-card"
                      >
                        <div
                          className="row between"
                          style={{ alignItems: "center", marginBottom: 6 }}
                        >
                          <span
                            className="row gap-2"
                            style={{ alignItems: "center" }}
                          >
                            <span
                              style={{
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                background: statusColor(tk.status),
                                flexShrink: 0,
                                boxShadow:
                                  tk.status === "processing"
                                    ? "0 0 8px var(--c-mint)"
                                    : "none",
                              }}
                            />
                            <span
                              className="mono"
                              style={{
                                fontSize: 11,
                                color: statusColor(tk.status),
                              }}
                            >
                              {t(`console.tasks.status.${tk.status}`)}
                            </span>
                          </span>
                          <span
                            className="mono"
                            style={{ fontSize: 10, color: "var(--fg-4)" }}
                          >
                            {tk.task_id.slice(0, 8)}…{tk.task_id.slice(-4)}
                          </span>
                        </div>
                        <div
                          style={{
                            fontSize: 13,
                            color: "var(--fg-1)",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {tk.endpoint_name || "—"}
                        </div>
                        <div
                          className="mono"
                          style={{
                            fontSize: 10,
                            color: "var(--fg-4)",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            marginTop: 2,
                          }}
                        >
                          {tk.api_service_name
                            ? tk.api_service_name + " · "
                            : ""}
                          {tk.method || ""} {tk.request_path || ""}
                        </div>
                        {tk.api_key_name && (
                          <div
                            className="row gap-2"
                            style={{
                              marginTop: 6,
                              fontSize: 11,
                              color: "var(--fg-3)",
                              alignItems: "center",
                            }}
                          >
                            <XIcons.IconKey size={10} />
                            <span
                              style={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                minWidth: 0,
                                flex: 1,
                              }}
                            >
                              {tk.api_key_name}
                            </span>
                            {(tk.api_key_prefix || tk.api_key_suffix) && (
                              <span
                                className="mono"
                                style={{ color: "var(--fg-4)", fontSize: 10 }}
                              >
                                {tk.api_key_prefix || ""}…
                                {tk.api_key_suffix || ""}
                              </span>
                            )}
                          </div>
                        )}
                        <div
                          className="row between"
                          style={{
                            marginTop: 8,
                            fontSize: 11,
                            color: "var(--fg-3)",
                          }}
                        >
                          <span className="mono">
                            {XApi.helpers.formatRelative(tk.created_at)} ·{" "}
                            {fmtDuration(tk.created_at, completedRef)}
                          </span>
                          <span className="mono">
                            <TaskCostCell tk={tk} t={t} />
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
                {hasMore && (
                  <button
                    className="btn btn-sm"
                    style={{ margin: 12, justifyContent: "center" }}
                    onClick={() => fetchPage({ append: true })}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <div className="spinner" /> Loading…
                      </>
                    ) : (
                      t("console.tasks.loadMore")
                    )}
                  </button>
                )}
              </>
            )}
          </div>

          {/* Detail drawer */}
          {openTaskId &&
            typeof document !== "undefined" &&
            createPortal(
              <>
                <div className="mobile-sheet-backdrop" onClick={closeDrawer} />
                <div
                  className="task-detail-drawer"
                  role="dialog"
                  aria-label="Task detail"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div
                    className="row between"
                    style={{
                      padding: "14px 18px",
                      borderBottom: "1px solid var(--line)",
                    }}
                  >
                    <div className="micro">{t("console.tasks.title")}</div>
                    <button
                      onClick={closeDrawer}
                      className="btn btn-sm btn-ghost btn-icon"
                      aria-label="Close"
                    >
                      <XIcons.IconX size={12} />
                    </button>
                  </div>
                  <div style={{ padding: 18, overflowY: "auto", flex: 1 }}>
                    {openTaskLoading && (
                      <div style={{ padding: 20, textAlign: "center" }}>
                        <div className="spinner" />
                        <span
                          style={{
                            marginLeft: 10,
                            color: "var(--fg-3)",
                            fontSize: 13,
                          }}
                        >
                          Loading…
                        </span>
                      </div>
                    )}
                    {!openTaskLoading && !openTaskData && (
                      <div
                        style={{
                          padding: 20,
                          fontSize: 13,
                          color: "var(--fg-3)",
                          textAlign: "center",
                        }}
                      >
                        Task not found.
                      </div>
                    )}
                    {openTaskData &&
                      (() => {
                        const td = openTaskData;
                        // Poll response only carries id/status/timing/result. Endpoint, service,
                        // method, path, and cost live on the list row — merge them here so the
                        // drawer can show the full picture without a second backend call.
                        const row =
                          tasks.find((x) => x.task_id === td.task_id) || {};
                        const finalized = [
                          "succeeded",
                          "failed",
                          "expired",
                        ].includes(td.status);
                        const completedRef = finalized
                          ? row.completed_at || td.expires_at
                          : null;
                        const isPolling =
                          td.status === "pending" || td.status === "processing";
                        return (
                          <div className="col gap-3">
                            <div>
                              <div
                                className="micro"
                                style={{ marginBottom: 4 }}
                              >
                                Task id
                              </div>
                              <div
                                className="row gap-2"
                                style={{ alignItems: "center" }}
                              >
                                <span
                                  className="mono"
                                  style={{
                                    fontSize: 12,
                                    wordBreak: "break-all",
                                    flex: 1,
                                    minWidth: 0,
                                  }}
                                >
                                  {td.task_id}
                                </span>
                                <button
                                  className="btn btn-sm btn-ghost btn-icon"
                                  onClick={() => {
                                    try {
                                      navigator.clipboard.writeText(td.task_id);
                                      toast("Copied");
                                    } catch {}
                                  }}
                                  title="Copy"
                                >
                                  <XIcons.IconCopy size={11} />
                                </button>
                              </div>
                            </div>
                            <div
                              className="row gap-2"
                              style={{ alignItems: "center", flexWrap: "wrap" }}
                            >
                              {isPolling ? (
                                <span
                                  className="tag"
                                  style={{
                                    color: "var(--c-amber)",
                                    background: "var(--c-amber-bg)",
                                    borderColor:
                                      "color-mix(in oklab, var(--c-amber) 40%, transparent)",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 6,
                                  }}
                                >
                                  <span
                                    style={{
                                      width: 7,
                                      height: 7,
                                      borderRadius: "50%",
                                      background: "var(--c-amber)",
                                      boxShadow: "0 0 8px var(--c-amber)",
                                      animation:
                                        "pulse-dot 1.2s ease-in-out infinite",
                                    }}
                                  />
                                  {t(`console.tasks.status.${td.status}`)}
                                </span>
                              ) : (
                                <span
                                  className="tag mono"
                                  style={{ color: statusColor(td.status) }}
                                >
                                  {t(`console.tasks.status.${td.status}`)}
                                </span>
                              )}
                              {isPolling && (
                                <span
                                  className="row gap-2"
                                  style={{
                                    alignItems: "center",
                                    color: "var(--c-amber)",
                                    fontSize: 12,
                                    fontWeight: 500,
                                  }}
                                >
                                  <div
                                    className="spinner"
                                    style={{
                                      width: 12,
                                      height: 12,
                                      borderColor:
                                        "color-mix(in oklab, var(--c-amber) 30%, transparent)",
                                      borderTopColor: "var(--c-amber)",
                                    }}
                                  />
                                  {t("apidetail.tryit.async.polling")}
                                </span>
                              )}
                            </div>
                            <div>
                              <div
                                className="micro"
                                style={{ marginBottom: 6 }}
                              >
                                {t("console.tasks.detail.calling")}
                              </div>
                              {row.endpoint_name ||
                              row.api_service_name ||
                              row.method ||
                              row.request_path ? (
                                <div
                                  style={{
                                    fontSize: 13,
                                    color: "var(--fg-1)",
                                    lineHeight: 1.5,
                                  }}
                                >
                                  {row.endpoint_name && (
                                    <div style={{ marginBottom: 2 }}>
                                      {row.endpoint_name}
                                    </div>
                                  )}
                                  <div
                                    className="mono"
                                    style={{
                                      fontSize: 11,
                                      color: "var(--fg-3)",
                                      wordBreak: "break-all",
                                    }}
                                  >
                                    {row.api_service_name
                                      ? row.api_service_name + " · "
                                      : ""}
                                    {row.method ? (
                                      <span
                                        style={{
                                          color: "var(--fg-2)",
                                          fontWeight: 500,
                                        }}
                                      >
                                        {row.method}
                                      </span>
                                    ) : null}
                                    {row.method && row.request_path ? " " : ""}
                                    {row.request_path || ""}
                                  </div>
                                </div>
                              ) : (
                                <div
                                  className="mono"
                                  style={{ fontSize: 11, color: "var(--fg-4)" }}
                                >
                                  —
                                </div>
                              )}
                            </div>
                            <div>
                              <div
                                className="micro"
                                style={{ marginBottom: 6 }}
                              >
                                {t("console.tasks.detail.cost")}
                              </div>
                              <div
                                className="mono"
                                style={{ fontSize: 12, color: "var(--fg-2)" }}
                              >
                                <TaskCostCell
                                  tk={{ ...row, status: td.status }}
                                  t={t}
                                />
                              </div>
                            </div>
                            <div>
                              <div
                                className="micro"
                                style={{ marginBottom: 6 }}
                              >
                                {t("console.tasks.detail.timing")}
                              </div>
                              <div
                                className="mono"
                                style={{
                                  fontSize: 11,
                                  color: "var(--fg-3)",
                                  lineHeight: 1.8,
                                }}
                              >
                                <div>created: {td.created_at}</div>
                                <div>expires: {td.expires_at}</div>
                                <div>
                                  duration:{" "}
                                  {fmtDuration(td.created_at, completedRef)}
                                </div>
                              </div>
                            </div>
                            <div>
                              <div
                                className="micro"
                                style={{ marginBottom: 6 }}
                              >
                                {t("console.tasks.detail.result")}
                              </div>
                              {isPolling ? (
                                <div
                                  className="micro-mono"
                                  style={{
                                    textTransform: "none",
                                    letterSpacing: 0,
                                    color: "var(--fg-3)",
                                  }}
                                >
                                  Waiting for upstream…
                                </div>
                              ) : (
                                <TaskResultView
                                  result={td.result}
                                  error={td.error}
                                  status={td.status}
                                />
                              )}
                            </div>
                          </div>
                        );
                      })()}
                  </div>
                </div>
              </>,
              document.body,
            )}
        </>
      }
    </>
  );
};


const B402_TOKEN_LABELS = {
  '0xce24439f2d9c6a2289f741120fe202248b666666': 'U',
  '0x8d0d000ee44948fc98c9b98a4fa4921476f08b0d': 'USD1',
  '0x55d398326f99059ff775485246999027b3197955': 'USDT',
  '0x8ac76a51cc950d9822d68b83fe1ad97b32cd580d': 'USDC',
};

const formatBillingDescription = (row) => {
  const raw = row.description || row.note || '';
  const m = raw.match(/^Top-up via (b402|x402):\s*\$([0-9.]+)\s*\(([^)]+)\)/i);
  if (!m) return { title: raw || '—', meta: '' };

  const provider = m[1].toUpperCase();
  const amount = `$${Number(m[2]).toFixed(2)}`;
  const assetRaw = m[3];
  const asset = B402_TOKEN_LABELS[String(assetRaw).toLowerCase()] || assetRaw;
  const network = provider === 'B402' ? 'BSC stablecoin' : 'Base USDC';

  return {
    title: `Top-up via ${provider} · ${asset}`,
    meta: `${amount} · ${network}`,
  };
};

const BillingDescription = ({ row }) => {
  const desc = formatBillingDescription(row);
  return (
    <div>
      <div style={{fontSize: 13, color:'var(--fg-1)', fontWeight: 500, lineHeight: 1.35}}>{desc.title}</div>
      {desc.meta && (
        <div className="micro-mono" style={{marginTop: 4, color:'var(--fg-3)', letterSpacing: 0, textTransform:'none'}}>
          {desc.meta}
        </div>
      )}
    </div>
  );
};

// ============ Sandbox Instances Tab ============
// 消费者的实例管理：读走 JWT（/sandbox-consumer/*，实例/日志/时间线/计费段），
// 写走 gateway（挂起/恢复/销毁/SSH/端口必须带 xapi-key —— key 库里是哈希，
// console 拿不到明文，所以沿用 /sandbox 页的模式让用户粘一次，共用同一个
// sessionStorage 槽位，两边只需连一次）。
const SB_KEY_STORAGE = "xapi.sandbox.session-key";
const SB_BASE = SHARED_SANDBOX_BASE;
const sbGateway = async (path, apiKey, init = {}) => {
  const headers = { accept: "application/json", authorization: `Bearer ${apiKey}` };
  if (init.body) headers["content-type"] = "application/json";
  const res = await fetch(`${SB_BASE}${path}`, { ...init, headers });
  const text = await res.text();
  let body = null; try { body = text ? JSON.parse(text) : null; } catch { body = text; }
  if (!res.ok) {
    const raw = body?.message ?? body?.error ?? `HTTP ${res.status}`;
    throw new Error(Array.isArray(raw) ? raw.join("；") : String(raw));
  }
  return body;
};
const sbMoney = (v) => {
  const n = Number(v || 0);
  return Number.isFinite(n) ? `$${n.toFixed(6).replace(/0+$/, "").replace(/\.$/, "") || "0"}` : "—";
};
// 表格里用紧凑格式：完整 locale 串（"8/10/2026, 10:31:15 PM"）在这么多列的
// 表里必然被挤断，读者看到的是 "8/10/202" 这种半截日期。展开区仍用完整时间。
const sbTime = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getMonth() + 1}/${d.getDate()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const sbTimeFull = (v) => (v ? new Date(v).toLocaleString() : "—");
const SB_STATE_STYLE = {
  RUNNING: { color: "#52d3ad", label: "运行中" },
  SUSPENDED: { color: "#e8b34b", label: "已挂起" },
  PROVISIONING: { color: "#7aa5ff", label: "创建中" },
  TERMINATED: { color: "var(--fg-4)", label: "已销毁" },
  FAILED: { color: "#e06b5e", label: "失败" },
};
const SbStateBadge = ({ state }) => {
  const meta = SB_STATE_STYLE[state] || { color: "var(--fg-3)", label: state };
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: meta.color }}>
      <span style={{ width: 7, height: 7, borderRadius: "50%", background: meta.color }} />
      {meta.label}
    </span>
  );
};
// 数据面调用（用户代码真正跑过什么）与控制面（生命周期）分色。
const SB_OP_LABEL = {
  CREATE: "创建", SUSPEND: "挂起", RESUME: "恢复", TERMINATE: "销毁",
  EXTENSION: "扩展", EXEC: "执行命令", FILE_READ: "读文件", FILE_WRITE: "写文件",
  FILE_LIST: "列目录", PORT_OPEN: "开端口",
};
const SB_DATA_PLANE = new Set(["EXEC", "FILE_READ", "FILE_WRITE", "FILE_LIST", "PORT_OPEN"]);

const SbOpRow = ({ op, showInstance }) => {
  const [open, setOpen] = ucS(false);
  const hasBody = op.stdoutPreview || op.stderrPreview || op.error || op.input?.command;
  const dataPlane = SB_DATA_PLANE.has(op.type);
  return (
    <div style={{ borderBottom: "1px solid var(--line, rgba(255,255,255,0.06))", padding: "7px 0" }}>
      <div
        style={{ display: "flex", gap: 10, alignItems: "center", cursor: hasBody ? "pointer" : "default", fontSize: 12 }}
        onClick={() => hasBody && setOpen((v) => !v)}
      >
        <span className="mono" style={{
          padding: "1px 7px", borderRadius: 4, fontSize: 11, flexShrink: 0,
          background: dataPlane ? "rgba(122,165,255,0.12)" : "rgba(255,255,255,0.06)",
          color: dataPlane ? "#7aa5ff" : "var(--fg-2)",
        }}>{SB_OP_LABEL[op.type] || op.type}</span>
        <span style={{ color: op.status === "FAILED" ? "#e06b5e" : "var(--fg-3)", flexShrink: 0 }}>
          {op.status === "SUCCEEDED" ? "成功" : op.status === "FAILED" ? "失败" : op.status}
        </span>
        {op.exitCode !== undefined && op.exitCode !== null && (
          <span className="mono" style={{ color: op.exitCode === 0 ? "var(--fg-3)" : "#e06b5e", flexShrink: 0 }}>exit={op.exitCode}</span>
        )}
        {op.durationMs !== undefined && op.durationMs !== null && (
          <span style={{ color: "var(--fg-4)", flexShrink: 0 }}>{op.durationMs}ms</span>
        )}
        {showInstance && op.serviceName && (
          <span style={{ color: "var(--fg-4)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{op.serviceName}</span>
        )}
        <span style={{ marginLeft: "auto", color: "var(--fg-4)", flexShrink: 0 }}>{sbTimeFull(op.requestedAt)}</span>
      </div>
      {open && hasBody && (
        <div style={{ margin: "6px 0 2px 0", fontSize: 12 }}>
          {op.input?.command && (
            <pre className="mono" style={{ margin: "4px 0", padding: "6px 10px", background: "rgba(255,255,255,0.04)", borderRadius: 6, overflowX: "auto", color: "var(--fg-2)" }}>$ {op.input.command}</pre>
          )}
          {op.stdoutPreview && (
            <pre className="mono" style={{ margin: "4px 0", padding: "6px 10px", background: "rgba(0,0,0,0.35)", borderRadius: 6, overflowX: "auto", color: "var(--fg-2)", whiteSpace: "pre-wrap" }}>{op.stdoutPreview}</pre>
          )}
          {op.stderrPreview && (
            <pre className="mono" style={{ margin: "4px 0", padding: "6px 10px", background: "rgba(224,107,94,0.08)", borderRadius: 6, overflowX: "auto", color: "#e06b5e", whiteSpace: "pre-wrap" }}>{op.stderrPreview}</pre>
          )}
          {op.outputTruncated && <div style={{ color: "var(--fg-4)" }}>· 输出已截断，仅保留头部</div>}
          {op.error && <div style={{ color: "#e06b5e" }}>错误：{op.error}</div>}
        </div>
      )}
    </div>
  );
};

// SSH 面板：只在 offering 声明了 connection_info 扩展时渲染。IP 是 RUNNING 后
// 约 50 秒异步分配的，所以 connectionReady=false 时自动每 5 秒重试。
const SbSshPanel = ({ instance, gwKey }) => {
  const [state, setState] = ucS({ phase: "idle" });
  React.useEffect(() => {
    if (state.phase !== "polling") return undefined;
    let cancelled = false;
    const tick = async () => {
      try {
        const res = await sbGateway(
          `/v1/sandboxes/${instance.id}/extensions/runpod.connection_info`,
          gwKey, { method: "POST", body: JSON.stringify({ input: {} }) },
        );
        // gateway 包一层 {operation,result}，result 又是 adapter 信封 {operationId,result}
        const payload = res?.result?.result || {};
        if (cancelled) return;
        if (payload.connectionReady) setState({ phase: "ready", data: payload });
        else setTimeout(() => !cancelled && tick(), 5000);
      } catch (e) {
        if (!cancelled) setState({ phase: "error", error: e.message });
      }
    };
    tick();
    return () => { cancelled = true; };
  }, [state.phase, instance.id, gwKey]);
  const mapped = state.data?.portMappings?.["22"];
  const sshCmd = state.data?.publicIp && mapped ? `ssh root@${state.data.publicIp} -p ${mapped}` : null;
  return (
    <div style={{ marginTop: 8 }}>
      {state.phase === "idle" && (
        <button type="button" className="btn btn-sm" disabled={!gwKey} onClick={() => setState({ phase: "polling" })}>
          获取 SSH 连接信息
        </button>
      )}
      {state.phase === "polling" && <span style={{ fontSize: 12, color: "var(--fg-3)" }}>公网 IP 分配中（约 1 分钟）…</span>}
      {state.phase === "error" && <span style={{ fontSize: 12, color: "#e06b5e" }}>{state.error}</span>}
      {state.phase === "ready" && (sshCmd ? (
        <div className="row gap-2" style={{ alignItems: "center", flexWrap: "wrap" }}>
          <code className="mono" style={{ padding: "4px 10px", background: "rgba(0,0,0,0.35)", borderRadius: 6, fontSize: 12 }}>{sshCmd}</code>
          <button type="button" className="btn btn-sm" onClick={() => navigator.clipboard?.writeText(sshCmd)}>复制</button>
        </div>
      ) : <span style={{ fontSize: 12, color: "var(--fg-3)" }}>该实例未开放 22/tcp 端口</span>)}
    </div>
  );
};

// 端口访问链接：只在 capabilities.ports 时渲染。
const SbPortPanel = ({ instance, gwKey }) => {
  const [port, setPort] = ucS("");
  const [result, setResult] = ucS(null);
  const [busy, setBusy] = ucS(false);
  const openPort = async () => {
    const n = Number(port);
    if (!Number.isInteger(n) || n < 1 || n > 65535) { setResult({ error: "端口号 1-65535" }); return; }
    setBusy(true); setResult(null);
    try {
      const res = await sbGateway(`/v1/sandboxes/${instance.id}/ports/${n}`, gwKey);
      setResult({ url: res?.url });
    } catch (e) { setResult({ error: e.message }); }
    finally { setBusy(false); }
  };
  return (
    <div className="row gap-2" style={{ marginTop: 8, alignItems: "center", flexWrap: "wrap" }}>
      <input className="input mono" style={{ width: 110 }} placeholder="端口 如 8888" value={port} onChange={(e) => setPort(e.target.value)} />
      <button type="button" className="btn btn-sm" disabled={busy || !gwKey} onClick={openPort}>{busy ? "获取中…" : "获取访问链接"}</button>
      {result?.url && <a className="mono" style={{ fontSize: 12 }} href={result.url} target="_blank" rel="noreferrer">{result.url}</a>}
      {result?.error && <span style={{ fontSize: 12, color: "#e06b5e" }}>{result.error}</span>}
    </div>
  );
};

// 极简 SVG 面积图（零依赖）。参考 e2b Monitoring：深底、绿线、虚线网格。
const SbChart = ({ buckets, field, color = "#52d3ad", height = 120, emptyText }) => {
  const W = 720, H = height, PAD = 4;
  const vals = buckets.map((b) => b[field] || 0);
  const max = Math.max(2, ...vals);
  const allZero = vals.every((v) => !v);
  const pts = vals.map((v, i) => [
    PAD + (i / Math.max(1, vals.length - 1)) * (W - PAD * 2),
    H - PAD - (v / max) * (H - PAD * 2),
  ]);
  const line = pts.map((p, i) => `${i ? "L" : "M"}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(" ");
  const area = `${line} L${(W - PAD).toFixed(1)},${H - PAD} L${PAD},${H - PAD} Z`;
  const fmt = (iso) => { const d = new Date(iso); return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`; };
  if (allZero && emptyText) {
    return <div style={{ padding: "22px 0", textAlign: "center", color: "var(--fg-4)", fontSize: 12 }}>{emptyText}</div>;
  }
  return (
    <div style={{ display: "flex", gap: 8 }}>
      {/* Y 轴刻度：没有刻度的图只是装饰，读不出「那条线是几」 */}
      <div className="mono" style={{ display: "flex", flexDirection: "column", justifyContent: "space-between", fontSize: 10, color: "var(--fg-4)", textAlign: "right", paddingBottom: 14, flexShrink: 0, width: 26 }}>
        <span>{max}</span><span>{Math.round(max / 2)}</span><span>0</span>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: "auto", display: "block" }} preserveAspectRatio="none">
        {[0.25, 0.5, 0.75].map((f) => (
          <line key={f} x1={PAD} x2={W - PAD} y1={H * f} y2={H * f} stroke="rgba(255,255,255,0.08)" strokeDasharray="4 5" strokeWidth="1" />
        ))}
        <path d={area} fill={`${color}18`} />
        <path d={line} fill="none" stroke={color} strokeWidth="1.6" />
      </svg>
      {buckets.length > 1 && (
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "var(--fg-4)", fontFamily: "var(--font-mono, monospace)" }}>
          <span>{fmt(buckets[0].t)}</span><span>{fmt(buckets[Math.floor(buckets.length / 2)].t)}</span><span>{fmt(buckets[buckets.length - 1].t)}</span>
        </div>
      )}
      </div>
    </div>
  );
};

// 实例详情（e2b 的 Inspect：操作行为 / 状态变动 / 计费 + 条件出现的 SSH / 端口面板）
const SbInstanceDetail = ({ inst, detail, detailLoading, detailTab, setDetailTab, gwKey, onLoadComplete, auditBusy }) => {
  if (detailLoading) return <div style={{ color: "var(--fg-3)", fontSize: 12, padding: "10px 0" }}>加载详情…</div>;
  if (detail?.__err) return <div style={{ color: "#e06b5e", fontSize: 12, padding: "10px 0" }}>{detail.__err}</div>;
  if (!detail) return null;
  const execOps = (detail.operations || []).filter((o) => SB_DATA_PLANE.has(o.type));
  const counts = detail.auditCounts || {};
  const tabProperty = { ops: "operations", timeline: "events", billing: "usageSegments", periods: "billingPeriods" }[detailTab];
  const shownAudit = detail[tabProperty]?.length || 0;
  const totalAudit = counts[tabProperty] ?? shownAudit;
  const avgMs = execOps.length ? Math.round(execOps.reduce((a, o) => a + (o.durationMs || 0), 0) / execOps.length) : null;
  return (
    <div style={{ padding: "10px 0 4px" }}>
      {/* 概要行：操作行为的量化摘要（执行次数 / 平均耗时 / 消费） */}
      <div style={{ display: "flex", gap: 18, fontSize: 12, color: "var(--fg-3)", marginBottom: 8, flexWrap: "wrap" }}>
        <span>操作审计 <b style={{ color: "var(--fg-1)" }}>{counts.operations ?? detail.operations?.length ?? 0}</b> 条</span>
        {avgMs !== null && <span>平均执行 <b style={{ color: "var(--fg-1)" }}>{avgMs}ms</b></span>}
        <span>状态变动 <b style={{ color: "var(--fg-1)" }}>{counts.events ?? (detail.events || []).length}</b> 次</span>
        <span>累计消费 <b style={{ color: "var(--fg-1)" }}>{sbMoney(inst.totalCost)}</b></span>
      </div>
      {shownAudit < totalAudit && (
        <button type="button" className="btn btn-sm btn-ghost" disabled={auditBusy} onClick={() => onLoadComplete(tabProperty)} style={{ marginBottom: 8 }}>
          {auditBusy ? "加载完整历史…" : `加载完整历史（${shownAudit} / ${totalAudit}）`}
        </button>
      )}
      <div className="row gap-2" style={{ marginBottom: 8 }}>
        {[["ops", `操作日志 ${counts.operations ?? detail.operations?.length ?? 0}`], ["timeline", `状态变动 ${counts.events ?? detail.events?.length ?? 0}`], ["billing", `计费段 ${counts.usageSegments ?? detail.usageSegments?.length ?? 0}`], ["periods", `结算账单 ${counts.billingPeriods ?? detail.billingPeriods?.length ?? 0}`]].map(([k, l]) => (
          <button key={k} type="button" className={`btn btn-sm ${detailTab === k ? "" : "btn-ghost"}`} onClick={() => setDetailTab(k)}>{l}</button>
        ))}
      </div>
      {detailTab === "ops" && (!(detail.operations || []).length
        ? <div style={{ color: "var(--fg-4)", fontSize: 12 }}>暂无操作记录。</div>
        : detail.operations.map((op) => <SbOpRow key={op.id} op={op} />))}
      {detailTab === "timeline" && (!(detail.events || []).length
        ? <div style={{ color: "var(--fg-4)", fontSize: 12 }}>暂无状态事件。</div>
        : detail.events.map((ev, i) => (
          <div key={i} style={{ display: "flex", gap: 10, fontSize: 12, padding: "6px 0", borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
            <span className="mono" style={{ color: "var(--fg-3)" }}>{ev.previousState || "·"} → <b style={{ color: "var(--fg-1)" }}>{ev.currentState}</b></span>
            {ev.reason && (
              <span style={{ color: ev.reason === "PROVIDER_INITIATED" ? "#e8b34b" : "var(--fg-4)" }}>
                {ev.reason === "PROVIDER_INITIATED"
                  ? "服务商主动结束（超时 / 抢占 / 配额回收）"
                  : ev.reason}
              </span>
            )}
            <span style={{ marginLeft: "auto", color: "var(--fg-4)" }}>{sbTimeFull(ev.observedAt)}</span>
          </div>
        )))}
      {detailTab === "billing" && (!(detail.usageSegments || []).length
        ? <div style={{ color: "var(--fg-4)", fontSize: 12 }}>暂无计费段。</div>
        : <>
          {detail.usageSegments.map((seg, i) => (
            <div key={i} style={{ display: "flex", gap: 10, fontSize: 12, padding: "6px 0", borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
              <span className="mono">{seg.state}</span>
              <span style={{ color: "var(--fg-3)" }}>{seg.durationSeconds != null ? `${seg.durationSeconds}s` : "进行中"}</span>
              <span style={{ color: "var(--fg-3)" }}>{seg.amount != null ? sbMoney(seg.amount) : "—"}</span>
              <span style={{ marginLeft: "auto", color: "var(--fg-4)" }}>{seg.status}</span>
            </div>
          ))}
          <div style={{ fontSize: 12, color: "var(--fg-3)", marginTop: 6, textAlign: "right" }}>与「累计消费」对应：{sbMoney(inst.totalCost)}</div>
        </>)}
      {detailTab === "periods" && (!(detail.billingPeriods || []).length
        ? <div style={{ color: "var(--fg-4)", fontSize: 12 }}>暂无已结算账单。</div>
        : <>
          {detail.billingPeriods.map((period, i) => (
            <div key={period.id || i} style={{ display: "flex", gap: 10, fontSize: 12, padding: "6px 0", borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
              <span className="mono">{period.kind}</span>
              <span style={{ color: "var(--fg-3)" }}>{sbTimeFull(period.startsAt)} → {period.endedAt ? sbTimeFull(period.endedAt) : "进行中"}</span>
              <span style={{ color: "var(--fg-1)" }}>{period.amount != null ? sbMoney(period.amount) : "—"}</span>
              <span style={{ marginLeft: "auto", color: "var(--fg-4)" }}>{period.status}</span>
            </div>
          ))}
          <div style={{ fontSize: 12, color: "var(--fg-3)", marginTop: 6, textAlign: "right" }}>账单合计应与累计消费一致：{sbMoney(inst.totalCost)}</div>
        </>)}
      {!["TERMINATED", "FAILED"].includes(inst.state) && (inst.offering?.capabilities?.extensionIds || []).includes("runpod.connection_info") && (
        <SbSshPanel instance={inst} gwKey={gwKey} />
      )}
      {!["TERMINATED", "FAILED"].includes(inst.state) && inst.offering?.capabilities?.ports && (
        <SbPortPanel instance={inst} gwKey={gwKey} />
      )}
    </div>
  );
};

const SandboxTab = () => {
  const { authed } = XShell.useAuth();
  const [view, setView] = ucS("monitor"); // monitor | instances | ops
  const [metrics, setMetrics] = ucS(null);
  const [metricsWindow, setMetricsWindow] = ucS(60);
  const [data, setData] = ucS({ items: [], total: 0 });
  const [loading, setLoading] = ucS(true);
  const [err, setErr] = ucS(null);
  const [search, setSearch] = ucS("");
  // 默认只看活着的：已销毁的实例是历史记录，堆在默认视图里会把正在跑的淹没。
  const [stateFilter, setStateFilter] = ucS("ACTIVE");
  const [refreshSec, setRefreshSec] = ucS(15);
  const [expandedId, setExpandedId] = ucS(null);
  const [detail, setDetail] = ucS(null);
  const [detailLoading, setDetailLoading] = ucS(false);
  const [detailTab, setDetailTab] = ucS("ops");
  const [auditBusy, setAuditBusy] = ucS(false);
  const [gwKey, setGwKey] = ucS(() => { try { return sessionStorage.getItem(SB_KEY_STORAGE) || ""; } catch { return ""; } });
  const [actionBusy, setActionBusy] = ucS(null);
  const [opsData, setOpsData] = ucS({ items: [], total: 0 });
  const [opsPage, setOpsPage] = ucS(1);
  const [refreshTick, setRefreshTick] = ucS(0);

  React.useEffect(() => { try { sessionStorage.setItem(SB_KEY_STORAGE, gwKey); } catch {} }, [gwKey]);

  // 监控指标
  React.useEffect(() => {
    if (!authed || view !== "monitor") return undefined;
    let cancelled = false;
    XApi.apiSvc.sandboxMetrics({ windowMinutes: metricsWindow })
      .then((d) => { if (!cancelled) { setMetrics(d); setErr(null); } })
      .catch((e) => { if (!cancelled) setErr(e.message || "加载失败"); });
    return () => { cancelled = true; };
  }, [authed, view, metricsWindow, refreshTick]);

  // 实例列表
  React.useEffect(() => {
    if (!authed || view !== "instances") return undefined;
    let cancelled = false;
    XApi.apiSvc.sandboxInstances({ pageSize: 50, ...(stateFilter ? { state: stateFilter } : {}) })
      .then((d) => { if (!cancelled) { setData(d || { items: [] }); setErr(null); } })
      .catch((e) => { if (!cancelled) setErr(e.message || "加载失败"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [authed, view, stateFilter, refreshTick]);

  // 统一调用日志
  React.useEffect(() => {
    if (!authed || view !== "ops") return undefined;
    let cancelled = false;
    XApi.apiSvc.sandboxOperations({ page: opsPage, pageSize: 30 })
      .then((d) => { if (!cancelled) setOpsData(d || { items: [] }); })
      .catch((e) => { if (!cancelled) setErr(e.message || "加载失败"); });
    return () => { cancelled = true; };
  }, [authed, view, opsPage, refreshTick]);

  // 自动刷新（e2b 风格的 Auto-refresh 选择器；监控视图恒定 15s）
  React.useEffect(() => {
    const sec = view === "monitor" ? 15 : refreshSec;
    if (!sec) return undefined;
    const t = setTimeout(() => setRefreshTick((v) => v + 1), sec * 1000);
    return () => clearTimeout(t);
  }, [view, refreshSec, refreshTick]);

  // 展开详情
  React.useEffect(() => {
    if (!expandedId) { setDetail(null); return undefined; }
    let cancelled = false;
    setDetailLoading(true);
    XApi.apiSvc.sandboxInstance(expandedId)
      .then((d) => {
        if (!cancelled) setDetail((current) => {
          if (!current || current.id !== d?.id) return d;
          const preserved = {};
          for (const kind of ["operations", "events", "usageSegments", "billingPeriods"]) {
            if ((current[kind]?.length || 0) > (d[kind]?.length || 0)) preserved[kind] = current[kind];
          }
          return { ...d, ...preserved };
        });
      })
      .catch((e) => { if (!cancelled) setDetail({ __err: e.message }); })
      .finally(() => { if (!cancelled) setDetailLoading(false); });
    return () => { cancelled = true; };
  }, [expandedId, refreshTick]);

  const loadCompleteAudit = async (kind) => {
    if (!expandedId || auditBusy) return;
    setAuditBusy(true);
    try {
      const collected = [];
      let page = 1;
      let totalPages = 1;
      let total = 0;
      do {
        const result = await XApi.apiSvc.sandboxInstanceAudit(expandedId, { kind, page, pageSize: 100 });
        collected.push(...(result?.items || []));
        total = result?.total || collected.length;
        totalPages = result?.totalPages || 1;
        page += 1;
      } while (page <= totalPages);
      setDetail((current) => current?.id === expandedId
        ? { ...current, [kind]: collected, auditCounts: { ...current.auditCounts, [kind]: total } }
        : current);
    } catch (e) { setErr(e.message || "加载完整历史失败"); }
    finally { setAuditBusy(false); }
  };

  const doAction = async (inst, action) => {
    if (!gwKey) return;
    setActionBusy(`${inst.id}:${action}`);
    try {
      await sbGateway(`/v1/sandboxes/${inst.id}/${action}`, gwKey, { method: "POST", body: JSON.stringify({}) });
      setRefreshTick((v) => v + 1);
    } catch (e) { setErr(e.message); }
    finally { setActionBusy(null); }
  };
  const actionsFor = (inst) => {
    const acts = [];
    if (inst.state === "RUNNING" && inst.offering?.capabilities?.suspension?.supported) acts.push(["suspend", "挂起"]);
    if (inst.state === "SUSPENDED" && inst.offering?.capabilities?.suspension?.resumeSupported !== false) acts.push(["resume", "恢复"]);
    if (!["TERMINATED", "FAILED"].includes(inst.state)) acts.push(["terminate", "销毁"]);
    return acts;
  };
  const shown = (data.items || []).filter((i) => !search || i.id.includes(search.trim()));
  const liveCount = (data.items || []).filter((i) => !["TERMINATED", "FAILED"].includes(i.state)).length;

  if (!authed) return <div className="card" style={{ padding: 20, color: "var(--fg-3)" }}>登录后查看你的 Sandbox 实例。</div>;

  const th = { textAlign: "left", padding: "8px 10px", fontSize: 11, color: "var(--fg-4)", letterSpacing: "0.06em", fontWeight: 500, borderBottom: "1px solid rgba(255,255,255,0.08)", whiteSpace: "nowrap" };
  const td = { padding: "9px 10px", fontSize: 12, borderBottom: "1px solid rgba(255,255,255,0.05)", verticalAlign: "middle", whiteSpace: "nowrap" };

  return (
    <div className="col gap-3">
      <div className="row between" style={{ alignItems: "center", flexWrap: "wrap", gap: 10 }}>
        <div className="row gap-2" style={{ alignItems: "center" }}>
          {[["monitor", "监控"], ["instances", "实例列表"], ["ops", "调用日志"]].map(([k, l]) => (
            <button key={k} type="button" className={`btn btn-sm ${view === k ? "" : "btn-ghost"}`} onClick={() => setView(k)}>{l}</button>
          ))}
          {metrics && (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: "#52d3ad", marginLeft: 8 }}>
              <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#52d3ad" }} />
              LIVE · {metrics.currentConcurrent} 并发
            </span>
          )}
        </div>
        <div className="row gap-2" style={{ alignItems: "center" }}>
          {gwKey ? (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: "#52d3ad" }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#52d3ad" }} />操作已启用
            </span>
          ) : (
            <span style={{ fontSize: 12, color: "var(--fg-4)" }}>操作密钥</span>
          )}
          <input className="input mono" type="password" style={{ width: gwKey ? 150 : 230 }} placeholder="粘贴 API Key 以启用操作" value={gwKey} onChange={(e) => setGwKey(e.target.value.trim())} title="挂起/恢复/销毁/SSH/端口需要用你的 API Key 调 Sandbox Gateway；查看日志不需要" />
        </div>
      </div>
      {err && (
        <div className="card" style={{ padding: "10px 14px", color: "#e06b5e", fontSize: 13 }}>
          {/^not authenticated$/i.test(err) ? "登录状态已过期，请重新登录后再试。" : err}
        </div>
      )}

      {view === "monitor" && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 }}>
            {[
              ["当前并发实例", metrics ? metrics.currentConcurrent : "…", "实时"],
              ["30 天并发峰值", metrics ? metrics.peak30d : "…", "近 30 天最大值"],
              ["24 小时消费", metrics ? sbMoney(metrics.spend24h) : "…", "已结算 + 进行中"],
            ].map(([label, val, tag]) => (
              <div className="card" key={label} style={{ padding: "16px 18px", textAlign: "center" }}>
                <div style={{ fontSize: 26, fontWeight: 700 }}>{val}</div>
                <div style={{ fontSize: 12, color: "var(--fg-3)", marginTop: 4 }}>{label}</div>
                <div className="mono" style={{ fontSize: 10, color: "var(--fg-4)", marginTop: 2 }}>{tag}</div>
              </div>
            ))}
          </div>
          <div className="card" style={{ padding: "14px 18px" }}>
            <div className="row between" style={{ alignItems: "center", marginBottom: 8 }}>
              <div className="micro">并发实例</div>
              <div className="row gap-1">
                {[[60, "1H"], [360, "6H"], [1440, "24H"]].map(([m, l]) => (
                  <button key={m} type="button" className={`btn btn-sm ${metricsWindow === m ? "" : "btn-ghost"}`} onClick={() => setMetricsWindow(m)}>{l}</button>
                ))}
              </div>
            </div>
            {metrics ? <SbChart buckets={metrics.buckets} field="concurrent" /> : <div style={{ color: "var(--fg-4)", fontSize: 12 }}>加载中…</div>}
          </div>
          <div className="card" style={{ padding: "14px 18px" }}>
            <div className="micro" style={{ marginBottom: 8 }}>新建实例（每时间桶）</div>
            {metrics ? <SbChart buckets={metrics.buckets} field="starts" color="#7aa5ff" height={90} emptyText="该时间窗口内没有新建实例" /> : <div style={{ color: "var(--fg-4)", fontSize: 12 }}>加载中…</div>}
          </div>
        </>
      )}

      {view === "instances" && (
        <div className="card" style={{ padding: "12px 14px" }}>
          <div className="row between" style={{ alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
            <div className="row gap-2" style={{ alignItems: "center", flexWrap: "wrap" }}>
              <input className="input mono" style={{ width: 190 }} placeholder="按 ID 搜索" value={search} onChange={(e) => setSearch(e.target.value)} />
              <Select
                value={stateFilter}
                onChange={setStateFilter}
                width={140}
                options={[
                  { value: "ACTIVE", label: "活跃（默认）" },
                  { value: "RUNNING", label: "运行中" },
                  { value: "SUSPENDED", label: "已挂起" },
                  { value: "TERMINATED", label: "已销毁" },
                  { value: "", label: "全部（含已销毁）" },
                ]}
              />
              <span style={{ fontSize: 12, color: "var(--fg-4)" }}>{shown.length} / {data.total} · 活跃 {liveCount}</span>
            </div>
            <div className="row gap-1" style={{ alignItems: "center" }}>
              <span style={{ fontSize: 11, color: "var(--fg-4)" }}>自动刷新</span>
              <Select
                value={String(refreshSec)}
                onChange={(v) => setRefreshSec(Number(v))}
                width={80}
                options={[
                  { value: "5", label: "5s" },
                  { value: "15", label: "15s" },
                  { value: "60", label: "60s" },
                  { value: "0", label: "关" },
                ]}
              />
            </div>
          </div>
          {loading ? <div style={{ padding: 16, color: "var(--fg-3)", fontSize: 13 }}>加载中…</div>
            : !shown.length ? <div style={{ padding: 16, color: "var(--fg-4)", fontSize: 13 }}>
                {stateFilter === "ACTIVE" ? "当前没有活跃实例。历史实例在状态筛选里选「已销毁」或「全部」查看。" : "没有匹配的实例。"}
              </div>
            : <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead><tr>
                  <th style={th}>ID</th><th style={th}>服务商</th><th style={th}>规格</th>
                  <th style={th}>资源</th><th style={th}>状态</th><th style={th}>创建时间</th>
                  <th style={{ ...th, textAlign: "right" }}>累计消费</th><th style={th}></th>
                </tr></thead>
                <tbody>
                  {shown.map((inst) => (
                    <React.Fragment key={inst.id}>
                      <tr style={{ cursor: "pointer" }} onClick={() => setExpandedId(expandedId === inst.id ? null : inst.id)}>
                        <td style={td}>
                          <span style={{ color: "var(--fg-4)", fontSize: 10, marginRight: 6 }}>{expandedId === inst.id ? "▾" : "▸"}</span>
                          <span
                            className="mono" style={{ fontSize: 11, color: "var(--fg-3)", cursor: "copy" }}
                            title={`${inst.id}（点击复制完整 ID）`}
                            onClick={(e) => { e.stopPropagation(); navigator.clipboard?.writeText(inst.id); }}
                          >{inst.id.slice(0, 8)}…</span>
                        </td>
                        <td style={td}><b>{inst.provider?.serviceName || "—"}</b>{inst.provider?.host && <span className="mono" style={{ fontSize: 10, color: "var(--fg-4)", marginLeft: 6 }}>{inst.provider.host}</span>}</td>
                        <td style={td}>{inst.offering?.name || "—"}</td>
                        <td style={td}><span className="mono" style={{ fontSize: 11, color: "var(--fg-3)" }}>{inst.offering?.resources?.cpu ?? "?"}C · {inst.offering?.resources?.memoryGiB ?? "?"}G{inst.offering?.resources?.volumeGiB ? ` · ${inst.offering.resources.volumeGiB}G卷` : ""}</span></td>
                        <td style={td}><SbStateBadge state={inst.state} /></td>
                        <td style={td}><span style={{ color: "var(--fg-3)" }}>{sbTime(inst.createdAt)}</span></td>
                        <td style={{ ...td, textAlign: "right" }}><b>{sbMoney(inst.totalCost)}</b></td>
                        <td style={td} onClick={(e) => e.stopPropagation()}>
                          <div className="row gap-1">
                            {actionsFor(inst).map(([action, label]) => (
                              <button key={action} type="button" className={`btn btn-sm ${action === "terminate" ? "btn-ghost" : ""}`}
                                disabled={!gwKey || !!actionBusy} title={gwKey ? "" : "先粘贴 API Key"}
                                onClick={() => doAction(inst, action)}>
                                {actionBusy === `${inst.id}:${action}` ? "…" : label}
                              </button>
                            ))}
                          </div>
                        </td>
                      </tr>
                      {expandedId === inst.id && (
                        <tr><td colSpan={8} style={{ padding: "0 14px 12px", borderBottom: "1px solid rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.015)" }}>
                          <SbInstanceDetail inst={inst} detail={detail} detailLoading={detailLoading} detailTab={detailTab} setDetailTab={setDetailTab} gwKey={gwKey} onLoadComplete={loadCompleteAudit} auditBusy={auditBusy} />
                        </td></tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>}
        </div>
      )}

      {view === "ops" && (
        <div className="card" style={{ padding: "12px 16px" }}>
          <div className="micro" style={{ marginBottom: 6 }}>全部实例的接口调用记录（含执行/文件/端口等数据面调用）</div>
          {!(opsData.items || []).length ? <div style={{ color: "var(--fg-4)", fontSize: 12 }}>暂无调用记录。</div>
            : opsData.items.map((op) => <SbOpRow key={op.id} op={op} showInstance />)}
          {opsData.total > 30 && (
            <div className="row gap-2" style={{ marginTop: 10 }}>
              <button type="button" className="btn btn-sm btn-ghost" disabled={opsPage <= 1} onClick={() => setOpsPage((p) => p - 1)}>上一页</button>
              <span style={{ fontSize: 12, color: "var(--fg-3)" }}>{opsPage} / {Math.ceil(opsData.total / 30)}</span>
              <button type="button" className="btn btn-sm btn-ghost" disabled={opsPage >= Math.ceil(opsData.total / 30)} onClick={() => setOpsPage((p) => p + 1)}>下一页</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const EMPTY_BILLING_TX = {
  items: [],
  total: 0,
  listQueryKey: null,
  countQueryKey: null,
  summaryStatus: 'unknown',
};
const BILLING_SUMMARY_MAX_AGE_MS = 30_000;

export const BillingTab = () => {
  const { t } = useT();
  const { authed, user } = XShell.useAuth();
  const { navigate, route } = XShell.useRoute();
  const manualOpen = route.params?.payment === 'manual';
  const setupReturn = route.params?.autoRecharge === 'setup-success'
    ? 'auto-recharge-setup-success'
    : route.params?.autoRecharge === 'setup-cancel'
      ? 'auto-recharge-setup-cancel'
      : ['auto-recharge-setup-success', 'auto-recharge-setup-cancel'].includes(route.params?.stripe)
        ? route.params.stripe
        : null;
  const autoRechargeOpen = (route.params?.autoRecharge === 'open' || Boolean(setupReturn)) && !manualOpen;
  const [manualBusy, setManualBusy] = ucS(false);
  const closeManualPayment = () => { if (!manualBusy) navigate('console', { tab: 'billing' }); };
  const openAutoRecharge = () => navigate('console', { tab: 'billing', autoRecharge: 'open' });
  const activeUserId = user?._raw?.id || user?.id || user?.handle || null;
  const [useCrypto, setUseCrypto] = useRechargeMode(activeUserId);
  const { payments: pendingPayments, settledRevision } = useStripePaymentTracking();
  const [tx, setTx] = ucS(() => ({
    ...EMPTY_BILLING_TX,
    ownerId: activeUserId,
  }));
  const [page, setPage] = ucS(1);
  const [pageCursor, setPageCursor] = ucS(null);
  const [pageDirection, setPageDirection] = ucS('next');
  const [filter, setFilter] = ucS('');
  const [loading, setLoading] = ucS(true);
  // Same anti-silent-failure pattern as OverviewTab: a rejected fetch shows a
  // retry bar instead of a $0.00 that looks like "no data".
  const [loadErr, setLoadErr] = ucS(false);
  const [reloadFlag, setReloadFlag] = ucS(0);
  // 汇总只在首次进入或充值/账户余额变化后刷新；分页和类型筛选只加载列表。
  const summaryRevisionRef = React.useRef(null);
  const summaryLoadedAtRef = React.useRef(0);
  const previousUserIdRef = React.useRef(activeUserId);
  const PAGE = 20;
  const refreshKey = route.params && route.params.refresh;
  const listQueryKey = `${activeUserId ?? ''}:${page}:${filter}:${pageDirection}:${pageCursor ?? ''}`;
  const countQueryKey = `${activeUserId ?? ''}:${filter}`;

  // 鉴权失效或账户切换时立即丢弃上一账户的财务状态。visibleTx 的 owner
  // 检查还会在 effect 执行前的首帧阻止旧账户数据渲染。
  React.useEffect(() => {
    if (!authed || previousUserIdRef.current !== activeUserId) {
      previousUserIdRef.current = activeUserId;
      summaryRevisionRef.current = null;
      summaryLoadedAtRef.current = 0;
      setTx({ ...EMPTY_BILLING_TX, ownerId: activeUserId });
      setPage(1);
      setPageCursor(null);
      setPageDirection('next');
      setFilter('');
    }
  }, [authed, activeUserId]);

  React.useEffect(() => {
    if (!authed || !activeUserId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    const controller = new AbortController();
    const summaryRevision = `${activeUserId}:${user?.balance ?? ''}:${refreshKey ?? ''}:${reloadFlag}:${settledRevision}`;
    const summaryExpired =
      Date.now() - summaryLoadedAtRef.current >= BILLING_SUMMARY_MAX_AGE_MS;
    const includeSummary =
      summaryRevisionRef.current !== summaryRevision || summaryExpired;
    const refreshSummary =
      includeSummary &&
      (Boolean(refreshKey) ||
        reloadFlag > 0 ||
        (summaryRevisionRef.current !== null &&
          summaryRevisionRef.current !== summaryRevision));
    const includeCount =
      page === 1 || includeSummary || pageDirection === 'last';
    setLoading(true);
    setLoadErr(false);
    XApi.statsService
      .transactions({
        page,
        limit: PAGE,
        type: filter || undefined,
        includeSummary,
        refreshSummary,
        includeCount,
        cursor: pageCursor || undefined,
        direction: pageDirection,
        signal: controller.signal,
      })
      .then((value) => {
        if (!cancelled) {
          const summaryComplete =
            value?.currentBalance != null &&
            value?.totalRecharge != null &&
            value?.totalConsumption != null;
          // 数量字段省略时保留同筛选条件的分页；汇总请求若只返回部分字段，
          // 必须清空金额并显示不可用，不能把缺失的财务数据伪装成 0。
          setTx((previous) => ({
            ...(previous.ownerId === activeUserId
              ? previous
              : EMPTY_BILLING_TX),
            ...value,
            ownerId: activeUserId,
            listQueryKey,
            ...(value?.total != null ? { countQueryKey } : {}),
            ...(includeSummary
              ? summaryComplete
                ? { summaryStatus: 'available' }
                : {
                    totalRecharge: undefined,
                    totalConsumption: undefined,
                    summaryStatus: 'unavailable',
                  }
              : {}),
          }));
          if (includeSummary) {
            if (summaryComplete) {
              summaryRevisionRef.current = summaryRevision;
              summaryLoadedAtRef.current = Date.now();
            } else {
              setLoadErr(true);
            }
          }
        }
      })
      .catch((error) => {
        if (!cancelled && error?.name !== 'AbortError') setLoadErr(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [
    authed,
    activeUserId,
    page,
    pageCursor,
    pageDirection,
    filter,
    user?.balance,
    refreshKey,
    reloadFlag,
    settledRevision,
    listQueryKey,
    countQueryKey,
  ]);

  if (!authed)
    return (
      <>
        <TabHeader
          title={t('console.billing.title')}
          subtitle={t('console.billing.signinSubtitle')}
        />
      </>
    );

  const ownsTx = tx.ownerId === activeUserId;
  const visibleTx = ownsTx
    ? {
        ...tx,
        items: tx.listQueryKey === listQueryKey ? tx.items : [],
        total: tx.countQueryKey === countQueryKey ? tx.total : 0,
      }
    : EMPTY_BILLING_TX;
  const balance =
    visibleTx?.currentBalance != null
      ? parseFloat(visibleTx.currentBalance)
      : user?.balance || 0;
  const totalRecharge =
    visibleTx.summaryStatus === 'available' && visibleTx?.totalRecharge != null
      ? parseFloat(visibleTx.totalRecharge)
      : null;
  const totalCons =
    visibleTx.summaryStatus === 'available' &&
    visibleTx?.totalConsumption != null
      ? parseFloat(visibleTx.totalConsumption)
      : null;
  const totalPages = Math.max(1, Math.ceil((visibleTx.total || 0) / PAGE));
  const changeBillingPage = (targetPage, intent) => {
    if (loading) return;
    const nextPage = Math.min(Math.max(1, targetPage), totalPages);
    if (nextPage === page) return;
    if (intent === 'first' || nextPage === 1) {
      setPageCursor(null);
      setPageDirection('next');
    } else if (intent === 'last') {
      setPageCursor(null);
      setPageDirection('last');
    } else if (
      (intent === 'next' || nextPage === page + 1) &&
      visibleTx.endCursor
    ) {
      setPageCursor(visibleTx.endCursor);
      setPageDirection('next');
    } else if (
      (intent === 'previous' || nextPage === page - 1) &&
      visibleTx.startCursor
    ) {
      setPageCursor(visibleTx.startCursor);
      setPageDirection('previous');
    } else {
      return;
    }
    setPage(nextPage);
  };

  return (
    <>
      <TabHeader
        title={t('console.billing.title')}
        subtitle={t('console.billing.subtitle')}
      />
      {pendingPayments.length > 0 && (
        <div role="status" className="card" style={{ marginBottom: 12, padding: '12px 16px' }}>
          {pendingPayments.every((payment) => payment.timedOut)
            ? 'Payment has not finished processing. Check billing later for the final credit.'
            : 'Payment is processing. This page will update when the credit is confirmed.'}
        </div>
      )}
      {loadErr && (
        <div
          className="row between"
          style={{
            marginBottom: 12,
            padding: '10px 14px',
            borderRadius: 6,
            background: 'rgba(239,68,68,0.08)',
            border: '1px solid rgba(239,68,68,0.3)',
            color: 'var(--err)',
            fontSize: 12,
            alignItems: 'center',
          }}
        >
          <span>{t('console.overview.loadErr')}</span>
          <button
            className="btn btn-sm"
            onClick={() => setReloadFlag((f) => f + 1)}
          >
            {t('common.retry')}
          </button>
        </div>
      )}
      <div className="billing-balance-hero">
        <div className="billing-balance-primary">
          <div className="micro">{t('console.billing.balance')}</div>
          <div className="billing-balance-amount mono">${balance.toFixed(2)}</div>
          <div className="billing-balance-caption">{t('console.billing.balanceHint')}</div>
        </div>
        <div className="billing-balance-secondary">
          <div><span>{t('console.billing.recharged')}</span><strong className="mono">{loading && visibleTx.summaryStatus === 'unknown' ? '…' : totalRecharge == null ? '—' : '$' + totalRecharge.toFixed(2)}</strong></div>
          <div><span>{t('console.billing.spent')}</span><strong className="mono">{loading && visibleTx.summaryStatus === 'unknown' ? '…' : totalCons == null ? '—' : '$' + totalCons.toFixed(2)}</strong></div>
        </div>
      </div>
      <div className="billing-funding-grid">
        <section className="card billing-funding-card" aria-label={t('console.billing.manualTitle')}>
          <div className="billing-funding-header">
            <div className="billing-funding-icon"><XIcons.IconPlus size={18} /></div>
            <RechargeModeSwitch checked={useCrypto} onChange={setUseCrypto} disabled={manualBusy} />
          </div>
          <div className="micro">{t('console.billing.manualLabel')}</div>
          <h2>{t('console.billing.manualTitle')}</h2>
          <p>{t(useCrypto ? 'topup.cryptoDescription' : 'topup.cardDescription')}</p>
          <div className="billing-funding-methods">{t(useCrypto ? 'topup.cryptoMethods' : 'topup.cardMethods')}</div>
          <button type="button" className="btn btn-primary billing-funding-action" onClick={() => navigate('console', { tab: 'billing', payment: 'manual' })}>
            {t('console.billing.manualAction')} <XIcons.IconArrowRight size={13} />
          </button>
        </section>
        <AutoRechargeSettings
          ownerId={activeUserId}
          returnStatus={setupReturn}
          setupSessionId={route.params?.setupSession || route.params?.session || null}
          compact
          openRequest={autoRechargeOpen}
          onCloseRequest={() => navigate('console', { tab: 'billing' })}
        />
      </div>
      <Dialog open={manualOpen} onClose={closeManualPayment} maxWidth={640} maxHeight="88vh" responsive>
        <DialogHeader title={t('console.billing.manualTitle')} onClose={closeManualPayment} action={<div className="row gap-3">
          <RechargeModeSwitch checked={useCrypto} onChange={setUseCrypto} disabled={manualBusy} />
          <button type="button" className="btn btn-sm btn-ghost btn-icon" aria-label="Close" disabled={manualBusy} onClick={closeManualPayment}><XIcons.IconX size={12} /></button>
        </div>} />
        <DialogBody padding={0}>
          <div className="billing-payment-dialog-intro">
            <span>{t(useCrypto ? 'topup.cryptoDescription' : 'topup.cardDescription')}</span>
            <button type="button" className="billing-auto-discovery-link" disabled={manualBusy} onClick={openAutoRecharge}>{t('console.billing.autoDiscoveryAction')} <XIcons.IconArrowRight size={12} /></button>
          </div>
          <TopUpPage key={activeUserId} embedded useCrypto={useCrypto} onCryptoChange={setUseCrypto} onBusyChange={setManualBusy} />
        </DialogBody>
      </Dialog>
      <div
        className="row gap-2 console-toolbar"
        style={{ marginBottom: 14, flexWrap: 'wrap' }}
      >
        <Select
          value={filter}
          onChange={(v) => {
            setFilter(v);
            setPage(1);
            setPageCursor(null);
            setPageDirection('next');
          }}
          width={200}
          options={[
            { value: '', label: 'All types' },
            { value: 'RECHARGE', label: 'Recharge' },
            { value: 'CONSUMPTION', label: 'Consumption' },
            { value: 'BILLING_ADJUSTMENT', label: 'Billing adjustment' },
            { value: 'BONUS', label: 'Bonus' },
          ]}
        />
        <button
          type="button"
          className="btn btn-sm"
          disabled={loading}
          onClick={() => setReloadFlag((value) => value + 1)}
        >
          <XIcons.IconRefresh size={13} /> {t('common.refresh')}
        </button>
        <div className="grow" />
        <XPrim.Pagination
          page={page}
          pageSize={PAGE}
          total={visibleTx.total}
          disabled={loading}
          onPage={changeBillingPage}
        />
      </div>
      <div className="card">
        <div
          style={{
            padding: '14px 18px',
            borderBottom: '1px solid var(--line)',
          }}
          className="micro"
        >
          {t('console.billing.transactions')}
        </div>
        {loading && (
          <div style={{ padding: 20, textAlign: 'center' }}>
            <div className="spinner" />{' '}
            <span
              style={{ marginLeft: 10, color: 'var(--fg-3)', fontSize: 13 }}
            >
              Loading…
            </span>
          </div>
        )}
        {!loading && !loadErr && visibleTx.items.length === 0 && (
          <div
            style={{
              padding: 32,
              textAlign: 'center',
              color: 'var(--fg-3)',
              fontSize: 13,
            }}
          >
            No transactions yet.
          </div>
        )}
        {!loading && visibleTx.items.length > 0 && (
          <>
            <div className="table-scroll">
              <table className="table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Type</th>
                    <th>Description</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleTx.items.map((t, i) => {
                    const amt = parseFloat(t.amount || 0);
                    const date = (t.createdAt || t.date || '').slice(0, 10);
                    return (
                      <tr key={t.id || i}>
                        <td
                          className="mono"
                          style={{
                            fontSize: 12,
                            color: 'var(--fg-3)',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {date}
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          <span className="tag" style={{ fontSize: 10 }}>
                            {t.type}
                          </span>
                        </td>
                        <td style={{ fontSize: 13 }}>
                          <BillingDescription row={t} />
                        </td>
                        <td
                          className="mono"
                          style={{
                            color: amt > 0 ? 'var(--ok)' : 'var(--fg-1)',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          <XPrim.TinyAmount value={amt} sign />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <XPrim.Pagination
              inline
              page={page}
              pageSize={PAGE}
              total={visibleTx.total}
              disabled={loading}
              onPage={changeBillingPage}
            />
          </>
        )}
      </div>
    </>
  );
};

// 返佣金额常见于分位以下（platformFee × 10%），toFixed(2) 会把 $0.0004 显示成
// $0.00 —— 保留到 6 位、去尾零、至少 2 位。
const fmtReward = (n) => {
  const v = Number(n) || 0;
  const trimmed = v.toFixed(6).replace(/0+$/, "");
  const dec = trimmed.split(".")[1] || "";
  return dec.length >= 2 ? trimmed : v.toFixed(2);
};

const ReferralTab = () => {
  const { t } = useT();
  const { authed } = XShell.useAuth();
  const { toast } = XPrim.useToast();
  const [info, setInfo] = ucS(null);
  const [summary, setSummary] = ucS(null);
  // 主账本：用量返佣（cursor 分页，无 total）
  const [usage, setUsage] = ucS({ items: [], nextCursor: null });
  const [loadingMore, setLoadingMore] = ucS(false);
  // 旧充值账本：冻结只读，切到该子 Tab 时才懒加载
  const [ledger, setLedger] = ucS("usage");
  const [legacy, setLegacy] = ucS(null); // null = 未加载
  const [legacyLoading, setLegacyLoading] = ucS(false);
  const [loading, setLoading] = ucS(true);
  const [err, setErr] = ucS(null);
  const [transferring, setTransferring] = ucS(false);

  const reload = React.useCallback(async () => {
    if (!authed) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setErr(null);
    try {
      const [i, s, u] = await Promise.allSettled([
        XApi.referralService.info(),
        XApi.referralService.summary(),
        XApi.referralService.usageRewards({ limit: 20 }),
      ]);
      if (i.status === "fulfilled") setInfo(i.value);
      else if (i.status === "rejected") setErr(i.reason?.message || "Failed");
      if (s.status === "fulfilled") setSummary(s.value);
      if (u.status === "fulfilled") setUsage(u.value);
    } finally {
      setLoading(false);
    }
  }, [authed]);

  const loadMoreUsage = async () => {
    if (!usage.nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const next = await XApi.referralService.usageRewards({
        cursor: usage.nextCursor,
        limit: 20,
      });
      setUsage((prev) => ({
        items: [...prev.items, ...next.items],
        nextCursor: next.nextCursor,
      }));
    } catch (e) {
      toast(e.message || "Failed to load more");
    } finally {
      setLoadingMore(false);
    }
  };

  const openLegacy = async () => {
    setLedger("recharge");
    if (legacy !== null || legacyLoading) return;
    setLegacyLoading(true);
    try {
      setLegacy(await XApi.referralService.rewards({ page: 1, limit: 50 }));
    } catch {
      setLegacy({ items: [], total: 0 });
    } finally {
      setLegacyLoading(false);
    }
  };

  React.useEffect(() => {
    reload();
  }, [reload]);

  const transferAll = async () => {
    if (!info || !info.referralBalance) return;
    const amount = parseFloat(info.referralBalance);
    if (!amount || amount <= 0) {
      toast("Nothing to transfer");
      return;
    }
    setTransferring(true);
    try {
      await XApi.referralService.transfer(amount);
      toast("Transferred to account");
      reload();
    } catch (e) {
      toast(e.message || "Transfer failed");
    } finally {
      setTransferring(false);
    }
  };

  if (!authed)
    return (
      <>
        <TabHeader
          title={t("console.referral.title")}
          subtitle={t("console.referral.signinSubtitle")}
        />
      </>
    );

  const link = info?.referralLink || "";
  const code = info?.referralCode || "";
  const usageRewardPct = Math.round((info?.usageRewardRate ?? 0.1) * 100);
  return (
    <>
      <TabHeader
        title={t("console.referral.title")}
        subtitle={t("console.referral.subtitle", { pct: usageRewardPct })}
      />
      {err && (
        <div
          className="card"
          style={{
            padding: 16,
            marginBottom: 16,
            color: "var(--err)",
            fontSize: 13,
          }}
        >
          {err}
        </div>
      )}
      <div
        className="card"
        style={{
          padding: 24,
          marginBottom: 16,
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          className="grid-bg"
          style={{ position: "absolute", inset: 0, opacity: 0.25 }}
        />
        <div style={{ position: "relative" }}>
          <div className="micro">Your referral link</div>
          <div className="row gap-2" style={{ marginTop: 10 }}>
            <div
              className="hairline mono"
              style={{
                flex: 1,
                padding: "10px 14px",
                borderRadius: 6,
                background: "var(--bg-2)",
                fontSize: 13,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {loading
                ? "Loading…"
                : link || (code ? `https://xapi.to/r/${code}` : "—")}
            </div>
            <button
              className="btn btn-primary"
              disabled={!link && !code}
              onClick={() => {
                navigator.clipboard?.writeText(
                  link || `https://xapi.to/r/${code}`,
                );
                toast("Copied");
              }}
            >
              <XIcons.IconCopy size={13} /> Copy
            </button>
          </div>
        </div>
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 12,
          marginBottom: 16,
        }}
      >
        <StatCard
          label="Total referrals"
          value={String(info?.totalReferrals ?? "—")}
        />
        <StatCard
          label="Referral balance"
          value={
            summary || info
              ? `$${fmtReward(summary?.referralBalance ?? info?.referralBalance)}`
              : "—"
          }
        />
        <StatCard
          label="Usage rewards earned"
          value={summary ? `$${fmtReward(summary.totalUsageRewards)}` : "—"}
        />
      </div>
      {info && parseFloat(info.referralBalance || 0) > 0 && (
        <div
          className="card"
          style={{
            padding: 16,
            marginBottom: 16,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div style={{ fontSize: 13, color: "var(--fg-2)" }}>
            Transfer ${fmtReward(info.referralBalance)} to your spending
            balance
          </div>
          <button
            className="btn btn-primary btn-sm"
            disabled={transferring}
            onClick={transferAll}
          >
            {transferring ? (
              <>
                <div className="spinner" /> Transferring…
              </>
            ) : (
              "Transfer now"
            )}
          </button>
        </div>
      )}
      <div className="card">
        {/* 主列表 = 用量返佣；旧充值返佣账本冻结只读，放独立子 Tab */}
        <div
          className="row gap-2"
          style={{
            padding: "10px 18px",
            borderBottom: "1px solid var(--line)",
            alignItems: "center",
          }}
        >
          <button
            className={"btn btn-sm " + (ledger === "usage" ? "btn-primary" : "btn-ghost")}
            onClick={() => setLedger("usage")}
          >
            {t("console.referral.ledger.usage")}
          </button>
          <button
            className={"btn btn-sm " + (ledger === "recharge" ? "btn-primary" : "btn-ghost")}
            onClick={openLegacy}
          >
            {t("console.referral.ledger.recharge")}
          </button>
          {ledger === "recharge" && (
            <span className="micro" style={{ color: "var(--fg-3)" }}>
              {t("console.referral.ledger.frozen")}
              {summary
                ? ` · Σ $${fmtReward(summary.totalRechargeRewards)}`
                : ""}
            </span>
          )}
        </div>

        {ledger === "usage" && (
          <>
            {loading && (
              <div style={{ padding: 20, textAlign: "center" }}>
                <div className="spinner" />
              </div>
            )}
            {!loading && usage.items.length === 0 && (
              <div
                style={{
                  padding: 32,
                  textAlign: "center",
                  color: "var(--fg-3)",
                  fontSize: 13,
                }}
              >
                {t("console.referral.ledger.usageEmpty")}
              </div>
            )}
            {!loading && usage.items.length > 0 && (
              <table className="table">
                <thead>
                  <tr>
                    <th>Referee</th>
                    <th>Platform fee</th>
                    <th>Rate</th>
                    <th>Reward</th>
                    <th>Calls</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {usage.items.map((r, i) => {
                    const refereeLabel = r.referee?.twitterScreenName
                      ? "@" + r.referee.twitterScreenName
                      : (r.refereeId || "").slice(0, 8) || "—";
                    return (
                      <tr key={r.id || i}>
                        <td className="mono">{refereeLabel}</td>
                        <td className="mono">${fmtReward(r.baseFeeAmount)}</td>
                        <td className="mono">
                          {Math.round((r.rewardRate || 0) * 10000) / 100}%
                        </td>
                        <td className="mono" style={{ color: "var(--ok)" }}>
                          +${fmtReward(r.rewardAmount)}
                        </td>
                        <td className="mono">{r.earningsCount ?? "—"}</td>
                        <td style={{ fontSize: 12, color: "var(--fg-3)" }}>
                          {(r.createdAt || "").slice(0, 10)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
            {!loading && usage.nextCursor && (
              <div
                style={{
                  padding: "10px 18px",
                  borderTop: "1px solid var(--line)",
                  textAlign: "center",
                }}
              >
                <button
                  className="btn btn-ghost btn-sm"
                  disabled={loadingMore}
                  onClick={loadMoreUsage}
                >
                  {loadingMore ? (
                    <>
                      <div className="spinner" /> Loading…
                    </>
                  ) : (
                    t("console.referral.ledger.loadMore")
                  )}
                </button>
              </div>
            )}
          </>
        )}

        {ledger === "recharge" && (
          <>
            {legacyLoading && (
              <div style={{ padding: 20, textAlign: "center" }}>
                <div className="spinner" />
              </div>
            )}
            {!legacyLoading && (legacy?.items?.length ?? 0) === 0 && (
              <div
                style={{
                  padding: 32,
                  textAlign: "center",
                  color: "var(--fg-3)",
                  fontSize: 13,
                }}
              >
                No rewards yet.
              </div>
            )}
            {!legacyLoading && (legacy?.items?.length ?? 0) > 0 && (
              <table className="table">
                <thead>
                  <tr>
                    <th>Referee</th>
                    <th>Recharge</th>
                    <th>Rate</th>
                    <th>Reward</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {legacy.items.map((r, i) => {
                    // Backend returns referee as an object { id, twitterScreenName,
                    // twitterAvatar } — project out a string (rendering the object
                    // would crash React). Mirrors xapi-new's ReferralPage (a61438a).
                    const referee =
                      r.referee && typeof r.referee === "object"
                        ? r.referee
                        : null;
                    const refereeLabel = referee?.twitterScreenName
                      ? "@" + referee.twitterScreenName
                      : typeof r.referee === "string"
                        ? r.referee
                        : "—";
                    return (
                      <tr key={r.id || i}>
                        <td className="mono">{refereeLabel}</td>
                        <td className="mono">
                          ${parseFloat(r.rechargeAmount || 0).toFixed(2)}
                        </td>
                        <td className="mono">
                          {Math.round((r.rewardRate || 0) * 100) / 1}%
                        </td>
                        <td className="mono" style={{ color: "var(--ok)" }}>
                          +${fmtReward(r.rewardAmount)}
                        </td>
                        <td style={{ fontSize: 12, color: "var(--fg-3)" }}>
                          {(r.createdAt || "").slice(0, 10)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </>
        )}
      </div>
    </>
  );
};

const SettingsTab = () => {
  const { t } = useT();
  const { user, authed, logout } = XShell.useAuth();
  const { navigate } = XShell.useRoute();
  const { toast } = XPrim.useToast();
  const raw = user?._raw || {};
  const [applyOpen, setApplyOpen] = ucS(false);

  if (!authed)
    return (
      <>
        <TabHeader
          title={t("console.settings.title")}
          subtitle={t("console.settings.signinSubtitle")}
        />
      </>
    );

  return (
    <>
      <TabHeader
        title={t("console.settings.title")}
        subtitle={t("console.settings.subtitle")}
      />
      <div className="card" style={{ padding: 22, marginBottom: 16 }}>
        <h3 style={{ margin: "0 0 16px", fontSize: 14, fontWeight: 500 }}>
          Profile
        </h3>
        <div
          style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}
        >
          <div>
            <label className="field-label">Handle</label>
            <input className="input" disabled value={user?.handle || ""} />
          </div>
          <div>
            <label className="field-label">Twitter</label>
            <input
              className="input"
              disabled
              value={raw.twitterScreenName || "—"}
            />
          </div>
          <div>
            <label className="field-label">Account type</label>
            <input
              className="input"
              disabled
              value={
                (user?.accountType || "") + (raw.twitterId ? " · Verified" : "")
              }
            />
          </div>
          <div>
            <label className="field-label">Wallet</label>
            <input className="input" disabled value={raw.address || "—"} />
          </div>
          <div>
            <label className="field-label">Tier</label>
            <input className="input" disabled value={raw.tier || "—"} />
          </div>
          <div>
            <label className="field-label">Member since</label>
            <input
              className="input"
              disabled
              value={(raw.createdAt || "").slice(0, 10)}
            />
          </div>
        </div>
      </div>
      {user?.accountType !== "ENTITY" && (
        <div
          className="card"
          style={{
            padding: 22,
            marginBottom: 16,
            borderColor: "rgba(245,165,36,0.4)",
            background:
              "linear-gradient(180deg, rgba(245,165,36,0.06), transparent)",
          }}
        >
          <h3 style={{ margin: "0 0 6px", fontSize: 14, fontWeight: 500 }}>
            Upgrade to a verified account
          </h3>
          <p style={{ color: "var(--fg-3)", fontSize: 13, marginTop: 0 }}>
            Tweet a one-time code to link your Twitter handle. Unlocks API
            publishing and better rate limits.
          </p>
          <button
            className="btn btn-primary"
            style={{ marginTop: 10 }}
            onClick={() => navigate("bind")}
          >
            <XIcons.IconTwitter size={13} /> Start verification
          </button>
        </div>
      )}
      <div className="card" style={{ padding: 22, marginBottom: 16 }}>
        <h3 style={{ margin: "0 0 6px", fontSize: 14, fontWeight: 500 }}>
          Provider access
        </h3>
        <p style={{ color: "var(--fg-3)", fontSize: 13, marginTop: 0 }}>
          Apply to publish APIs. Reviewed within 48h.
        </p>
        <div className="row gap-2" style={{ marginTop: 10 }}>
          {raw.canProvideApis ? (
            <>
              <span className="tag" style={{ color: "var(--ok)" }}>
                <span className="tag-dot ok" /> Approved
              </span>
            </>
          ) : (
            <>
              <span className="tag" style={{ color: "var(--warn)" }}>
                <span className="tag-dot" /> Not yet approved
              </span>
              <button className="btn btn-sm" onClick={() => setApplyOpen(true)}>
                Apply now
              </button>
            </>
          )}
        </div>
      </div>
      {applyOpen && (
        <ApplyProviderModal
          onClose={() => setApplyOpen(false)}
          onDone={() => {
            setApplyOpen(false);
            toast("Application submitted");
          }}
        />
      )}
      <div className="card" style={{ padding: 22 }}>
        <h3 style={{ margin: "0 0 6px", fontSize: 14, fontWeight: 500 }}>
          Session
        </h3>
        <p style={{ color: "var(--fg-3)", fontSize: 13, margin: "6px 0 12px" }}>
          Sign out from this browser. Your API keys remain active.
        </p>
        <button
          className="btn btn-sm"
          onClick={async () => {
            await logout();
            navigate("portal");
            toast("Signed out");
          }}
        >
          Sign out
        </button>
      </div>
    </>
  );
};

const ApplyProviderModal = ({ onClose, onDone }) => {
  const { toast } = XPrim.useToast();
  const [email, setEmail] = ucS("");
  const [note, setNote] = ucS("");
  const [busy, setBusy] = ucS(false);
  const [err, setErr] = ucS(null);
  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setErr("Enter a valid email");
      return;
    }
    setBusy(true);
    try {
      await XApi.fetchJson("/auth/provider/apply", {
        method: "POST",
        auth: "always",
        body: { email, note: note || undefined },
      });
      onDone && onDone();
    } catch (e) {
      setErr(e.message || "Apply failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={true} onClose={busy ? null : onClose} maxWidth={460}>
      <DialogHeader
        title="Apply for provider access"
        onClose={busy ? null : onClose}
      />
      <DialogBody>
        <form onSubmit={submit}>
          <p
            style={{
              fontSize: 12.5,
              color: "var(--fg-3)",
              lineHeight: 1.7,
              margin: "0 0 14px",
            }}
          >
            We review applications within 48 hours. Approved accounts can
            publish APIs to the marketplace.
          </p>
          <label className="field-label">Contact email</label>
          <input
            className="input"
            autoFocus
            type="email"
            placeholder="you@yourco.com"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setErr(null);
            }}
          />
          <label className="field-label" style={{ marginTop: 12 }}>
            What would you like to publish? (optional)
          </label>
          <textarea
            className="input"
            rows={4}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Brief description of the APIs you want to onboard…"
          />
          {err && (
            <div style={{ color: "var(--err)", fontSize: 12, marginTop: 8 }}>
              {err}
            </div>
          )}
          <div
            className="row gap-2"
            style={{ marginTop: 18, justifyContent: "flex-end" }}
          >
            <button
              type="button"
              className="btn"
              onClick={onClose}
              disabled={busy}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={busy}>
              {busy ? (
                <>
                  <div className="spinner" /> Submitting…
                </>
              ) : (
                "Submit"
              )}
            </button>
          </div>
        </form>
      </DialogBody>
    </Dialog>
  );
};

// ============================================================================

export const XConsole = { ConsolePage };
export { ConsolePage, OverviewTab };
if (typeof window !== "undefined") window.XConsole = XConsole;
